import type {
  CaptureImageOptions,
  CaptureImageResult,
  ImageUrlResolver,
  MapLibreAdapterOptions,
} from "./adapters/maplibre/MapLibreAdapter";
import {
  DEFAULT_NEW_SHAPE_LAYER,
  MapLibreAdapter,
} from "./adapters/maplibre/MapLibreAdapter";
import { isSvgImageSrc } from "./adapters/maplibre/imageFormat";
import { trustedImageDimensions } from "./adapters/maplibre/imageQuad";
import type { SnapThresholds } from "./adapters/maplibre/snapping";
import type {
  DrawTriggerOptions,
  HighlightShapesOptions,
  LngLat,
  OsmBackgroundOptions,
  RemoteCursor,
  Shape,
  ShapeImage,
  ShapeStyle,
  ShapeThemes,
  ShapeText,
  ShapeUrlAction,
  StartDrawingImageOptions,
  StartDrawingOptions,
  StartEditingMapViewOptions,
  TileLayerName,
  TokaMapColors,
} from "./adapters/types";
import type {
  MapViewState,
  TokaMapConfigurationState,
} from "./core/commands";
import type { MapFraming } from "./core/framing";
import {
  BatchCommand,
  ConvertShapeKindCommand,
  CreateShapeCommand,
  DeleteShapeCommand,
  MoveShapeCommand,
  MoveVertexCommand,
  RotateShapeCommand,
  ScaleShapeCommand,
  SetConfigurationCommand,
  SetShapeActionCommand,
  SetShapeEntityCommand,
  SetShapeHoverStyleCommand,
  SetShapeHoverTextCommand,
  SetShapeImageCommand,
  SetShapeLayerCommand,
  SetShapeLockedCommand,
  SetShapePathPriorityCommand,
  SetShapeHeightCommand,
  SetShapeElevationCommand,
  SetShapeHoverElevationCommand,
  SetShapeHoverHeightCommand,
  SetShapeMinZoomOffsetCommand,
  SetShapeThemeIdCommand,
  SetShapeStyleCommand,
  SetShapeTextCommand,
  SetShapeTargetDocumentIdCommand,
  SetShapeUrlActionCommand,
  SetShapeUseActionIconCommand,
  SetShapeWalkableCommand,
} from "./core/commands";
import type {
  SaveConfigurationEvent,
  SaveConfigurationStatusListener,
} from "./core/ConfigurationSaveTracker";
import { ConfigurationSaveTracker } from "./core/ConfigurationSaveTracker";
import { FormatMigrationGate } from "./core/FormatMigrationGate";
import { resolveIncomingView } from "./core/documentTransition";
import { generateId } from "./core/generateId";
import { relocateShapes } from "./core/relocateShapes";
import type { Command } from "./core/History";
import { History } from "./core/History";
import type { RouteEndpoint, RouteResult } from "./core/pathfinding";
import { findRoute } from "./core/pathfinding";
import { findRouteDebugGrid } from "./core/pathfinding";
import { computeAreaDebugGrid } from "./core/pathfinding";
import { ShapeStore } from "./core/ShapeStore";
import type { LinkedContentResolver } from "./core/linkedContent";
import { applyLinkedContent } from "./core/linkedContent";
import type {
  BatchSaveEntry,
  SaveEvent,
  SaveStatusListener,
} from "./core/ShapeSaveTracker";
import { ShapeSaveTracker } from "./core/ShapeSaveTracker";

export type { SaveEvent } from "./core/ShapeSaveTracker";
export type { MapViewState } from "./core/commands";
export type { SaveConfigurationEvent } from "./core/ConfigurationSaveTracker";
import {
  detectSvgImageType,
  loadImageDimensions,
} from "./loadImageDimensions";
import type { LatestRawFeature, LoadedData } from "./loaders/jsonLoader";
import {
  LATEST_FORMAT_VERSION,
  migrateDocumentToLatest,
  parseJsonDocument,
  parseLatestJsonFeature,
} from "./loaders/jsonLoader";
import { AddressSearchBox } from "./ui/AddressSearchBox";
import { CancelDrawingButton } from "./ui/CancelDrawingButton";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import {
  ContextMenu,
  type ContextMenuItem,
} from "./ui/ContextMenu";
import { DrawTutorial } from "./ui/DrawTutorial";
import type { EntityPanelActions, EntityPanelEntry } from "./ui/EntityPanel";
import { EntityPanel } from "./ui/EntityPanel";
import { FpsMeter } from "./ui/FpsMeter";
import type { GeolocationButtonOptions, GeolocationButtonPosition } from "./ui/GeolocationButton";
import { GeolocationButton } from "./ui/GeolocationButton";
import type { StackedWidget } from "./ui/floatingStack";
import { stackOffsetPx } from "./ui/floatingStack";
import { createIcon } from "./ui/icons";
import type { ZoomControlOptions, ZoomControlPosition } from "./ui/ZoomControl";
import { ZOOM_CONTROL_HEIGHT_PX, ZOOM_CONTROL_WIDTH_PX, ZoomControl } from "./ui/ZoomControl";
import { LoadErrorView } from "./ui/LoadErrorView";
import { MapViewCrosshair } from "./ui/MapViewCrosshair";
import { MapViewEditControls } from "./ui/MapViewEditControls";
import { MapViewFrame } from "./ui/MapViewFrame";
import { MigrationDialog } from "./ui/MigrationDialog";
import type { ResetViewButtonOptions, ResetViewButtonPosition } from "./ui/ResetViewButton";
import { ResetViewButton } from "./ui/ResetViewButton";
import type { DocumentSwitcherItem, DocumentSwitcherPosition } from "./ui/DocumentSwitcher";
import { DocumentSwitcher } from "./ui/DocumentSwitcher";
import type { RoutePanelOptions, RoutePanelSelection } from "./ui/RoutePanel";
import { RoutePanel } from "./ui/RoutePanel";
import type {
  SearchBoxCategory,
  SearchBoxEntry,
  SearchBoxOptions,
  SearchBoxPosition,
} from "./ui/SearchBox";
import { SearchBox } from "./ui/SearchBox";
import { UrlOverlay } from "./ui/UrlOverlay";

/** Représentation affichable d'une forme dans le champ de recherche (voir `TokaMapConfig.search`) —
 * jamais l'`entity` brute : au consommateur d'en extraire ce qui doit être affiché. */
export interface SearchItem {
  title: string;
  subTitle?: string;
  image?: string;
  /** Texte additionnel pris en compte par la recherche en plus de `title`, sans jamais être affiché
   * (voir `SearchBoxEntry.searchText`) — ex. une adresse/un emplacement qu'on veut trouvable sans
   * l'ajouter à `subTitle`, qui reste purement pour l'affichage. */
  searchText?: string;
  /**
   * Identifiant stable de l'entité métier représentée par la forme (ex. `entity.entity.id` côté
   * Japan Expo) — PAS le `Shape.id`. Plusieurs formes peuvent porter la même entité (ex. une place
   * découpée en plusieurs polygones) : quand `key` coïncide entre plusieurs formes, elles sont
   * fusionnées en un seul résultat de recherche (voir `getEntries` dans `createTokaMap`), dont le
   * clic surligne TOUTES les formes concernées plutôt qu'une seule. Omis : chaque forme reste son
   * propre résultat (équivalent à `key: shape.id`, comportement historique).
   */
  key?: string;
  categoryId?: string;
}

/**
 * Résultat de recherche qui ne correspond à aucune forme du document affiché — typiquement une
 * destination située sur un autre document que le consommateur sait atteindre (voir
 * `SearchBoxConfig.getExternalItems`). `payload` lui est rendu tel quel au clic, `@tokamap/core`
 * n'ayant aucune notion de ce qu'est cet "ailleurs".
 */
export interface SearchExternalItem {
  title: string;
  subTitle?: string;
  image?: string;
  /** Même rôle que `SearchItem.searchText` : comparé à la saisie sans jamais être affiché. */
  searchText?: string;
  categoryId?: string;
  payload: unknown;
}

/** Options de `TokaMapInstance.loadDocument` (voir son commentaire). */
export interface LoadDocumentOptions {
  /**
   * Rejoindre la vue du nouveau document en volant (`true`, par défaut) ou en y sautant (`false`).
   */
  animateView?: boolean;
}

/** Options de `TokaMapConfig.search` (voir son commentaire) — distinct de `SearchBoxOptions`
 * (`ui/SearchBox.ts`), qui reste agnostique de `Shape`/`ShapeStore` (frontière core/adapter). */
export interface SearchBoxConfig {
  /**
   * Appelée pour chaque forme du `ShapeStore` à chaque frappe dans le champ de recherche (pas mise
   * en cache — voir `SearchBoxOptions.getEntries`) : retourne l'entrée à afficher pour cette forme,
   * ou `null`/`undefined` pour l'exclure des résultats. Voir `SearchItem.key` pour dédupliquer
   * plusieurs formes vers un même résultat.
   */
  getSearchItem: (shape: Shape) => SearchItem | null | undefined;
  /**
   * Tags sous le champ : en choisir un liste les résultats de même `categoryId` (`getExternalItems`
   * compris), que la saisie filtre encore. Une catégorie sans résultat n'est pas affichée.
   */
  getCategories?: () => SearchBoxCategory[];
  /**
   * Appelée APRÈS que la/les forme(s) correspondant au résultat cliqué ont été surlignées sur la
   * carte (voir `MapAdapter.highlightShapes`/`zoomToFit` ci-dessous) — optionnelle : absente, le
   * clic se contente de surligner/recentrer, sans notifier le consommateur davantage. `shapeIds`
   * porte toutes les formes du résultat (voir `SearchItem.key`), pas seulement la première.
   */
  onSearch?: (shapeIds: string[], item: SearchItem) => void;
  /**
   * Résultats supplémentaires, sans forme chargée derrière eux (voir `SearchExternalItem`) —
   * rappelée à chaque frappe comme `getSearchItem`, et listée APRÈS les formes du document
   * affiché, dans l'ordre rendu. Sert à chercher au-delà du document courant, ex. les autres cartes
   * d'un même projet.
   */
  getExternalItems?: () => SearchExternalItem[];
  /**
   * Appelée au clic sur un résultat de `getExternalItems`, avec son `payload` — remplace
   * `onSearch`, qui n'est PAS appelée dans ce cas : rien n'a été surligné ni recentré, il n'y a
   * aucune forme chargée à qui le faire. C'est au consommateur d'aller où le résultat désigne
   * (typiquement `TokaMapInstance.loadDocument` puis `highlightShapes`).
   */
  onSelectExternalItem?: (payload: unknown) => void;
  /** Recentre/zoome la caméra pour faire tenir la/les forme(s) surlignée(s) à l'écran (voir
   * `HighlightShapesOptions.zoomToFit`). Par défaut : `true` (contrairement à `highlightShapes`
   * elle-même, dont le défaut est `false`) — le but d'une recherche est justement d'amener la forme
   * trouvée à l'écran. */
  zoomToFit?: boolean;
  /** Coin de l'écran où placer le composant. Par défaut : "top-left" — quand `resetViewButton`/
   * `geolocationButton` occupent le même coin, `createTokaMap` les décale automatiquement pour que
   * la recherche apparaisse au-dessus d'eux (voir `BASE_EDGE_OFFSET_PX`/`STACK_GAP_PX`), sans qu'il
   * n'y ait rien à configurer ici. */
  position?: SearchBoxPosition;
  /** Fusionné par-dessus le style par défaut — même convention que `ResetViewButtonOptions.style`. */
  style?: Partial<CSSStyleDeclaration>;
  /** Texte affiché dans le champ vide. Par défaut : "Rechercher…". */
  placeholder?: string;
}

/** Options de `TokaMapConfig.entityPanel` (voir son commentaire). */
export interface EntityPanelConfig {
  /**
   * Retourne le contenu de la fiche pour la forme cliquée, ou `null`/`undefined` pour ne rien
   * afficher (la fiche se ferme si elle était déjà ouverte pour une autre forme).
   */
  getEntityPanelEntry: (shape: Shape) => EntityPanelEntry | null | undefined;
}

/**
 * Options de `TokaMapConfig.routePanel` (voir CLAUDE.md "Itinéraire / pathfinding") — pas de
 * `getEntries`/`getRoutePanelEntry` ici, contrairement à `SearchBoxConfig`/`EntityPanelConfig` :
 * "choisir une forme liée à une entité" est exactement ce que `TokaMapConfig.search.getSearchItem`
 * fait déjà, `routePanel` le réutilise directement (voir `createTokaMap.ts`, `buildSearchEntries`)
 * plutôt que de faire fournir 2 fois la même logique par le consommateur — `routePanel` reste donc
 * sans effet si `search` n'est pas configuré (avertissement console).
 */
export interface RoutePanelConfig {
  /**
   * Destinations situées hors du document affiché (voir `SearchExternalItem`), ajoutées aux formes
   * du document dans les deux champs — même mécanisme que `SearchBoxConfig.getExternalItems`.
   * Choisir l'une d'elles produit une `RoutePanelSelection` de type `"external"`, que seul
   * `onValidate` sait résoudre : sans `onValidate`, ces entrées mèneraient à un itinéraire
   * impossible à calculer (avertissement console).
   */
  getExternalItems?: () => SearchExternalItem[];
  /**
   * Remplace le calcul interne de l'itinéraire (`core/pathfinding.ts` + `MapAdapter.showRoute`) :
   * appelée au clic sur "Valider" avec les deux extrémités choisies, à charge du consommateur de
   * calculer et d'afficher. Nécessaire dès que l'itinéraire peut traverser plusieurs documents —
   * `@tokamap/core` n'en connaît qu'un seul à la fois (voir `findRouteAcrossDocuments`, qui prend
   * tous les documents en paramètre parce qu'il ne peut pas les deviner).
   *
   * Le panneau se referme aussitôt (contrairement au calcul interne, qui le laisse ouvert sur son
   * résultat) : c'est le consommateur qui affiche la suite, le formulaire n'a plus rien à montrer et
   * masquerait son rendu. À lui, donc, de rapporter aussi bien un itinéraire qu'un échec —
   * `RoutePanel.setError` ne lui est pas accessible. `MapAdapter.showRoute`/`clearRoute` restent à
   * sa main via `TokaMapInstance`.
   */
  onValidate?: (from: RoutePanelSelection, to: RoutePanelSelection) => void;
  /**
   * Appelée à chaque ouverture du mode itinéraire (bouton, "🧭 Y aller" d'une fiche, ou
   * `TokaMapInstance.startRouteMode`), avant que le panneau n'apparaisse. `@tokamap/core` efface
   * déjà son propre tracé à ce moment-là ; c'est ici que le consommateur remet à plat ce qu'il
   * affichait de son côté (voir `onValidate`). Sans ça, demander un nouvel itinéraire effacerait le
   * tracé sans mettre fin au précédent, qui continuerait d'exister chez le consommateur — un
   * itinéraire à moitié vivant.
   */
  onStart?: () => void;
  /** Coin de l'écran où placer le panneau. Par défaut : "top-right". */
  position?: SearchBoxPosition;
  /** Fusionné par-dessus le style par défaut — même convention que `ResetViewButtonOptions.style`. */
  style?: Partial<CSSStyleDeclaration>;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  validateLabel?: string;
  /** Libellé de l'entrée "position utilisateur" du champ Départ (voir `MapAdapter.getUserLocation`).
   * Par défaut : "Ma position". */
  userLocationLabel?: string;
  /**
   * Infobulle du bouton qui ouvre ce panneau, posé à droite du champ de recherche (voir
   * `SearchBoxAction`). Par défaut : "Itinéraire".
   */
  buttonLabel?: string;
}

export interface DocumentSwitcherConfig {
  /** Relue à chaque ouverture de la liste. */
  getDocuments: () => DocumentSwitcherItem[];
  getCurrentDocumentId: () => string | undefined;
  /** C'est au consommateur de charger le document choisi (`loadDocument`). */
  onSelect: (documentId: string) => void;
  /** Par défaut : "top-right". */
  position?: DocumentSwitcherPosition;
  style?: Partial<CSSStyleDeclaration>;
  /** Par défaut : "Changer de carte". */
  label?: string;
}

/** Contenu d'un presse-papier de formes — voir `ShapeClipboard`. */
export interface ShapeClipboardContent {
  shapes: Shape[];
  /** `ShapeClipboard.origin` de l'instance qui a copié. */
  origin: string | null;
}

/**
 * Presse-papier de formes (Copier/Coller), fourni par le consommateur pour le partager entre
 * plusieurs instances — typiquement les cartes d'un même projet, chacune montée dans sa propre
 * instance. `origin` identifie le document de CETTE instance : coller un contenu de même origine
 * décale la copie près de l'originale, comme toujours ; d'une autre origine (une autre carte, placée
 * ailleurs), le groupe atterrit au centre de la vue (ou sous le clic droit), à sa taille réelle —
 * voir `relocateShapes`.
 *
 * C'est au consommateur de décider entre quelles instances le contenu circule (en rendant `null`
 * depuis `read` là où il ne doit pas être collé).
 */
export interface ShapeClipboard {
  origin: string | null;
  read(): ShapeClipboardContent | null;
  write(content: ShapeClipboardContent): void;
}

export interface TokaMapConfig {
  /**
   * Charge les formes/la vue initiales. Par défaut (ni `loadInitialData` ni `loadJson` fournis) :
   * aucune, la carte démarre vide — `@tokamap/core` ne présume d'aucun fichier/URL par défaut (voir
   * CLAUDE.md, frontière core/exemple). Voir `packages/example/src/App.tsx` (`loadJson`) pour un
   * exemple de branchement sur `exemple.json`.
   */
  loadInitialData?: () => Promise<LoadedData>;
  /**
   * Alternative à `loadInitialData` quand on veut juste changer *d'où vient* le JSON (une API, un
   * import statique, IndexedDB...) sans réimplémenter le parsing : retourne le document déjà
   * désérialisé (`JSON.parse`-like), qui sera passé à `parseJsonDocument` (même logique que
   * `loadShapesFromUrl`, sans repasser par un texte brut). Ignoré si `loadInitialData` est fourni.
   */
  loadJson?: () => Promise<unknown>;
  /** Voir `MapLibreAdapterOptions.resolveImageUrl`. Par défaut : aucune transformation (URL telle quelle). */
  resolveImageUrl?: ImageUrlResolver;
  /**
   * Valeur actuelle d'un texte ou d'une image reliés (`ShapeText.source`/`ShapeImage.source`), lue
   * par le consommateur dans sa propre donnée (ex. `Shape.entity`). `undefined` : valeur inconnue,
   * la forme garde celle qu'elle porte. Voir `applyLinkedContent`.
   */
  resolveLinkedContent?: LinkedContentResolver;
  /** Voir `MapLibreAdapterOptions.minImageOverlayScreenPx`. Par défaut : 40. */
  minImageOverlayScreenPx?: number;
  /**
   * Voir `MapLibreAdapterOptions.includeImages`. Par défaut : true. Décide, à l'affichage, si les
   * images déjà présentes dans les formes (`Shape.image`, quelle que soit son origine) sont
   * montrées sur la carte. La désactiver masque toutes les images sans toucher aux données
   * chargées (`Shape.image` reste inchangé).
   */
  includeImages?: boolean;
  /** Voir `MapLibreAdapterOptions.includeIndirectImages`. Par défaut : true. */
  includeIndirectImages?: boolean;
  /** Voir `MapLibreAdapterOptions.showDrawingMeasures`. Par défaut : false. */
  showDrawingMeasures?: boolean;
  /** Voir `MapLibreAdapterOptions.snapThresholds`. Par défaut : `DEFAULT_SNAP_THRESHOLDS`. */
  snapThresholds?: Partial<SnapThresholds>;
  /**
   * Mode édition. Par défaut : false (mode visualisation seule).
   * - `true` : affiche le compteur FPS et le menu contextuel (clic droit sur une forme) ; survoler
   *   une forme ajoute un petit fond bleu et une bordure bleue (voir `MapLibreAdapterOptions.editMode`).
   *   Le dessin (`startDrawingPolygon`/`startDrawingRectangle`) n'a plus de déclencheur intégré ici
   *   — au consommateur d'exposer son propre bouton/raccourci (voir `packages/example/src/Actions.tsx`).
   * - `false` : aucun de ces éléments n'apparaît. Les formes gardent leur seule couleur explicite
   *   (`Shape.style.bgColor`/`borderColor`), sans état de survol — sinon rien.
   */
  editMode?: boolean;
  /**
   * Presse-papier de formes partagé (voir `ShapeClipboard`). Par défaut : un presse-papier propre à
   * l'instance, perdu avec elle.
   */
  clipboard?: ShapeClipboard;
  /**
   * Voir `MapLibreAdapterOptions.defaultShapeTheme`. Style (`bgColor`/`borderColor`) appliqué aux
   * nouvelles formes dessinées (polygone/rectangle). Par défaut : aucun (formes sans style
   * explicite). Sans effet sur les formes issues des données initiales.
   *
   * Sert de valeur de secours seulement : si les données chargées (`loadJson`/`loadInitialData`)
   * portent un `configurations.defaultShapeStyle` (v2)/`featureStyle` (v1, voir
   * `jsonLoader`), celui-ci prend le dessus une fois le chargement terminé (reflète ce qui a
   * été enregistré en dernier via `TokaMapInstance.setDefaultShapeStyle`) — cette option ne s'applique
   * donc que tant qu'aucune valeur n'a encore été chargée/enregistrée.
   */
  defaultShapeTheme?: ShapeStyle;
  /**
   * Table initiale des thèmes référençables par `Shape.themeId` (voir `ShapeTheme`). Valeur de
   * départ seulement : `TokaMapInstance.setThemes` la remplace après coup (c'est le chemin normal
   * pour un consommateur dont les thèmes vivent hors du document, comme le back-office), et
   * `configurations.themes` des données chargées prend le dessus une fois le chargement terminé —
   * même mécanisme que `defaultShapeTheme`.
   */
  themes?: ShapeThemes;
  /**
   * Thème (`Shape.themeId`) posé d'office sur toute forme nouvellement dessinée — voir
   * `TokaMapInstance.setDefaultThemeId`. Valeur de départ seulement.
   */
  defaultThemeId?: string;
  /**
   * Voir `MapLibreAdapterOptions.defaultTileLayerName`. Fond de carte (voir `TileLayerName`) affiché
   * à l'initialisation. Par défaut : `"unset"` (aucun fond, comportement historique).
   *
   * Sert de valeur de secours seulement, même mécanisme que `defaultShapeTheme` : si les données
   * chargées (`loadJson`/`loadInitialData`) portent un `configurations.tileLayerName`, celui-ci prend
   * le dessus une fois le chargement terminé (reflète ce qui a été enregistré en dernier via
   * `TokaMapInstance.setTileLayerName`) — cette option ne s'applique donc que tant qu'aucune valeur
   * n'a encore été chargée/enregistrée.
   */
  defaultTileLayerName?: TileLayerName;
  /** Voir `MapLibreAdapterOptions.defaultOsmOptions` — valeur de secours seulement, même mécanisme
   * que `defaultTileLayerName`. */
  defaultOsmOptions?: OsmBackgroundOptions;
  /**
   * Voir `MapLibreAdapterOptions.defaultBackgroundColor`. Couleur de fond du conteneur DOM de la
   * carte. Par défaut : `undefined` (aucune couleur explicite, laisse le CSS du consommateur
   * décider).
   *
   * Sert de valeur de secours seulement, même mécanisme que `defaultTileLayerName` : si les données
   * chargées (`loadJson`/`loadInitialData`) portent un `configurations.backgroundColor`, celui-ci
   * prend le dessus une fois le chargement terminé (reflète ce qui a été enregistré en dernier via
   * `TokaMapInstance.setBackgroundColor`) — cette option ne s'applique donc que tant qu'aucune valeur
   * n'a encore été chargée/enregistrée.
   */
  defaultBackgroundColor?: string;
  /**
   * Voir `MapLibreAdapterOptions.defaultAllowPitch`/`MapAdapter.getAllowPitch`. Autorise
   * l'inclinaison 3D de la caméra. Par défaut : `false` (carte toujours vue du dessus, inclinaison
   * bloquée).
   *
   * Sert de valeur de secours seulement, même mécanisme que `defaultTileLayerName` : si les données
   * chargées (`loadJson`/`loadInitialData`) portent un `configurations.allowPitch`, celui-ci prend
   * le dessus une fois le chargement terminé (reflète ce qui a été enregistré en dernier via
   * `TokaMapInstance.setAllowPitch`) — cette option ne s'applique donc que tant qu'aucune valeur n'a
   * encore été chargée/enregistrée.
   */
  defaultAllowPitch?: boolean;
  /**
   * Bouton "recentrer la carte sur sa position d'origine" (celle chargée au démarrage — voir
   * `loadInitialData`/`loadJson`, ou les valeurs par défaut de `MapLibreAdapter` sinon). Masqué tant
   * que la vue courante correspond encore à cette vue de départ, affiché dès qu'elle en diverge
   * (déplacement, zoom ou rotation — voir `"map-view:changed"`, `ResetViewButton.setVisible`) ;
   * coin haut-gauche par défaut. `position`/`style` permettent de le replacer ou de le restyler sans
   * construire son propre widget. `false` : ne crée pas le bouton du tout.
   */
  resetViewButton?: ResetViewButtonOptions | false;
  /**
   * Boutons "+"/"−" (voir `ui/ZoomControl`) — coin bas-droit par défaut, sous les deux boutons de
   * recentrage. Seul widget flottant présent en édition comme en visualisation : zoomer n'est pas un
   * geste de visiteur. Chaque bouton se grise une fois la borne de `setZoomBounds` atteinte.
   * `false` : ne crée pas le contrôle du tout.
   */
  zoomControl?: ZoomControlOptions | false;
  /**
   * Repérage de la position de l'utilisateur (géolocalisation navigateur, voir BESOINS.md §15) :
   * affiche un point sur la carte à sa position courante, ainsi qu'un indicateur de direction (cap
   * boussole, quand disponible) qui pivote avec elle. Par défaut : `false` (aucune requête de
   * géolocalisation, aucun point affiché).
   *
   * Sert de valeur de secours seulement, même mécanisme que `defaultTileLayerName` : si les données
   * chargées (`loadJson`/`loadInitialData`) portent un `configurations.geolocation`, celui-ci prend
   * le dessus une fois le chargement terminé (voir `TokaMapInstance.setGeolocationEnabled`) — cette
   * option ne s'applique donc que tant qu'aucune valeur n'a encore été chargée/enregistrée. Comme
   * `tileLayerName`, ce réglage FAIT partie de la "configuration" persistée (undoable, transmis à
   * `onSaveConfiguration` — voir `TokaMapConfigurationState.geolocation`) une fois modifié via
   * `TokaMapInstance.setGeolocationEnabled`.
   */
  geolocation?: boolean;
  /**
   * Bouton "recentrer la carte sur ma position" (voir `geolocation` ci-dessus) — même convention que
   * `resetViewButton` : affiché par défaut (coin haut-gauche, comme `resetViewButton` et `search`),
   * masqué automatiquement tant que la géolocalisation est désactivée ou que la position courante
   * reste déjà visible à l'écran (voir `MapAdapterEvent` `"geolocation:visibility"`). Partageant son
   * coin par défaut avec `resetViewButton`/`search`, `createTokaMap` le décale automatiquement sous
   * celui/ceux d'entre eux qui sont effectivement affichés dans ce même coin, EN TEMPS RÉEL (voir
   * `geolocationButtonStackOffsetPx`) : ce décalage se resserre aussitôt si `resetViewButton`
   * disparaît en cours de session, sans qu'il n'y ait rien à configurer ici. `false` : ne crée pas
   * le bouton du tout.
   */
  geolocationButton?: GeolocationButtonOptions | false;
  /**
   * Champ de recherche flottant (coin haut-gauche par défaut, au-dessus de `resetViewButton`/
   * `geolocationButton` — voir `SearchBoxOptions.position`) : filtre par titre une liste construite
   * à partir des formes
   * actuellement dans le `ShapeStore`, PAS d'une liste fournie telle quelle par le consommateur —
   * `getSearchItem` est appelée pour chaque forme (chargée ou dessinée depuis, y compris après
   * undo/redo) et décide, à partir de sa charge opaque `Shape.entity` (voir CLAUDE.md), si/comment
   * cette forme doit apparaître dans les résultats. Retourner `null`/`undefined` exclut la forme
   * (ex. une forme sans entité métier associée). Absent/`false` : aucun champ de recherche affiché
   * (comportement historique).
   */
  search?: SearchBoxConfig | false;
  /**
   * Fiche entité affichée au clic sur une forme (voir `ui/EntityPanel`) : bottom sheet sur mobile,
   * panneau à gauche au-dessus d'un breakpoint (CSS pur, voir `style.css`) — actif UNIQUEMENT hors
   * `editMode` (mode visualisation seule). En mode édition, ce clic n'est géré nulle part ici :
   * c'est au consommateur de brancher sa propre UI d'édition (voir `packages/example/src/Sidebar.tsx`
   * et `App.tsx`, qui écoutent `shape:click` sur `mapAdapter` directement).
   *
   * `getEntityPanelEntry` est appelée à chaque `shape:click`, avec la forme cliquée, pour décider
   * si/comment l'afficher (même principe que `SearchBoxConfig.getSearchItem`, mais sans dédoublonnage
   * par clé — une fiche correspond toujours à UNE forme). `null`/`undefined` ferme la fiche sans
   * rien afficher (ex. une forme sans entité métier associée). Absent/`false` : aucune fiche, aucun
   * effet sur `shape:click` (comportement historique).
   */
  entityPanel?: EntityPanelConfig | false;
  /**
   * Mode "itinéraire" (voir CLAUDE.md "Itinéraire / pathfinding", `TokaMapInstance.startRouteMode`) :
   * panneau flottant (`ui/RoutePanel`) avec 2 champs Départ/Arrivée + bouton Valider, qui calcule un
   * itinéraire à travers les formes `Shape.walkable: true` (`core/pathfinding.ts`) et l'affiche via
   * `MapAdapter.showRoute`. Réutilise `TokaMapConfig.search.getSearchItem` pour étiqueter les formes
   * sélectionnables (voir `RoutePanelConfig`) — sans effet (avertissement console) si `search` n'est
   * pas configuré. Absent/`false` : aucun panneau, `startRouteMode` ne fait rien.
   */
  routePanel?: RoutePanelConfig | false;
  /** Réservé à la visualisation, masqué sous deux documents. Se remet à jour à chaque `loadDocument`. */
  documentSwitcher?: DocumentSwitcherConfig | false;
  /**
   * Persistance des modifications (BESOINS.md §7) : appelée après chaque création, déplacement de
   * sommet, rotation, déplacement de forme entière ou suppression — avec l'état de la forme
   * concernée (son dernier état connu pour "delete", puisqu'elle n'est déjà plus dans le store).
   *
   * Optionnelle : absente, les modifications s'appliquent normalement, sans aucune promesse en jeu
   * (et sans appeler cette méthode). C'est aussi ce qui se passe pour toute modification faite
   * directement via `TokaMapInstance.shapeStore` plutôt que par un geste utilisateur — cette méthode
   * n'est câblée qu'au niveau `History`/`Command`, jamais à l'intérieur de `ShapeStore` lui-même.
   *
   * Optimiste : la modification s'applique tout de suite, sans attendre la résolution de la
   * promesse. Si elle est rejetée, la modification est annulée automatiquement — sauf si une autre
   * modification a déjà eu lieu sur cette même forme entre-temps (voir `startDrawingPolygon`/
   * `startDrawingRectangle`/`enableEditMode` : chaque forme a son propre cycle, indépendant des
   * autres, et un rollback tardif n'écrase jamais un changement plus récent déjà réussi).
   */
  onSave?: (event: SaveEvent) => Promise<void>;

  /**
   * Persistance de la "configuration" globale de la carte (BESOINS.md §7.1, `configurations` du
   * fichier exemple.json) : appelée après chaque modification de la vue de caméra par défaut
   * (`setMapView`, ou "Enregistrer" du mode interactif via `confirmMapViewEdit`) OU du style par
   * défaut des nouvelles formes dessinées (`setDefaultShapeStyle`) — même sémantique que `onSave`,
   * mais pour la "configuration" (une seule entité globale, pas une par forme) plutôt qu'une
   * `Shape`. Reçoit à chaque fois l'état COMPLET (`view` ET le style par défaut), pas seulement le
   * champ qui a changé — même principe qu'un `SaveEvent` de forme, qui porte le `Shape` entier. Voir
   * `ConfigurationSaveTracker`.
   *
   * Optionnelle, optimiste, avec rollback automatique en cas de rejet (sauf modification plus
   * récente déjà en vol/réussie) — mêmes règles que `onSave`, voir son commentaire.
   */
  onSaveConfiguration?: (event: SaveConfigurationEvent) => Promise<void>;

  /**
   * Appelé quand la carte a été chargée (via `loadJson`) depuis un document dont le format n'est
   * pas le plus récent (`LoadedData.formatVersion`, voir BESOINS.md §8.1/§8.3) — reçoit le document
   * ENTIER, déjà transformé dans le format le plus récent (voir `migrateDocumentToLatest`, exporté
   * par `@tokamap/core`), à charge pour le consommateur de le persister (ex. appel API) là où il
   * stocke habituellement le document brut.
   *
   * Tant qu'elle n'est pas fournie ET résolue avec succès, TOUT `onSave`/`onSaveConfiguration` est
   * bloqué : l'utilisateur voit un dialogue de confirmation avant chaque tentative de sauvegarde
   * (voir `FormatMigrationGate`/`ui/MigrationDialog`) — s'il refuse, la modification en cours est
   * annulée (comme un `onSave` rejeté ordinaire) et le dialogue réapparaîtra à la prochaine
   * tentative ; s'il accepte, cette méthode est appelée UNE fois (partagée entre toutes les
   * sauvegardes en attente au même moment, ex. une sélection multiple), puis la modification qui
   * avait déclenché le dialogue est sauvegardée normalement. Une fois résolue avec succès, plus
   * aucune vérification n'a lieu pour le reste de la session.
   *
   * Optionnelle : absente, aucune vérification n'a lieu (comportement historique inchangé) — ex.
   * un consommateur qui n'a jamais eu de données dans un format antérieur au plus récent n'a rien
   * à faire. Sans effet si `loadInitialData` est utilisé à la place de `loadJson` (pas de notion de
   * version de format dans ce cas).
   */
  onMigrateDocument?: (migratedDocument: unknown) => Promise<void>;
  /**
   * Thème clair/sombre de l'UI du package (voir `ui/style.css`) — seules les teintes neutres
   * (fonds/bordures/textes des widgets : `ContextMenu`, `SearchBox`, `EntityPanel`, `RoutePanel`,
   * `MigrationDialog`, boutons de mode édition de vue...) en dépendent ; les couleurs sémantiques
   * (voir `colors` ci-dessous) restent les mêmes dans les deux thèmes, déjà lisibles sur les deux
   * fonds. Appliqué via `container.dataset.tokaTheme`, dont héritent tous les widgets (tous montés
   * dans `container` ou un de ses descendants — voir `createTokaMap`). Par défaut : `"light"`
   * (comportement historique inchangé pour un consommateur qui ne configure rien).
   */
  theme?: "light" | "dark";
  /**
   * Remplace, champ par champ, les couleurs par défaut de l'éditeur — voir `TokaMapColors` pour le
   * détail de chaque clé et son usage. S'applique à la fois au rendu MapLibre (`MapLibreAdapter`,
   * via `MapLibreAdapterOptions.colors`) et, pour les clés qui ont un équivalent CSS
   * (`accent`/`accentSecondary`/`draft`/`danger`/`success`), aux widgets `ui/*` — posé comme
   * `theme` ci-dessus, via des variables `--toka-*` sur `container` (voir `createTokaMap`).
   * `rotate`/`highlight` n'ont pas d'équivalent CSS. Un champ omis garde sa valeur par défaut.
   */
  colors?: Partial<TokaMapColors>;
}

/**
 * Une commande pilotable depuis la console (`window.tokamap.commands`, voir
 * `packages/example/src/TokaMap.tsx`) — un simple pointeur de découvrabilité vers une méthode déjà
 * exposée ailleurs sur l'instance (ex. `setShapeStyle`), pas un mécanisme d'exécution séparé.
 */
export interface ConsoleCommand {
  /** Identifiant de la commande (ex. pour la retrouver via `commands.find(...)`) — pas affiché
   * dans le `console.table` de `TokaMap.tsx` (déjà présent dans `signature`). */
  name: string;
  /** Exemple d'appel concret, avec des valeurs bouchon plutôt que des noms de paramètres —
   * ex. `setShapeStyle("shape-1", { bgColor: "#ff0000" })`, copiable tel quel dans la console. */
  signature: string;
  /** Ce que fait la commande, en une courte phrase — pas la signature (voir `signature`). */
  description: string;
  run: (...args: any[]) => void;
}

export interface AddShapeFromFeatureOptions extends DrawTriggerOptions {
  /**
   * Calque (`Shape.layer`, z-order — voir `ShapeStore.getAll`) à assigner à la forme, ex. `10`,
   * `20`, `30` : les mêmes valeurs que les clés numériques `layers["10"]`/`layers["20"]`/... du
   * fichier exemple.json. Omis : la forme n'a pas de calque (affichée au-dessus de toutes les
   * formes calquées).
   */
  layer?: number;
}

/** Options de `TokaMapInstance.startRouteMode` (voir son commentaire). */
export interface StartRouteModeOptions {
  /** Préremplit le champ Départ avec cette forme — prioritaire sur le repli automatique sur la
   * position utilisateur (voir `startRouteMode`). Omis : le champ se préremplit sur la position
   * utilisateur si la géolocalisation est active, sinon reste vide. */
  prefillFromShapeId?: string;
  /** Préremplit le champ Arrivée avec cette forme. Omis : le champ reste vide. */
  prefillToShapeId?: string;
  /**
   * Calcule et affiche l'itinéraire immédiatement (comme si "Valider" était déjà cliqué), à
   * condition que les 2 champs aient une sélection à ce moment (prérempli explicitement, ou repli
   * automatique sur la position utilisateur — voir `startRouteMode`) — sinon sans effet, le panneau
   * reste simplement ouvert en attendant que l'utilisateur choisisse. Le panneau reste affiché dans
   * tous les cas (pour permettre de changer un champ et revalider), voir `RoutePanel.show`. Par
   * défaut : `false`.
   */
  autoValidate?: boolean;
}

export interface TokaMapInstance {
  mapAdapter: MapLibreAdapter;
  shapeStore: ShapeStore;
  history: History;
  /**
   * Remplace le document affiché par `json` (même format que ce que rend `TokaMapConfig.loadJson`,
   * voir `loaders/jsonLoader`) sans reconstruire l'instance : les formes, la caméra, les thèmes, le
   * fond et les styles par défaut deviennent ceux du nouveau document (un réglage qu'il ne porte pas
   * reprend sa valeur de `TokaMapConfig`, jamais celle du document précédent), tout ce qui pointait vers
   * l'ancien (fiche entité ouverte, itinéraire tracé, sélection, surlignage, pile d'annulation) est
   * remis à plat.
   *
   * La caméra REJOINT la nouvelle vue en volant (voir `LoadDocumentOptions.animateView`) : le
   * déplacement dit où l'on est allé — ce qu'un saut ne dit pas. La Promise résout
   * une fois les nouvelles formes peintes, sans attendre la fin du vol : un appelant qui enchaîne
   * (ex. tracer un itinéraire) le fait pendant le déplacement, pas après.
   *
   * Existe pour les documents qui se succèdent dans une même vue — typiquement plusieurs cartes
   * d'un même projet entre lesquelles on navigue. Le reconstruire à la place (recréer un
   * `createTokaMap`) laisserait derrière lui l'instance MapLibre précédente, faute de `destroy()`
   * sur `TokaMapInstance` : quelques allers-retours suffisent à épuiser les contextes WebGL que le
   * navigateur accorde à une page.
   *
   * La géolocalisation suit le document, comme son fond et ses thèmes : elle s'active ou se
   * désactive selon ce que porte `json`.
   */
  loadDocument: (json: unknown, options?: LoadDocumentOptions) => Promise<void>;
  /** À appeler quand la liste de documents change sans `loadDocument`. */
  refreshDocumentSwitcher: () => void;
  /**
   * S'abonne aux changements de statut de sauvegarde (BESOINS.md §7.1) : "pending" quand une
   * modification vient d'être envoyée à `TokaMapConfig.onSave`, "success"/"error" quand plus aucune
   * sauvegarde n'est en vol pour la forme concernée, avec le résultat final. Absent si `onSave`
   * n'est pas configuré (ou pour toute modification faite avec `skipSave`) — voir `ShapeSaveTracker`.
   */
  onSaveStatusChange: (listener: SaveStatusListener) => () => void;
  /**
   * Même rôle que `onSaveStatusChange`, mais pour `TokaMapConfig.onSaveConfiguration` (la vue de
   * caméra par défaut) plutôt que pour une forme — voir `ConfigurationSaveTracker`. Une seule entité
   * suivie ici (pas de `shapeId`).
   */
  onSaveConfigurationStatusChange: (listener: SaveConfigurationStatusListener) => () => void;
  /**
   * Recentre la carte sur sa vue de départ (`initialView` : centre/zoom/rotation chargés au
   * démarrage, ou valeurs par défaut de `MapLibreAdapter` sinon) ET remet le pitch (inclinaison 3D)
   * à 0 — même action que `ResetViewButton` (voir `TokaMapConfig.resetViewButton`), exposée ici
   * comme fonction nommée/commande console pour être déclenchable directement (ex. avant d'entrer
   * dans `startEditingMapView`, qui l'appelle déjà elle-même).
   */
  resetView: () => void;
  /**
   * Vue de caméra courante (centre, zoom, bornes de zoom, rotation) — voir `setMapView`. Simple
   * lecture de `mapAdapter` (jamais `undefined`, contrairement à `LoadedView`/`jsonLoader`,
   * qui laisse un champ absent si `exemple.json` ne le précise pas).
   */
  getMapView: () => MapViewState;
  /**
   * Capture la carte affichée et rend l'image (voir `MapLibreAdapter.captureImage`) : le fond, les
   * images SVG des formes et les formes, sans rien de l'interface ni de l'état d'édition (sélection,
   * poignées, surlignage). De quoi produire la vignette d'un projet sans repasser par un rendu
   * serveur.
   */
  captureImage: (options?: CaptureImageOptions) => Promise<CaptureImageResult>;
  /**
   * Change la vue de caméra (centre, zoom, bornes de zoom, rotation) — les mêmes informations que
   * `configurations` dans exemple.json (voir `jsonLoader`), mais modifiables après coup.
   * Chaque champ omis garde sa valeur courante (fusionné avec `getMapView()`, pas un remplacement
   * complet). Passe par `History` comme toute autre modification (undoable sauf
   * `options.skipHistory`), et par `ConfigurationSaveTracker`/`TokaMapConfig.onSaveConfiguration`
   * comme toute autre modification de forme passe par `ShapeSaveTracker`/`onSave` — mêmes options
   * `skipSave` (n'appelle jamais `onSaveConfiguration` pour cette modification) et `skipConfirm`
   * (même en cas de succès, ne fait pas avancer la référence "confirmée" utilisée par un rollback
   * futur) que pour les formes (voir `ShapeSaveTracker.run`).
   */
  setMapView: (
    view: Partial<MapViewState>,
    options?: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
  ) => void;
  /**
   * Change le style par défaut (`bgColor`/`borderColor`) appliqué aux PROCHAINES formes dessinées
   * (polygone/rectangle) — voir `TokaMapConfig.defaultShapeTheme`/`getDefaultShapeStyle`. Fusionné
   * avec le style par défaut courant (seules les clés fournies changent), sans effet sur les formes
   * déjà créées. Même mécanisme que `setMapView` : passe par `History`/`ConfigurationSaveTracker`
   * (undoable sauf `options.skipHistory`, déclenche `onSaveConfiguration` avec la configuration
   * ENTIÈRE — vue comprise — sauf `options.skipSave`, ne confirme jamais si `options.skipConfirm`).
   */
  setDefaultShapeStyle: (
    style: Partial<ShapeStyle>,
    options?: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
  ) => void;
  /**
   * Change le fond de carte (voir `TileLayerName`/`TokaMapConfig.defaultTileLayerName`) — REMPLACE
   * entièrement la valeur courante (scalaire, pas d'objet à fusionner comme `setDefaultShapeStyle`).
   * Même mécanisme que `setMapView`/`setDefaultShapeStyle` : passe par `History`/
   * `ConfigurationSaveTracker` (undoable sauf `options.skipHistory`, déclenche `onSaveConfiguration`
   * avec la configuration ENTIÈRE — vue et style par défaut compris — sauf `options.skipSave`, ne
   * confirme jamais si `options.skipConfirm`).
   */
  setTileLayerName: (
    tileLayerName: TileLayerName,
    options?: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
  ) => void;
  /**
   * FUSIONNE les champs fournis avec les réglages courants, comme `setDefaultShapeStyle` : un appel
   * ne portant que `{ style }` ne touche ni à la langue ni aux libellés. Même mécanisme par ailleurs
   * que `setTileLayerName`. Sans effet visible sous un autre fond, où la valeur est seulement
   * enregistrée.
   */
  setOsmOptions: (
    osmOptions: Partial<OsmBackgroundOptions>,
    options?: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
  ) => void;
  /**
   * Change la couleur de fond du conteneur DOM de la carte (voir
   * `TokaMapConfig.defaultBackgroundColor`/`getBackgroundColor`) — REMPLACE entièrement la valeur
   * courante (scalaire, pas d'objet à fusionner comme `setDefaultShapeStyle`), `undefined` pour
   * revenir au comportement historique (laisser le CSS du consommateur décider). Même mécanisme que
   * `setTileLayerName` : passe par `History`/`ConfigurationSaveTracker` (undoable sauf
   * `options.skipHistory`, déclenche `onSaveConfiguration` avec la configuration ENTIÈRE sauf
   * `options.skipSave`, ne confirme jamais si `options.skipConfirm`).
   */
  setBackgroundColor: (
    backgroundColor: string | undefined,
    options?: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
  ) => void;
  /**
   * Autorise/bloque l'inclinaison 3D de la caméra (voir `TokaMapConfig.defaultAllowPitch`/
   * `getAllowPitch`) — REMPLACE entièrement la valeur courante (scalaire). Même mécanisme que
   * `setTileLayerName`/`setBackgroundColor` : passe par `History`/`ConfigurationSaveTracker`
   * (undoable sauf `options.skipHistory`, déclenche `onSaveConfiguration` avec la configuration
   * ENTIÈRE sauf `options.skipSave`, ne confirme jamais si `options.skipConfirm`).
   */
  setAllowPitch: (
    allowPitch: boolean,
    options?: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
  ) => void;
  getFraming: () => MapFraming | undefined;
  setFraming: (
    framing: MapFraming | undefined,
    options?: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
  ) => void;
  /**
   * Démarre le mode d'édition INTERACTIVE de la vue par défaut (contrairement à `setMapView`, qui
   * l'applique directement) : recentre d'abord sur `initialView` (même vue que `ResetViewButton`)
   * — un point de départ propre et prévisible, plutôt que de partir d'où la caméra se trouvait par
   * hasard avant d'entrer dans le mode — puis affiche une croix au centre de l'écran et les
   * boutons Enregistrer/Annuler (voir `ui/MapViewCrosshair`/`ui/MapViewEditControls`). L'utilisateur
   * déplace/zoome/fait pivoter librement la carte avec les gestes natifs habituels (le pitch/
   * inclinaison 3D reste bloqué le temps du mode) à partir de cette vue réinitialisée, puis clique
   * "Enregistrer" (voir `confirmMapViewEdit`) pour valider la vue courante comme nouvelle vue par
   * défaut, ou "Annuler" (voir `cancelMapViewEdit`) pour revenir à `initialView` sans rien changer
   * — `options.allowCancel: false` masque même le bouton Annuler (et désactive Échap pour ce mode) :
   * seul "Enregistrer" permet alors d'en sortir. `options.skipSave`/`skipHistory`/`skipConfirm`
   * (`DrawTriggerOptions`, mêmes options que `setMapView`/`enableEditMode`) sont capturées ici et
   * reportées sur le `SetConfigurationCommand` que `confirmMapViewEdit()` construira, pas sur
   * `startEditingMapView` lui-même (qui ne construit aucune commande).
   */
  startEditingMapView: (options?: StartEditingMapViewOptions) => void;
  /**
   * "Enregistrer" (voir `startEditingMapView`) : applique la vue courante comme nouvelle vue par
   * défaut via `SetConfigurationCommand` (undoable), en utilisant la configuration capturée à
   * `startEditingMapView()` comme état "avant" — pas la vue courante, qui vaut alors exactement ce
   * qu'on enregistre (voir le commentaire de `mapViewEditBefore`). Sans effet si le mode n'est pas
   * actif (ex. déjà fermé via Annuler/Échap/un dessin démarré entre-temps).
   */
  confirmMapViewEdit: () => void;
  /**
   * "Annuler" (voir `startEditingMapView`) : revient à la vue capturée à `startEditingMapView()`
   * sans rien enregistrer, puis referme le mode. Sans effet si le mode n'est pas actif.
   */
  cancelMapViewEdit: () => void;
  /**
   * Démarre le mode "itinéraire" (voir CLAUDE.md "Itinéraire / pathfinding", `TokaMapConfig.routePanel`)
   * : affiche le panneau Départ/Arrivée/Valider — sans effet si `routePanel` n'est pas configuré (ou
   * mal configuré, voir l'avertissement console à ce sujet). Interrompt un dessin ou une édition de
   * vue en cours, même exclusivité que `startEditingMapView` avec le dessin (voir
   * `"drawing:started"`/`"map-view:edit-started"`, qui interrompent réciproquement ce mode).
   * `options.prefillFromShapeId`/`.prefillToShapeId` préremplissent l'un des deux champs (voir
   * `StartRouteModeOptions`) — sans `prefillFromShapeId`, le champ Départ se préremplit
   * automatiquement sur la position utilisateur courante si la géolocalisation est active (voir
   * `MapAdapter.getUserLocation`), pour permettre un "🧭 Y aller" direct depuis la fiche entité (voir
   * `EntityPanelActions.onGoTo`).
   */
  startRouteMode: (options?: StartRouteModeOptions) => void;
  /**
   * Ferme le mode itinéraire (bouton "×" du panneau, ou appel direct) : masque le panneau et efface
   * le tracé/surlignage affichés. Sans effet si le mode n'est pas actif.
   */
  cancelRouteMode: () => void;
  /**
   * Outil de diagnostic ponctuel (voir `core/pathfinding.ts`'s `findRouteDebugGrid`) — affiche la
   * grille effectivement explorée par le calcul d'itinéraire entre ces 2 formes : cellules bloquées
   * (obstacles réels) en rouge, cellules à prix réduit (`Shape.pathPriority`) en vert, plus opaque
   * quand le rabais est plus fort. Sans rapport avec `startRouteMode`/le calcul d'itinéraire réel
   * affiché à l'utilisateur — pensé pour être appelé depuis la console
   * (`window.tokamap.debugPathfindingGrid("1", "2")`), pas par une UI. Efface tout aperçu précédent ;
   * `mapAdapter.clearPathfindingDebugGrid()` l'efface sans en recalculer un nouveau.
   */
  debugPathfindingGrid: (fromShapeId: string, toShapeId: string) => void;
  /**
   * Change `Shape.walkable` d'une forme par son ID (fait/défait son appartenance au réseau
   * traversable par le mode itinéraire — voir `core/pathfinding.ts`) — REMPLACE entièrement la
   * valeur courante. Sans effet si `shapeId` est inconnu. Passe par `History`/`ShapeSaveTracker`
   * comme toute autre modification (undoable sauf `options.skipHistory`, déclenche `onSave` sauf
   * `options.skipSave`, ne confirme jamais la forme si `options.skipConfirm`). `shapeId` accepte
   * aussi un tableau d'IDs (BESOINS.md §13, modification groupée) : chaque forme est modifiée par
   * rapport à son PROPRE état courant, mais tout le groupe forme une seule entrée `History` (un
   * `Annuler` défait tout le groupe d'un coup) et un seul appel `onSave` (voir
   * `ShapeSaveTracker.runBatch`, `runShapeMutation` dans `createTokaMap.ts`).
   */
  setShapeWalkable: (
    shapeId: string | string[],
    walkable: boolean | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change `Shape.pathPriority` d'une forme par son ID (marque/démarque un "chemin"/"mur attractif"
   * pour le mode itinéraire — voir `core/pathfinding.ts`) — REMPLACE entièrement la valeur courante.
   * Mêmes principes que `setShapeWalkable` ci-dessus (batch/History/onSave via `runShapeMutation`).
   */
  setShapePathPriority: (
    shapeId: string | string[],
    pathPriority: Shape["pathPriority"],
    options?: DrawTriggerOptions,
  ) => void;
  /** Change `Shape.height` — mêmes principes que `setShapeWalkable`. */
  setShapeHeight: (
    shapeId: string | string[],
    height: Shape["height"],
    options?: DrawTriggerOptions,
  ) => void;
  /** Change `Shape.elevation` — mêmes principes que `setShapeWalkable`. */
  setShapeElevation: (
    shapeId: string | string[],
    elevation: Shape["elevation"],
    options?: DrawTriggerOptions,
  ) => void;
  /** Change `Shape.hoverHeight` — mêmes principes que `setShapeWalkable`. */
  setShapeHoverHeight: (
    shapeId: string | string[],
    hoverHeight: Shape["hoverHeight"],
    options?: DrawTriggerOptions,
  ) => void;
  /** Change `Shape.hoverElevation` — mêmes principes que `setShapeWalkable`. */
  setShapeHoverElevation: (
    shapeId: string | string[],
    hoverElevation: Shape["hoverElevation"],
    options?: DrawTriggerOptions,
  ) => void;
  /** Change `Shape.minZoomOffset` — mêmes principes que `setShapeWalkable`. */
  setShapeMinZoomOffset: (
    shapeId: string | string[],
    minZoomOffset: Shape["minZoomOffset"],
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change la couleur de fond/bordure d'une forme par son ID (`Shape.style`) — fusionné avec le
   * style courant de la forme (seules les clés fournies changent), sans effet si `shapeId` est
   * inconnu. Passe par `History`/`ShapeSaveTracker` comme toute autre modification (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la
   * forme si `options.skipConfirm`). `shapeId` accepte aussi un tableau d'IDs pour une modification
   * groupée — voir `setShapeWalkable` ci-dessus pour le détail du comportement (une seule entrée
   * `History`, un seul appel `onSave`).
   */
  setShapeStyle: (
    shapeId: string | string[],
    style: Partial<ShapeStyle>,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change le style de survol d'une forme par son ID (`Shape.hoverStyle`) — même logique que
   * `setShapeStyle` (fusionné avec le style de survol courant, sans effet si `shapeId` est
   * inconnu, undoable sauf `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne
   * confirme jamais la forme si `options.skipConfirm`). Sans effet visuel en dehors d'`editMode`
   * (voir `MapLibreAdapterOptions.editMode`). `shapeId` accepte aussi un tableau d'IDs pour une
   * modification groupée — voir `setShapeWalkable`.
   */
  setShapeHoverStyle: (
    shapeId: string | string[],
    hoverStyle: Partial<ShapeStyle>,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change l'image d'une forme par son ID (`Shape.image`, plaquée sur la forme en mode "contain" —
   * voir `MapLibreAdapter.imageQuadFor`) — même logique que `setShapeStyle`/`setShapeHoverStyle`
   * (fusionné avec l'image courante de la forme, seules les clés fournies changent), ex.
   * `setShapeImage("1", { scale: 0.5 }, { skipSave: true })`. Pour retirer l'image entièrement,
   * passer explicitement `{ src: undefined }` (un `src` absent ou vide, une fois fusionné, est
   * traité comme "pas d'image"). Sans effet si `shapeId` est inconnu. Si `width`/`height` sont
   * absents ou incohérents (voir `trustedImageDimensions`), l'image est d'abord chargée
   * (`loadImageDimensions`) pour les déduire automatiquement avant d'appliquer la mutation — d'où la
   * Promise retournée. Passe par `History`/`ShapeSaveTracker` comme toute autre modification
   * (undoable sauf `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme
   * jamais la forme si `options.skipConfirm`). `shapeId` accepte aussi un tableau d'IDs pour une
   * modification groupée — voir `setShapeWalkable` ; la résolution des dimensions se fait alors en
   * parallèle, une fois par forme du groupe.
   */
  setShapeImage: (
    shapeId: string | string[],
    image?: Partial<ShapeImage>,
    options?: DrawTriggerOptions,
  ) => Promise<void>;
  /**
   * Change le texte affiché au centre (centroïde) d'une forme par son ID (`Shape.text`) — même
   * logique que `setShapeStyle`/`setShapeHoverStyle` : FUSIONNÉ avec le texte courant de la forme
   * (seules les clés fournies changent, ex. `setShapeText("1", { size: 16 })` ne touche ni `text` ni
   * `color`/`bgColor`), sans effet si `shapeId` est inconnu. `undefined` retire le texte entièrement
   * (contrairement à un objet partiel, qui fusionne). Passe par `History`/`ShapeSaveTracker` comme
   * toute autre modification (undoable sauf `options.skipHistory`, déclenche `onSave` sauf
   * `options.skipSave`, ne confirme jamais la forme si `options.skipConfirm`). `shapeId` accepte
   * aussi un tableau d'IDs pour une modification groupée — voir `setShapeWalkable`.
   */
  setShapeText: (
    shapeId: string | string[],
    text: Partial<ShapeText> | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change l'identifiant d'action déclenchée par un clic sur une forme par son ID (`Shape.action`)
   * — REMPLACE entièrement la valeur courante (scalaire, pas d'objet à fusionner comme
   * `setShapeStyle`/`setShapeText`), `undefined` la retire entièrement. Sans effet si `shapeId` est
   * inconnu. Passe par `History`/`ShapeSaveTracker` comme toute autre modification (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la forme
   * si `options.skipConfirm`). `shapeId` accepte aussi un tableau d'IDs pour une modification
   * groupée — voir `setShapeWalkable`.
   */
  setShapeAction: (
    shapeId: string | string[],
    action: string | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change `Shape.useActionIcon` d'une forme par son ID (affiche ou non une icône signalant que la
   * forme déclenche une action au clic) — REMPLACE entièrement la valeur courante (booléen, même
   * logique que `setShapeAction`). Sans effet si `shapeId` est inconnu. Passe par
   * `History`/`ShapeSaveTracker` comme toute autre modification (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la forme
   * si `options.skipConfirm`). `shapeId` accepte aussi un tableau d'IDs pour une modification
   * groupée — voir `setShapeWalkable`.
   */
  setShapeUseActionIcon: (
    shapeId: string | string[],
    useActionIcon: boolean | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change `Shape.urlAction` d'une forme par son ID (configuration de la redirection déclenchée
   * par `action === "open_url"`, voir `Shape.urlAction`) — FUSIONNE avec la valeur courante (même
   * logique que `setShapeText`/`setShapeStyle`, pas un remplacement scalaire comme `setShapeAction`) :
   * `undefined` la retire entièrement plutôt que de fusionner. Sans effet si `shapeId` est inconnu.
   * Passe par `History`/`ShapeSaveTracker` comme toute autre modification (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la forme
   * si `options.skipConfirm`). `shapeId` accepte aussi un tableau d'IDs pour une modification
   * groupée — voir `setShapeWalkable`.
   */
  setShapeUrlAction: (
    shapeId: string | string[],
    urlAction: Partial<ShapeUrlAction> | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change `Shape.targetDocumentId` d'une forme par son ID (le document ouvert au clic — jamais
   * interprété par `@tokamap/core` lui-même, voir `Shape.targetDocumentId`) — REMPLACE entièrement
   * la valeur courante (même logique que `setShapeAction`), `undefined` la retire ; tableau d'IDs
   * accepté pour une modification groupée.
   */
  setShapeTargetDocumentId: (
    shapeId: string | string[],
    targetDocumentId: string | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change `Shape.locked` d'une forme par son ID (verrouille/déverrouille — voir `Shape.locked`) —
   * REMPLACE entièrement la valeur courante (booléen, même logique que `setShapeUseActionIcon`).
   * Sans effet si `shapeId` est inconnu. Passe par `History`/`ShapeSaveTracker` comme toute autre
   * modification (undoable sauf `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`,
   * ne confirme jamais la forme si `options.skipConfirm`). Même effet que basculer le cadenas de la
   * barre d'outils flottante (survol en `editMode`, voir `MapAdapterEvent` `"shape:lock-toggled"`).
   * `shapeId` accepte aussi un tableau d'IDs pour une modification groupée — voir `setShapeWalkable`
   * (c'est ce que le menu contextuel "Verrouiller" d'un groupe sélectionné utilise désormais).
   */
  setShapeLocked: (
    shapeId: string | string[],
    locked: boolean | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Lie une forme à un thème (`Shape.themeId` — voir `ShapeTheme`), `undefined` pour la délier.
   * REMPLACE entièrement la valeur courante. Ne touche pas `Shape.style`/`hoverStyle` : délier une
   * forme lui rend son apparence propre, inchangée depuis avant le lien.
   *
   * Passe par `History`/`ShapeSaveTracker` comme toute autre modification de forme (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`). `shapeId` accepte un
   * tableau d'IDs pour une modification groupée — voir `setShapeWalkable`.
   */
  setShapeThemeId: (
    shapeId: string | string[],
    themeId: string | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Monte une forme d'un calque (`Shape.layer`, z-order — voir "Monter" du menu contextuel) : passe
   * au calque concret suivant, par pas de 10 au-dessus de `Shape.layer ?? 50` (calque assigné par
   * défaut à toute forme dessinée, voir `DEFAULT_NEW_SHAPE_LAYER`), plafonné à 100. Sans effet si
   * `shapeId` est inconnu. Passe par `History`/`ShapeSaveTracker` comme toute autre modification
   * (undoable sauf `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme
   * jamais la forme si `options.skipConfirm`). `shapeId` accepte aussi un tableau d'IDs pour une
   * modification groupée — voir `setShapeWalkable` ; chaque forme monte par rapport à SON PROPRE
   * calque courant.
   */
  raiseShapeLayer: (shapeId: string | string[], options?: DrawTriggerOptions) => void;
  /** Symétrique de `raiseShapeLayer` ("Descendre" du menu contextuel) : par pas de 10 en dessous de
   * `Shape.layer ?? 50`, plancher à 0. */
  lowerShapeLayer: (shapeId: string | string[], options?: DrawTriggerOptions) => void;
  /**
   * Monte une forme directement au calque maximal (`Shape.layer = 100`, voir "Monter tout en haut"
   * du menu contextuel) — contrairement à `raiseShapeLayer`, ignore le calque courant et ne monte
   * pas par pas. Sans effet si `shapeId` est inconnu ou déjà au calque maximal. Même comportement
   * `History`/`onSave`/tableau d'IDs que `raiseShapeLayer`.
   */
  bringShapeToFront: (shapeId: string | string[], options?: DrawTriggerOptions) => void;
  /** Symétrique de `bringShapeToFront` ("Descendre tout en bas" du menu contextuel) : calque minimal
   * (`Shape.layer = 0`). */
  sendShapeToBack: (shapeId: string | string[], options?: DrawTriggerOptions) => void;
  /**
   * Change la charge métier opaque d'une forme par son ID (`Shape.entity`, ex. Place JapanExpo) —
   * REMPLACE entièrement la valeur courante (jamais interprétée par `@tokamap/core`, voir
   * `Shape.entity`/`setShapeAction` pour la même logique appliquée à un autre champ scalaire/objet
   * opaque). Sans effet si `shapeId` est inconnu. Passe par `History`/`ShapeSaveTracker` comme toute
   * autre modification (undoable sauf `options.skipHistory`, déclenche `onSave` sauf
   * `options.skipSave`, ne confirme jamais la forme si `options.skipConfirm`). `shapeId` accepte
   * aussi un tableau d'IDs pour une modification groupée — voir `setShapeWalkable`.
   */
  setShapeEntity: (
    shapeId: string | string[],
    entity: unknown,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Change le texte affiché au survol (`editMode` uniquement) d'une forme par son ID
   * (`Shape.hoverText`) — même logique que `setShapeText` : REMPLACE entièrement le `hoverText`
   * courant (fusionné avec ses clés existantes, `undefined` le retire entièrement plutôt que de
   * fusionner), sans effet si `shapeId` est inconnu. Sans effet visuel en dehors d'`editMode` (voir
   * `MapLibreAdapterOptions.editMode`), comme `setShapeHoverStyle`. Passe par
   * `History`/`ShapeSaveTracker` comme toute autre modification (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la forme
   * si `options.skipConfirm`). `shapeId` accepte aussi un tableau d'IDs pour une modification
   * groupée — voir `setShapeWalkable`.
   */
  setShapeHoverText: (
    shapeId: string | string[],
    hoverText: Partial<ShapeText> | undefined,
    options?: DrawTriggerOptions,
  ) => void;
  /**
   * Supprime une forme par son ID — même logique que le menu contextuel "Supprimer" (voir
   * `mapAdapter.on(...)`), utilisable directement sans clic droit sur la carte. Passe par
   * `History`/`ShapeSaveTracker` comme toute autre modification (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la
   * forme si `options.skipConfirm`). Sans effet si `shapeId` est inconnu. `shapeId` accepte aussi un
   * tableau d'IDs pour une suppression groupée — voir `setShapeWalkable` pour le détail du
   * comportement (une seule entrée `History`, un seul appel `onSave`, ici avec `kind: "delete"`).
   */
  deleteShape: (shapeId: string | string[], options?: DrawTriggerOptions) => void;
  /**
   * Copie les formes données (menu contextuel "Copier", ou raccourci clavier Ctrl+C côté exemple)
   * dans le presse-papier (`TokaMapConfig.clipboard`, à défaut un presse-papier interne à l'instance)
   * — REMPLACE entièrement son contenu précédent (pas un ajout). Une forme `locked` ne peut normalement jamais atteindre cet appel : les deux seuls
   * chemins qui l'invoquent (menu contextuel, raccourci clavier) l'excluent déjà en amont (voir
   * `Shape.locked`, `packages/example/src/useKeyboardShortcuts.ts`) — `copyShapes` elle-même reste
   * volontairement inconditionnelle (comme `deleteShape`), pour ne pas imposer cette restriction à un
   * futur appelant qui ne la voudrait pas. Sans effet si aucun des ids donnés n'existe.
   */
  copyShapes: (shapeIds: string[]) => void;
  /**
   * Colle le contenu courant du presse-papier interne (voir `copyShapes`) : crée une copie de chaque
   * forme copiée, avec un nouvel id (`generateId`). No-op si le presse-papier est vide. Chaque forme
   * collée passe par `History`/`ShapeSaveTracker` comme une création ordinaire (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la forme si
   * `options.skipConfirm`).
   *
   * `at` (lng/lat) : point où doit atterrir le CENTRE du groupe collé (boîte englobante de toutes
   * les formes du presse-papier) — les formes gardent leurs positions relatives entre elles (une
   * sélection multiple copiée-collée garde sa forme), seul le groupe entier est translaté. Utilisé
   * par le menu contextuel "Coller" (voir `mapAdapter.screenPositionToLngLat`) pour coller
   * exactement là où le clic droit a eu lieu. Omis (Ctrl+V au clavier, appel console) : chaque forme
   * est décalée indépendamment par rapport à sa propre position d'origine (voir
   * `computePasteOffset`), pour rester visible/distincte sans dépendre d'un point de clic.
   *
   * Contenu copié depuis un autre document (`ShapeClipboard.origin` différent) : le groupe atterrit
   * centré sur `at`, ou à défaut sur le centre de la vue, à sa taille réelle (voir `relocateShapes`).
   */
  pasteClipboard: (at?: LngLat, options?: DrawTriggerOptions) => void;
  /**
   * Copie l'apparence d'une forme (`Shape.style`/`hoverStyle`/`image`/`text`/`hoverText`) dans un
   * presse-papier de style interne, séparé du presse-papier de formes ci-dessus (menu contextuel
   * "Copier le style") — REMPLACE entièrement son contenu précédent. Copie TOUT sauf 3 attributs de
   * contenu/comportement, jamais touchés : `ShapeImage.src` (l'URL de l'image), le texte lui-même
   * (`ShapeText.text`, pour `text` ET `hoverText`), et tout ce qui relève de l'action au clic
   * (`Shape.action`/`useActionIcon`/`urlAction`). Sans effet si `shapeId` est inconnu.
   */
  copyShapeStyle: (shapeId: string) => void;
  /**
   * Colle le style couramment copié (voir `copyShapeStyle`) sur une ou plusieurs formes par leur
   * ID — REMPLACE entièrement leur `style`/`hoverStyle` (pas une fusion partielle : une clé absente
   * du style copié efface la valeur courante correspondante sur la forme collée). `image`/`text`/
   * `hoverText` sont eux fusionnés (pas remplacés) avec ceux de la forme cible, et UNIQUEMENT si
   * elle en a déjà un — coller le style d'une forme avec image sur une forme SANS image ne lui en
   * crée pas une (il manquerait `src`, un champ requis) : rien à faire dans ce cas pour cette
   * partie précise. No-op entièrement si le presse-papier de style est vide. Passe par
   * `History`/`ShapeSaveTracker` comme toute autre modification (undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la forme
   * si `options.skipConfirm`). `shapeId` accepte aussi un tableau d'IDs pour une modification
   * groupée — voir `setShapeWalkable` (une seule entrée `History`, un seul appel `onSave` pour tout
   * le groupe, ET pour toutes les parties d'une même forme ensemble). Retourne une `Promise` (comme
   * `setShapeImage`) : la partie image, si copiée, doit d'abord résoudre ses dimensions.
   */
  pasteShapeStyle: (shapeId: string | string[], options?: DrawTriggerOptions) => Promise<void>;
  /**
   * Ajoute une forme à partir des mêmes données qu'un "feature" du fichier exemple.json, au format
   * le plus récent (voir `LatestRawFeature`/`parseLatestJsonFeature`, qui pointent toujours
   * vers le dernier connecteur de version, actuellement v2) — coordonnées Web Mercator, `properties`
   * imbriqué dans `geometry`, `properties.extra.type` pour distinguer rectangle/polygone — pas le
   * format interne `Shape` — pratique pour injecter directement une forme copiée depuis les données
   * réelles. Sans effet si `feature.geometry.type` n'est pas "Polygon" (ou anneau < 3 points). Passe
   * par `History`/`ShapeSaveTracker` comme toute autre création (undoable sauf `options.skipHistory`,
   * déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la forme si `options.skipConfirm`).
   */
  addShapeFromFeature: (feature: LatestRawFeature, options?: AddShapeFromFeatureOptions) => void;
  /**
   * Démarre le dessin d'un polygone (clic pour chaque sommet, puis clic sur le premier sommet pour
   * fermer) — simple relais vers `mapAdapter.startDrawingPolygon` (voir `DrawAdapter`), exposé
   * aussi directement ici pour la cohérence avec le reste de l'instance (`setShapeStyle`,
   * `deleteShape`, ...). Affiche par défaut un tutoriel pas-à-pas guidant ce geste (voir
   * `ui/DrawTutorial`, `StartDrawingOptions.skipTutorial`) ; `{ skipTutorial: true }` le désactive
   * pour cette session de dessin précise.
   */
  startDrawingPolygon: (options?: StartDrawingOptions) => void;
  /**
   * Démarre le dessin d'un rectangle (2 clics : premier coin, puis coin opposé) — voir
   * `startDrawingPolygon` ci-dessus, même tutoriel pas-à-pas (widget partagé), même option
   * `skipTutorial` pour le désactiver.
   */
  startDrawingRectangle: (options?: StartDrawingOptions) => void;
  /**
   * Démarre le dessin d'un cercle (2 clics : centre, puis point du bord) — voir
   * `startDrawingPolygon` ci-dessus, même tutoriel pas-à-pas (widget partagé), même option
   * `skipTutorial` pour le désactiver. `Shape.coordinates` ne garde que ces 2 points (voir son
   * commentaire) — le rendu développe un polygone régulier à l'affichage, sans le persister.
   */
  startDrawingCircle: (options?: StartDrawingOptions) => void;
  /**
   * Démarre le dessin d'une ligne (un clic par sommet, puis clic sur le dernier sommet posé pour
   * terminer — pas de fermeture sur le premier sommet, voir `DrawAdapter.startDrawingLine`) — voir
   * `startDrawingPolygon` ci-dessus, même tutoriel pas-à-pas (widget partagé), même option
   * `skipTutorial` pour le désactiver.
   */
  startDrawingLine: (options?: StartDrawingOptions) => void;
  /**
   * Démarre le dessin d'une image (2 clics : premier coin, puis coin opposé — même geste que
   * `startDrawingRectangle`), en gardant le ratio largeur/hauteur de l'image pendant tout le dessin
   * (voir `MapLibreAdapter.startDrawingImage`). Si `options.width`/`.height` sont absents ou
   * incohérents, l'image est d'abord chargée (`loadImageDimensions`) pour déduire son ratio avant
   * de démarrer le dessin (d'où la Promise retournée) — n'affiche rien tant que ce chargement n'est
   * pas résolu, et abandonne silencieusement (avec un avertissement console) s'il échoue.
   */
  startDrawingImage: (src: string, options?: StartDrawingImageOptions) => Promise<void>;
  /**
   * Retourne le style par défaut COURANT (`TokaMapConfig.defaultShapeTheme`, éventuellement remplacé
   * par `configurations.defaultShapeStyle`/`featureStyle` au chargement, ou par un appel ultérieur à
   * `setDefaultShapeStyle` — voir son commentaire) appliqué aux nouvelles formes dessinées
   * (polygone/rectangle) — `undefined` si aucun n'a été configuré. Toujours lu en direct depuis
   * `mapAdapter` (jamais la valeur figée de `TokaMapConfig` à la construction).
   */
  getDefaultShapeStyle: () => ShapeStyle | undefined;
  /**
   * Remplace la table des thèmes référençables par `Shape.themeId` (voir `ShapeTheme`). Les formes
   * liées sont redessinées immédiatement : c'est ainsi qu'une modification de thème se propage,
   * sans qu'aucune forme ne soit modifiée ni persistée.
   *
   * Contrairement à `setDefaultShapeStyle`, NE passe PAS par `History`/`ConfigurationSaveTracker` :
   * les thèmes n'appartiennent pas à la configuration de la carte (le consommateur les possède et
   * les persiste où il veut), donc ni undoable, ni `onSaveConfiguration`.
   */
  setThemes: (themes: ShapeThemes) => void;
  /** Table des thèmes courante — voir `setThemes`. */
  getThemes: () => ShapeThemes;
  /**
   * Thème posé d'office sur toute forme nouvellement DESSINÉE. Sans effet sur les formes déjà
   * créées, ni undoable, ni persisté par `onSaveConfiguration` : c'est au consommateur de savoir
   * quel thème est son défaut (côté back-office, un drapeau dans le `ProjectMeta` des thèmes).
   */
  setDefaultThemeId: (themeId: string | undefined) => void;
  /** Voir `setDefaultThemeId`. */
  getDefaultThemeId: () => string | undefined;
  /**
   * Retourne le fond de carte COURANT (`TokaMapConfig.defaultTileLayerName`, éventuellement remplacé
   * par `configurations.tileLayerName` au chargement, ou par un appel ultérieur à `setTileLayerName`
   * — voir son commentaire), `"unset"` si aucun n'a été configuré. Toujours lu en direct depuis
   * `mapAdapter` (jamais la valeur figée de `TokaMapConfig` à la construction).
   */
  getTileLayerName: () => TileLayerName;
  /** `undefined` tant qu'aucun réglage n'a été touché, et un champ jamais renseigné reste absent —
   * voir `resolveOsmOptions` pour la valeur par défaut de chacun. */
  getOsmOptions: () => OsmBackgroundOptions | undefined;
  /**
   * Retourne la couleur de fond COURANTE du conteneur DOM de la carte
   * (`TokaMapConfig.defaultBackgroundColor`, éventuellement remplacée par
   * `configurations.backgroundColor` au chargement, ou par un appel ultérieur à
   * `setBackgroundColor` — voir son commentaire), `undefined` si aucune n'a été configurée. Toujours
   * lue en direct depuis `mapAdapter` (jamais la valeur figée de `TokaMapConfig` à la construction).
   */
  getBackgroundColor: () => string | undefined;
  /**
   * Retourne si l'inclinaison 3D de la caméra est actuellement autorisée
   * (`TokaMapConfig.defaultAllowPitch`, éventuellement remplacée par `configurations.allowPitch` au
   * chargement, ou par un appel ultérieur à `setAllowPitch`). Toujours lue en direct depuis
   * `mapAdapter`, `false` si jamais activée.
   */
  getAllowPitch: () => boolean;
  /**
   * Retourne si le repérage de la position de l'utilisateur (voir BESOINS.md §15) est actuellement
   * activé — `TokaMapConfig.geolocation`, éventuellement remplacé par `configurations.geolocation` au
   * chargement, ou par un appel ultérieur à `setGeolocationEnabled`. Toujours lu en direct depuis
   * `mapAdapter`, `false` si jamais activé.
   */
  getGeolocationEnabled: () => boolean;
  /**
   * Active/désactive le repérage de la position de l'utilisateur — affiche (ou masque) un point sur
   * la carte à sa position courante, ainsi qu'un indicateur de direction (cap boussole, quand
   * disponible). Même mécanisme que `setTileLayerName` : REMPLACE entièrement la valeur courante
   * (scalaire), passe par `History`/`ConfigurationSaveTracker` (undoable sauf `options.skipHistory`,
   * déclenche `onSaveConfiguration` avec la configuration ENTIÈRE sauf `options.skipSave`, ne confirme
   * jamais si `options.skipConfirm`).
   */
  setGeolocationEnabled: (
    enabled: boolean,
    options?: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
  ) => void;
  /** Retourne toutes les formes actuellement dans le store (triées par calque puis ordre — voir
   * `ShapeStore.getAll`) — simple relais, exposé aussi comme commande console (voir `commands`). */
  getAllShapes: () => Shape[];
  /**
   * Surligne (contour + fond pulsants) l'ensemble des formes de `shapeIds` — simple relais vers
   * `mapAdapter.highlightShapes` (voir `MapAdapter.highlightShapes`), exposé aussi directement ici
   * pour la cohérence avec le reste de l'instance (`setShapeStyle`, `deleteShape`, ...) et comme
   * commande console (voir `commands`). `options.zoomToFit` centre/zoome la caméra pour faire tenir
   * les formes surlignées à l'écran (voir `HighlightShapesOptions`).
   */
  highlightShapes: (shapeIds: string[], options?: HighlightShapesOptions) => void;
  /**
   * Affiche (ou met à jour) le curseur d'un autre utilisateur sur la carte (collaboration
   * multi-utilisateur temps réel) — simple relais vers `mapAdapter.setRemoteCursor` (voir
   * `MapAdapter.setRemoteCursor`/`RemoteCursor`), exposé aussi directement ici pour la cohérence
   * avec le reste de l'instance et comme commande console (voir `commands`). Un appel avec un
   * `cursor.id` déjà affiché REMPLACE entièrement son état précédent (position/couleur/nom compris).
   * Purement visuel : ne passe ni par `History` ni par `onSave`.
   */
  setRemoteCursor: (cursor: RemoteCursor) => void;
  /**
   * Efface le curseur affiché par `setRemoteCursor` pour cet id — simple relais vers
   * `mapAdapter.removeRemoteCursor`, no-op si `id` est inconnu.
   */
  removeRemoteCursor: (id: string) => void;
  /** Tableau de commandes pilotables depuis la console (voir `ConsoleCommand`) : `resetView`,
   * `getMapView`, `setMapView`, `setDefaultShapeStyle`,
   * `startEditingMapView`, `confirmMapViewEdit`, `cancelMapViewEdit`,
   * `panByScreenPixels`, `setShapeStyle`, `setShapeHoverStyle`, `setShapeImage`, `setShapeText`,
   * `setShapeAction`, `setShapeUseActionIcon`, `setShapeLocked`, `raiseShapeLayer`, `lowerShapeLayer`,
   * `bringShapeToFront`, `sendShapeToBack`,
   * `setShapeEntity`, `setShapeHoverText`, `deleteShape`,
   * `copyShapes`, `pasteClipboard`,
   * `startDrawingPolygon`, `startDrawingRectangle`, `startDrawingCircle`, `startDrawingImage`,
   * `addShapeFromFeature`, `getDefaultShapeStyle`, `setTileLayerName`, `getTileLayerName`,
   * `setBackgroundColor`, `getBackgroundColor`,
   * `getAllShapes`, `highlightShapes`, `setRemoteCursor`, `removeRemoteCursor`,
   * `setGeolocationEnabled`, `getGeolocationEnabled`, `captureImage`. */
  commands: ConsoleCommand[];
}

/** Bornes de `Shape.layer` pour "Monter"/"Descendre"/"Monter tout en haut"/"Descendre tout en bas"
 * (menu contextuel) — voir `raiseShapeLayer`/`lowerShapeLayer`/`bringShapeToFront`/`sendShapeToBack`
 * ci-dessous. */
const MIN_SHAPE_LAYER = 0;
const MAX_SHAPE_LAYER = 100;
const SHAPE_LAYER_STEP = 10;

/** Niveau de zoom appliqué quand on valide une adresse dans `AddressSearchBox` (voir plus bas) — un
 * niveau "rue"/bâtiment, pour que la validation amène réellement sur le point visé plutôt que de se
 * contenter de le recentrer au zoom courant (qui peut être bien plus dézoomé). */
const ADDRESS_SEARCH_ZOOM = 17;

/**
 * Clé localStorage de la case "Grille de pathfinding" du bouton diagnostic de `FpsMeter` — voir son
 * écriture/lecture plus bas dans `createTokaMap`. Volontairement dans `@tokamap/core` (pas dans un
 * fichier propre à un consommateur, contrairement à `EDIT_MODE_STORAGE_KEY` côté `packages/example`) :
 * cette préférence doit être partagée par TOUTE instance `createTokaMap`, quel que soit le
 * consommateur qui l'embarque, sans qu'aucun d'eux n'ait à la câbler lui-même. `localStorage` étant
 * partagé par toutes les pages d'une même origine, une seule clé suffit à la fois à la faire
 * survivre à un rechargement ET à la synchroniser entre plusieurs onglets/instances déjà ouverts
 * (voir l'écouteur `"storage"` plus bas — qui ne se déclenche que dans les AUTRES onglets, jamais
 * celui qui vient d'écrire la valeur).
 */
const DEBUG_GRID_STORAGE_KEY = "tokamap:debugGridEnabled";

/** Voir BESOINS.md §7 : save() est appelé à chaque création/suppression/undo/redo. */
function save(): void {
  console.log("[save] déclenché");
}

/** Voir BESOINS.md §4 : clic sur une forme existante. */
function onShapeClick(shapeId: string): void {
  console.log("[onShapeClick]", shapeId);
}

/**
 * Câble une instance complète de l'outil (carte, formes, undo/redo, UI d'édition) dans `container`
 * — c'est le point d'entrée que tout consommateur (React ou non) doit utiliser plutôt que
 * d'assembler `MapLibreAdapter`/`ShapeStore`/`History`/`ui/*` à la main.
 */
export async function createTokaMap(
  container: HTMLElement,
  config: TokaMapConfig = {},
): Promise<TokaMapInstance> {
  // Conservé pour `migrateDocumentToLatest` (voir plus bas, `migrationGate`) : le document brut
  // (avant reprojection/conversion en `Shape[]`) est nécessaire pour construire le paramètre de
  // `TokaMapConfig.onMigrateDocument`, sinon jeté par `parseJsonDocument` une fois parsé.
  let rawLoadedDocument: unknown;
  const loadInitialData =
    config.loadInitialData ??
    (config.loadJson
      ? async () => {
          rawLoadedDocument = await config.loadJson!();
          return parseJsonDocument(rawLoadedDocument);
        }
      : // `@tokamap/core` ne présume d'aucune source de données par défaut (pas de fichier/URL
        // deviné) : ni `loadInitialData` ni `loadJson`, on démarre simplement sans forme, au
        // consommateur de fournir l'un des deux pour charger quoi que ce soit.
        async (): Promise<LoadedData> => ({ shapes: [] }));

  // Remonté depuis plus bas (voir son propre commentaire) : `LoadErrorView`, ci-dessous, est
  // positionnée `absolute` dans `container` comme les autres widgets, donc a besoin de ce même
  // ancêtre positionné AVANT le premier essai de chargement, pas seulement une fois qu'il a réussi.
  if (getComputedStyle(container).position === "static") {
    container.style.position = "relative";
  }
  if (getComputedStyle(container).overflow === "visible") {
    container.style.overflow = "hidden";
  }
  // Voir `TokaMapConfig.theme` : posé sur `container` lui-même (pas un widget en particulier) pour
  // que les variables CSS de `ui/style.css` (`[data-toka-theme="dark"] { ... }`) s'appliquent par
  // héritage à tous les widgets, quel que soit leur ancêtre direct (`container`, `drawOverlay`,
  // `mapContainer`...).
  container.dataset.tokaTheme = config.theme ?? "light";

  // Voir `TokaMapConfig.colors` : même principe que `theme` ci-dessus, mais uniquement pour les
  // clés fournies (un champ omis garde le fallback CSS posé dans `[data-toka-theme]`, voir
  // `style.css`) — `rotate`/`highlight` n'ont pas d'équivalent CSS, ignorées ici (elles atteignent
  // `MapLibreAdapter` seul, voir plus bas).
  const CSS_COLOR_VARS: Partial<Record<keyof TokaMapColors, string>> = {
    accent: "--toka-accent",
    accentSecondary: "--toka-accent-secondary",
    draft: "--toka-draft",
    danger: "--toka-danger",
    success: "--toka-success",
  };
  for (const [key, cssVar] of Object.entries(CSS_COLOR_VARS)) {
    const value = config.colors?.[key as keyof TokaMapColors];
    if (value != null) container.style.setProperty(cssVar, value);
  }

  // BESOINS.md §8 : chargement initial des formes déjà enregistrées, fait AVANT toute création de
  // DOM/MapLibre ci-dessous. Si `loadInitialData`/`loadJson` rejette, l'erreur est affichée à la
  // place de la carte (voir `ui/LoadErrorView`) avec un bouton "Réessayer" qui relance le
  // chargement — on ne quitte cette boucle, et donc on ne touche à `container`/`mapAdapter`, qu'une
  // fois le chargement réussi. Ce comportement vit ici plutôt que côté consommateur (voir
  // `packages/example/src/TokaMap.tsx`, qui se contente d'attendre que la Promise résolve) : c'est
  // générique, pas quelque chose que chaque application consommatrice devrait réimplémenter. Ce
  // n'est pas une action utilisateur : ça ne passe pas par History (pas d'undo "annuler le
  // chargement") ni par save().
  const loadErrorView = new LoadErrorView(container);
  let initialData: LoadedData;
  for (;;) {
    try {
      initialData = await loadInitialData();
      break;
    } catch (error) {
      console.error("[createTokaMap] échec du chargement des données initiales :", error);
      await loadErrorView.show(error);
    }
  }
  const { shapes: initialShapes, view } = initialData;

  const mapContainer = document.createElement("div");
  mapContainer.id = "map";
  container.appendChild(mapContainer);

  const editMode = config.editMode ?? false;

  const mapAdapter = new MapLibreAdapter({
    resolveImageUrl: config.resolveImageUrl,
    minImageOverlayScreenPx: config.minImageOverlayScreenPx,
    snapThresholds: config.snapThresholds,
    editMode,
    defaultAllowPitch: config.defaultAllowPitch,
    defaultShapeTheme: config.defaultShapeTheme,
    themes: config.themes,
    defaultThemeId: config.defaultThemeId,
    defaultTileLayerName: config.defaultTileLayerName,
    defaultOsmOptions: config.defaultOsmOptions,
    defaultBackgroundColor: config.defaultBackgroundColor,
    includeImages: config.includeImages,
    includeIndirectImages: config.includeIndirectImages,
    showDrawingMeasures: config.showDrawingMeasures,
    colors: config.colors,
  } satisfies MapLibreAdapterOptions);
  await mapAdapter.init(mapContainer);

  const shapeStore = new ShapeStore();
  shapeStore.onChange((shapes) => mapAdapter.renderShapes(
    config.resolveLinkedContent
      ? shapes.map((shape) => applyLinkedContent(shape, config.resolveLinkedContent))
      : shapes,
  ));
  // `let`, pas `const` : bascule à `LATEST_FORMAT_VERSION` une fois la migration acceptée
  // (voir `migrationGate` ci-dessous) — sinon `getConfigurationState()` continuerait de rapporter
  // l'ancienne version à `onSaveConfiguration` alors que le document a déjà été migré.
  let formatVersion = initialData.formatVersion;

  /**
   * Vue affichée au démarrage du document courant, quelle que soit son origine (chargée ou valeurs
   * par défaut de `MapLibreAdapter`) — ce sur quoi `ResetViewButton` recentre la carte (voir plus
   * bas). Réaffectée à chaque changement de document (voir `applyLoadedView`/`loadDocument`) : un
   * "recentrer" ramène à la vue du document affiché, pas à celle du tout premier chargé.
   */
  let initialView: { center: LngLat; zoom: number; rotationDeg: number };

  /**
   * Applique au `mapAdapter` tout ce qu'un document porte hors formes (caméra, thèmes, fond,
   * styles par défaut) et retient la vue obtenue dans `initialView`. Partagée entre le chargement
   * initial et `loadDocument` pour que les deux chemins ne puissent pas diverger.
   *
   * Appelée AVANT de peupler le store : `addMany`/`replaceAll` déclenchent le rendu (et donc, pour
   * les formes-image, le premier calcul de taille CDN) de façon synchrone. Dans l'autre ordre,
   * cette taille serait calculée avec le zoom/zoomMax par défaut de la carte au lieu de ceux de la
   * vue chargée, provoquant un premier fetch à une taille absurde suivi d'un second une fois la
   * caméra repositionnée.
   */
  function applyLoadedView(view: LoadedData["view"], { animate = false } = {}): void {
    if (view?.zoomMin != null || view?.zoomMax != null) {
      mapAdapter.setZoomBounds(view.zoomMin, view.zoomMax);
    }

    if (animate) {
      // Les bornes de zoom sont posées juste au-dessus, avant le vol : `easeTo` se chargera de
      // rester dedans, alors qu'une vue déjà en place puis bornée aurait été rognée après coup.
      mapAdapter.animateToView({ center: view?.center, zoom: view?.zoom, rotationDeg: view?.rotationDeg });
    } else {
      if (view?.center) mapAdapter.setCenter(view.center);
      if (view?.zoom != null) mapAdapter.setZoom(view.zoom);
      if (view?.rotationDeg != null) mapAdapter.setRotation(view.rotationDeg);
    }

    // Style par défaut lu depuis les données chargées (`configurations.defaultShapeStyle`/
    // `featureStyle` v1 — voir `jsonLoader`) : remplace `TokaMapConfig.defaultShapeTheme` (déjà
    // appliqué à la construction de `mapAdapter` ci-dessus) quand présent, reflétant ce qui a été
    // enregistré en dernier via `setDefaultShapeStyle` — voir son commentaire.
    if (view?.defaultShapeStyle !== undefined) mapAdapter.setDefaultShapeStyle(view.defaultShapeStyle);
    // Thèmes embarqués dans le document (`configurations.themes` en v2 — voir `jsonLoader`) :
    // remplacent `TokaMapConfig.themes`. C'est ce chemin qui permet à un viewer de résoudre les
    // `Shape.themeId` d'une carte publiée sans connaître la source des thèmes.
    if (view?.themes !== undefined) mapAdapter.setThemes(view.themes);

    // Fond de carte lu depuis les données chargées (`configurations.tileLayerName` — voir
    // `jsonLoader`) : remplace `TokaMapConfig.defaultTileLayerName` (déjà appliqué à la construction de
    // `mapAdapter` ci-dessus) quand présent, même mécanisme que `defaultShapeStyle` ci-dessus.
    if (view?.tileLayerName !== undefined) mapAdapter.setTileLayerName(view.tileLayerName);

    // EN BLOC (setter d'adapter, donc sans fusion), même mécanisme que `tileLayerName` ci-dessus.
    if (view?.osmOptions !== undefined) mapAdapter.setOsmOptions(view.osmOptions);

    // Couleur de fond lue depuis les données chargées (`configurations.backgroundColor` — voir
    // `jsonLoader`) : remplace `TokaMapConfig.defaultBackgroundColor` (déjà appliqué à la construction
    // de `mapAdapter` ci-dessus) quand présente, même mécanisme que `tileLayerName` ci-dessus.
    if (view?.backgroundColor !== undefined) mapAdapter.setBackgroundColor(view.backgroundColor);

    // Autorisation d'inclinaison 3D (`configurations.allowPitch` — voir `jsonLoader`) : remplace
    // `TokaMapConfig.defaultAllowPitch` (déjà appliqué à la construction de `mapAdapter` ci-dessus)
    // quand présente, même mécanisme que `backgroundColor` ci-dessus — synchrone comme elle,
    // contrairement à `geolocation` juste en dessous (pas de `watchPosition` à attendre).
    if (view?.allowPitch !== undefined) mapAdapter.setAllowPitch(view.allowPitch);

    mapAdapter.setFraming(view?.framing);

    // Géolocalisation (`configurations.geolocation`/`TokaMapConfig.geolocation`) : PAS appliquée ici,
    // contrairement à `tileLayerName`/`defaultShapeStyle` ci-dessus — voir plus bas, après
    // l'enregistrement de `mapAdapter.on(...)`. `MapLibreAdapter.setGeolocationEnabled` démarre
    // `navigator.geolocation.watchPosition`, dont la réponse (asynchrone mais pas forcément lente) doit
    // pouvoir être captée par `"geolocation:changed"`/`"geolocation:visibility"` — l'appliquer ici, avant
    // que ce listener existe, risquerait de manquer le tout premier évènement (voir le commentaire de
    // `MapLibreAdapter.geolocationEnabled`). `loadDocument` l'applique donc lui aussi de son côté,
    // hors de cette fonction, pour la même raison d'ordre.

    // Lu APRÈS positionnement, pas directement dans `view` : celui-ci peut laisser des champs
    // absents, auquel cas la vue retenue est celle que `MapLibreAdapter` a gardée. Pendant un vol,
    // la caméra est encore au départ : c'est la destination demandée qui fait foi, sans quoi
    // "recentrer" ramènerait à la vue du document précédent.
    initialView = animate
      ? {
          center: view?.center ?? mapAdapter.getCenter(),
          zoom: view?.zoom ?? mapAdapter.getZoom(),
          rotationDeg: view?.rotationDeg ?? mapAdapter.getRotation(),
        }
      : {
          center: mapAdapter.getCenter(),
          zoom: mapAdapter.getZoom(),
          rotationDeg: mapAdapter.getRotation(),
        };
    mapAdapter.setStartZoom(initialView.zoom);
  }

  /**
   * Ce qu'un document qui ne dit rien affiche : les valeurs de construction (`TokaMapConfig.default*`,
   * `themes`…), relevées AVANT que le premier document ne les remplace. `loadDocument` y retombe
   * pour chaque champ absent du document entrant — sans ça, ce champ garderait la valeur du document
   * sortant (ex. un fond OSM sombre qui suit le visiteur sur une carte restée en clair, faute d'avoir
   * jamais enregistré ce réglage).
   */
  const documentlessView: NonNullable<LoadedData["view"]> = {
    zoomMin: mapAdapter.getZoomBounds().min,
    zoomMax: mapAdapter.getZoomBounds().max,
    defaultShapeStyle: mapAdapter.getDefaultShapeStyle(),
    themes: mapAdapter.getThemes(),
    tileLayerName: mapAdapter.getTileLayerName(),
    osmOptions: mapAdapter.getOsmOptions(),
    backgroundColor: mapAdapter.getBackgroundColor(),
    allowPitch: mapAdapter.getAllowPitch(),
  };

  applyLoadedView(view);

  function showFramedStartView({ animate = false } = {}): boolean {
    const camera = mapAdapter.getStartCamera();
    if (!camera || !mapAdapter.showStartView({ animate, withPitch: true })) return false;

    initialView = animate
      ? { center: camera.center, zoom: camera.zoom, rotationDeg: camera.rotationDeg }
      : { center: mapAdapter.getCenter(), zoom: mapAdapter.getZoom(), rotationDeg: mapAdapter.getRotation() };
    return true;
  }

  /**
   * Ramène la carte sur la vue de départ : celle du cadrage quand le document en a un (inclinaison
   * comprise), sinon `initialView` avec le pitch remis à 0. Fonction nommée (plutôt qu'un callback anonyme
   * inline) pour être réutilisable à la fois par `ResetViewButton` et par `startEditingMapView`
   * (qui recentre la vue avant d'entrer dans le mode, voir plus bas), et pour être exposée comme
   * commande console (voir `commands`) — la même action, peu importe qui la déclenche.
   */
  function resetView(): void {
    if (showFramedStartView()) return;

    mapAdapter.setCenter(initialView.center);
    mapAdapter.setZoom(initialView.zoom);
    mapAdapter.setRotation(initialView.rotationDeg);
    mapAdapter.setPitch(0);
  }

  shapeStore.addMany(initialShapes);
  showFramedStartView();

  const history = new History();

  // Passerelle de migration de format (BESOINS.md §8.3) : construite seulement si le document
  // chargé a une version connue ET qu'elle n'est pas la plus récente ET que le consommateur a
  // fourni `onMigrateDocument` — sinon `migrationGate` reste `null` et `gateSave` ci-dessous ne
  // fait rien (comportement historique inchangé).
  const migrationGate =
    config.onMigrateDocument != null && formatVersion != null && formatVersion !== LATEST_FORMAT_VERSION
      ? (() => {
          const migrationDialog = new MigrationDialog(container);
          return new FormatMigrationGate(false, async () => {
            const accepted = await migrationDialog.confirm();
            if (!accepted) return false;
            await config.onMigrateDocument!(migrateDocumentToLatest(rawLoadedDocument));
            // Le document persisté par `onMigrateDocument` est désormais à `LATEST_FORMAT_VERSION` —
            // refléter ça ici pour que `getConfigurationState()`/`onSaveConfiguration` ne rapportent
            // plus l'ancienne version une fois la migration acceptée.
            formatVersion = LATEST_FORMAT_VERSION;
            return true;
          });
        })()
      : null;

  /**
   * Enveloppe `onSave`/`onSaveConfiguration` pour y intercaler la passerelle de migration — un
   * rejet ici (refus de l'utilisateur) déclenche le rollback déjà existant de
   * `ShapeSaveTracker`/`ConfigurationSaveTracker` (voir leur gestion d'un `onSave` rejeté,
   * BESOINS.md §7.1) sans qu'aucune des deux classes n'ait besoin de connaître la migration.
   */
  function gateSave<E>(save: ((event: E) => Promise<void>) | undefined): ((event: E) => Promise<void>) | undefined {
    if (!save || !migrationGate) return save;
    return async (event: E) => {
      const migrated = await migrationGate.ensureMigrated();
      if (!migrated) throw new Error("Migration du format refusée par l'utilisateur — modification annulée.");
      return save(event);
    };
  }

  // Sauvegarde optimiste avec rollback par forme (BESOINS.md §7.1) — logique extraite dans
  // `ShapeSaveTracker` (testable indépendamment de MapLibre/DOM, voir `ShapeSaveTracker.test.ts`).
  const shapeSaveTracker = new ShapeSaveTracker(shapeStore, history, gateSave(config.onSave));
  const runShapeCommand = shapeSaveTracker.run.bind(shapeSaveTracker);
  const runShapeBatchCommand = shapeSaveTracker.runBatch.bind(shapeSaveTracker);

  // Même principe que `shapeSaveTracker`, pour la vue de caméra par défaut (une seule entité
  // globale, pas une par forme) — voir `ConfigurationSaveTracker`.
  const configurationSaveTracker = new ConfigurationSaveTracker(
    mapAdapter,
    history,
    gateSave(config.onSaveConfiguration),
  );

  history.onChange((event) => {
    save();
    // Annuler/Rétablir (toolbar, ou `history.undo()`/`redo()` appelé directement) appellent aussi
    // `onSave`/`onSaveConfiguration`, avec le même suivi/rollback qu'une modification fraîche — pas
    // seulement les créations/éditions passées par `runShapeCommand`/`configurationSaveTracker.run`
    // ci-dessus (voir `ShapeSaveTracker.runHistoryChange`/`ConfigurationSaveTracker.runHistoryChange`,
    // BESOINS.md §7.1). "execute" est déjà géré par ces `run()` eux-mêmes ; "discard" est un retrait
    // interne du suivi, pas un geste utilisateur — les deux sont ignorés ici. Sans effet si
    // `event.command` n'est pas suivi par le tracker concerné (ex. une `Command` de forme n'a pas de
    // métadonnées dans `configurationSaveTracker`, et réciproquement).
    if (event.kind === "undo" || event.kind === "redo") {
      shapeSaveTracker.runHistoryChange(event.command, event.kind);
      configurationSaveTracker.runHistoryChange(event.command, event.kind);
    }
  });

  // Une forme dont le `onSave` échoue revient à sa dernière position confirmée (rollback ci-dessus),
  // mais rien ne la sort automatiquement d'une sélection multiple en cours (BESOINS.md §13) : sans
  // ça, elle resterait affichée en surbrillance "selected" alors qu'elle n'a pas suivi le reste du
  // groupe. `removeFromSelection` est un no-op si la forme n'est pas dans la sélection courante.
  shapeSaveTracker.onStatusChange((event) => {
    if (event.status === "error") mapAdapter.removeFromSelection(event.shapeId);
  });

  const contextMenu = editMode ? new ContextMenu(container) : null;

  // Fiche entité (voir `TokaMapConfig.entityPanel`) : à l'inverse de `contextMenu`/`FpsMeter`
  // ci-dessus (réservés à `editMode`), ce widget est réservé au mode visualisation — inutile de
  // construire l'un ou l'autre en dehors de son mode.
  const entityPanel =
    !editMode && config.entityPanel
      ? new EntityPanel(container, (insets) => searchBox?.setLeftShift(insets?.left ?? 0))
      : null;
  const entityPanelConfig = config.entityPanel || null;

  // Fenêtre superposée pour `Shape.urlAction.openMode === "overlay"` (voir `ui/UrlOverlay`) — même
  // garde que `entityPanel` (réservée à la visualisation), mais sans config consommateur : `url`
  // vient directement de `Shape.urlAction`, un champ interprété par `@tokamap/core` lui-même
  // (contrairement à `entity`, opaque), donc rien à fournir en plus d'`editMode`.
  const urlOverlay = !editMode ? new UrlOverlay(container) : null;

  // Confirmation avant redirection (voir `ShapeUrlAction.confirmBeforeOpen`, `ui/ConfirmDialog`) —
  // même garde qu'`urlOverlay` ci-dessus.
  const confirmDialog = !editMode ? new ConfirmDialog(container) : null;

  // Presse-papier (Copier/Coller) — un seul contenu, remplacé à chaque `copyShapes` (pas un
  // historique de plusieurs copies). Celui du consommateur s'il en fournit un (voir
  // `TokaMapConfig.clipboard`), sinon un presse-papier propre à l'instance. Voir
  // `TokaMapInstance.copyShapes`/`pasteClipboard`.
  const clipboard: ShapeClipboard = config.clipboard ?? (() => {
    let content: ShapeClipboardContent | null = null;
    return { origin: null, read: () => content, write: (next) => { content = next; } };
  })();
  const clipboardIsEmpty = () => !clipboard.read()?.shapes.length;

  // Presse-papier de STYLE ("Copier le style"/"Coller le style") — délibérément séparé du
  // presse-papier de formes ci-dessus : copier une forme entière et copier juste son apparence
  // sont 2 gestes différents (voir `TokaMapInstance.copyShapeStyle`/`pasteShapeStyle`). Copie TOUT
  // ce qui touche à l'apparence (`style`/`hoverStyle`/`image`/`text`/`hoverText`), à l'exclusion de
  // 3 attributs de CONTENU/COMPORTEMENT explicitement exclus : `ShapeImage.src` (l'URL de l'image),
  // `ShapeText.text`/`hoverText.text` (le texte lui-même), et tout ce qui relève de l'action au clic
  // (`action`/`useActionIcon`/`urlAction`, jamais touchés ici). `image`/`text`/`hoverText` sont
  // `null` si la forme source n'en a pas — voir `cloneImageStyle`/`cloneTextStyle`, aucune
  // apparence à copier dans ce cas.
  let styleClipboard: {
    style: ShapeStyle;
    hoverStyle: ShapeStyle;
    image: Partial<ShapeImage> | null;
    text: Partial<ShapeText> | null;
    hoverText: Partial<ShapeText> | null;
  } | null = null;

  /** Copie EXPLICITE des 4 clés de `ShapeStyle`, y compris celles absentes (valeur `undefined`) —
   * c'est ce qui permet à `pasteShapeStyle` de se comporter comme un vrai remplacement plutôt
   * qu'une fusion partielle une fois repassé par `setShapeStyle`/`setShapeHoverStyle` (`{
   * ...shape.style, ...style }` : une clé PRÉSENTE avec la valeur `undefined` écrase bel et bien
   * la valeur courante, contrairement à une clé absente). Voir `copyShapeStyle`. */
  function cloneShapeStyle(style: ShapeStyle | undefined): ShapeStyle {
    return {
      bgColor: style?.bgColor,
      borderColor: style?.borderColor,
      borderWidth: style?.borderWidth,
      borderDashed: style?.borderDashed,
    };
  }

  /** Copie EXPLICITE de `ShapeImage` SAUF `src` (l'URL — le seul attribut de contenu exclu, voir
   * `styleClipboard` ci-dessus) — `width`/`height`/`svg` sont copiés comme le reste : attention,
   * si l'image de la forme cible n'a pas les mêmes dimensions RÉELLES que celle de la source,
   * `width`/`height` copiés peuvent devenir un indice de taille FAUX pour elle (voir
   * `ShapeImage.width`/`height`, un simple indice de perf, jamais revérifié contre l'image réelle
   * une fois jugé plausible) — accepté ici puisque `src` reste, lui, celui de la forme cible,
   * inchangé. `Shape.shapeKind` (qui distingue une forme-image, anciennement `ShapeImage.isDirect`
   * — voir son commentaire dans `types.ts`) n'est PAS copié : c'est un discriminant structurel de
   * la forme, comme `coordinates`, pas une apparence. `null` si la forme source n'a pas d'image :
   * rien à en tirer (voir `copyShapeStyle`/`pasteShapeStyle`, qui laisse alors l'image de la forme
   * cible intacte plutôt que d'en fabriquer une sans `src`). */
  function cloneImageStyle(image: ShapeImage | undefined): Partial<ShapeImage> | null {
    if (!image) return null;
    return {
      width: image.width,
      height: image.height,
      scale: image.scale,
      opacity: image.opacity,
      rotation: image.rotation,
      rotateWithMap: image.rotateWithMap,
      horizontalAlign: image.horizontalAlign,
      verticalAlign: image.verticalAlign,
      minScreenPx: image.minScreenPx,
      svg: image.svg,
    };
  }

  /** Copie EXPLICITE de `ShapeText` SAUF `text` (le contenu — seul attribut exclu, voir
   * `styleClipboard` ci-dessus). Réutilisée pour `Shape.text` ET `Shape.hoverText` (même type). `null`
   * si la forme source n'a pas ce texte. */
  function cloneTextStyle(text: ShapeText | undefined): Partial<ShapeText> | null {
    if (!text) return null;
    return {
      size: text.size,
      fixedSizePx: text.fixedSizePx,
      color: text.color,
      bgColor: text.bgColor,
      horizontalAlign: text.horizontalAlign,
      verticalAlign: text.verticalAlign,
    };
  }

  /** Copie profonde suffisante pour qu'éditer la forme collée ne puisse jamais muter l'originale
   * (ou l'inverse) — voir `copyShapes`/`pasteClipboard`. `entity` reste partagé tel quel : charge
   * opaque jamais mutée en place par `@tokamap/core` (voir `Shape.entity`). */
  function cloneShapeForClipboard(shape: Shape): Shape {
    return {
      ...shape,
      coordinates: shape.coordinates.map(([x, y]) => [x, y] as LngLat),
      style: shape.style ? { ...shape.style } : shape.style,
      hoverStyle: shape.hoverStyle ? { ...shape.hoverStyle } : shape.hoverStyle,
      text: shape.text ? { ...shape.text } : shape.text,
      hoverText: shape.hoverText ? { ...shape.hoverText } : shape.hoverText,
      image: shape.image ? { ...shape.image } : shape.image,
    };
  }

  /** Décalage (lng/lat) appliqué à une forme collée par rapport à son originale — une fraction de
   * sa propre taille (pas une constante en degrés, qui serait tantôt invisible tantôt énorme selon
   * l'échelle réelle des données chargées), pour rester visible/distincte sans dépendre du zoom ou
   * de la position de la caméra. `PASTE_OFFSET_FALLBACK` couvre le cas dégénéré d'une forme dont
   * tous les points sont confondus (taille nulle). */
  const PASTE_OFFSET_RATIO = 0.15;
  const PASTE_OFFSET_FALLBACK = 0.0001;
  function computePasteOffset(coordinates: LngLat[]): LngLat {
    const xs = coordinates.map(([x]) => x);
    const ys = coordinates.map(([, y]) => y);
    const width = Math.max(...xs) - Math.min(...xs);
    const height = Math.max(...ys) - Math.min(...ys);
    const offset = Math.max(width, height) * PASTE_OFFSET_RATIO || PASTE_OFFSET_FALLBACK;
    return [offset, -offset];
  }

  // Les widgets ci-dessous (`drawOverlay`, `ResetViewButton`, `FpsMeter`) sont tous positionnés
  // `absolute` (voir style.css/ResetViewButton.ts) : sans ancêtre positionné, ils se
  // positionneraient par rapport à la page entière plutôt que d'être cantonnés à `container` — ex.
  // chevauchant une éventuelle barre d'outils au-dessus de `container` (`TopBar`, côté exemple).
  // `container` appartient au consommateur (n'importe quel élément qu'il choisit de passer à
  // `createTokaMap`), donc géré plus haut (avant la boucle de chargement, voir `LoadErrorView` un
  // peu plus haut) plutôt que de compter sur son propre CSS pour y penser.

  // Indépendant d'`editMode` : le dessin lui-même (`startDrawingPolygon`/`startDrawingRectangle`)
  // n'est pas non plus gardé par `editMode` (voir `DrawAdapter`) — rien n'empêche un consommateur
  // de le déclencher en mode visualisation seule, ces widgets doivent donc pouvoir s'afficher dans
  // ce cas aussi. `drawOverlay` regroupe visuellement `DrawTutorial` (un seul widget partagé par
  // les deux gestes) et `CancelDrawingButton` — deux widgets indépendants l'un de l'autre (le
  // bouton Annuler reste affiché même quand le tutoriel est désactivé via `skipTutorial`), qui
  // n'ont pas vocation à être placés l'un sans l'autre pour autant.
  const drawOverlay = document.createElement("div");
  drawOverlay.className = "toka-draw-overlay";
  container.appendChild(drawOverlay);
  const drawTutorial = new DrawTutorial(drawOverlay);
  const cancelDrawingButton = new CancelDrawingButton(drawOverlay, () => mapAdapter.cancelDrawing());

  // Édition interactive de la vue par défaut (centre/zoom/rotation, voir
  // `MapLibreAdapter.startEditingMapView`) : croix au centre de l'écran + tutoriel/boutons
  // Enregistrer-Annuler regroupés en bas à gauche, dans leur propre conteneur (indépendant de
  // `drawOverlay`, en haut) — réutilise le même widget `DrawTutorial` que le dessin (voir
  // `ui/DrawTutorial`), juste avec un autre message.
  const mapViewCrosshair = new MapViewCrosshair(container);
  const mapViewFrame = new MapViewFrame(container);
  const mapViewEditOverlay = document.createElement("div");
  mapViewEditOverlay.className = "toka-map-view-edit-overlay";
  container.appendChild(mapViewEditOverlay);
  const mapViewEditTutorial = new DrawTutorial(mapViewEditOverlay);
  const mapViewEditControls = new MapViewEditControls(mapViewEditOverlay);
  // Champ de recherche d'adresse (Nominatim/OpenStreetMap, voir `ui/AddressSearchBox`) — permet de
  // déplacer la carte à une adresse tapée en clair pendant ce mode. Recentre ET zoome sur le point
  // trouvé (`ADDRESS_SEARCH_ZOOM`) plutôt que de se contenter de recentrer au zoom courant — sinon
  // une adresse validée depuis une vue très dézoomée resterait illisible. Élément à part, en haut de
  // l'écran (pas dans `mapViewEditOverlay`, ancré en bas à gauche) — voir
  // `.toka-map-view-address-search`.
  const mapViewAddressSearch = new AddressSearchBox(container, (result) => {
    mapAdapter.setCenter(result.center);
    mapAdapter.setZoom(ADDRESS_SEARCH_ZOOM);
  });

  const STACK_METRICS = { baseEdgeOffsetPx: 10, gapPx: 4 };
  const ROUTE_ACTION_ID = "route";
  const SEARCH_BOX_HEIGHT_PX = 37;
  let searchBox: SearchBox | null = null;
  const searchBoxHeightPx = () => (searchBox ? searchBox.heightPx() : SEARCH_BOX_HEIGHT_PX);
  const BUTTON_HEIGHT_PX = 36;

  const searchPosition: SearchBoxPosition | null = config.search ? config.search.position ?? "top-left" : null;
  const zoomControlPosition: ZoomControlPosition | null =
    config.zoomControl !== false ? config.zoomControl?.position ?? "bottom-right" : null;
  const resetViewButtonPosition: ResetViewButtonPosition | null =
    config.resetViewButton !== false ? config.resetViewButton?.position ?? "bottom-right" : null;
  const geolocationButtonPosition: GeolocationButtonPosition | null =
    config.geolocationButton !== false ? config.geolocationButton?.position ?? "bottom-right" : null;

  // Entre dans le calcul de la pile, qui doit se resserrer quand ce bouton s'efface.
  let geolocationButtonVisible = false;

  /**
   * Pile d'un coin, du bord vers l'intérieur. `resetViewButton` est délibérément le dernier : c'est
   * celui dont la visibilité change le plus souvent (à chaque déplacement de carte), et rien
   * au-dessus de lui n'a alors à être redéplacé.
   */
  function stackBelow(): StackedWidget[] {
    return [
      { position: searchPosition, heightPx: searchBoxHeightPx(), visible: true },
      // Détaché du reste : les coller donnerait une colonne de quatre boutons indistincts.
      { position: zoomControlPosition, heightPx: ZOOM_CONTROL_HEIGHT_PX, visible: true, gapAfterPx: 12 },
      { position: geolocationButtonPosition, heightPx: BUTTON_HEIGHT_PX, visible: geolocationButtonVisible },
    ];
  }

  /** Recalcule la pile en plus de basculer : `resetViewButton`, juste au-dessus, doit reprendre la
   * place laissée libre. */
  function setGeolocationButtonVisible(visible: boolean): void {
    if (geolocationButtonVisible === visible) return;
    geolocationButtonVisible = visible;
    geolocationButton?.setVisible(visible);
    restackFloatingButtons();
  }

  /** Réapplique les décalages des deux boutons mobiles. */
  function restackFloatingButtons(): void {
    const below = stackBelow();
    if (geolocationButtonPosition) {
      geolocationButton?.setStackOffset(stackOffsetPx(geolocationButtonPosition, below.slice(0, 2), STACK_METRICS));
    }
    if (resetViewButtonPosition) {
      resetViewButton?.setStackOffset(stackOffsetPx(resetViewButtonPosition, below, STACK_METRICS));
    }
  }

  // Indépendant d'`editMode` : utile aussi en mode visualisation seule (un consommateur embarqué
  // peut vouloir laisser un visiteur revenir à la vue d'origine après avoir déplacé la carte).
  const resetViewButton =
    config.resetViewButton !== false ? new ResetViewButton(container, { ...config.resetViewButton }, resetView) : null;

  // Tolérance (pas une égalité stricte) : `initialView.center` a pu transiter par un aller-retour
  // getCenter/setCenter (`resetView`) ou une reprojection, sans garantie de bit-à-bit identique —
  // une vraie divergence (glisser/zoomer/pivoter la carte) dépasse ce seuil de plusieurs ordres de
  // grandeur, donc aucun risque de masquer le bouton après un déplacement réel.
  const VIEW_EPSILON = 1e-9;
  function isAtInitialView(view: MapViewState): boolean {
    return (
      Math.abs(view.center[0] - initialView.center[0]) < VIEW_EPSILON &&
      Math.abs(view.center[1] - initialView.center[1]) < VIEW_EPSILON &&
      Math.abs(view.zoom - initialView.zoom) < VIEW_EPSILON &&
      Math.abs(view.rotationDeg - initialView.rotationDeg) < VIEW_EPSILON
    );
  }

  // Voir BESOINS.md §15 : caché par défaut (voir `GeolocationButton`), affiché seulement quand la
  // géolocalisation est active ET que la position courante n'est plus visible à l'écran — piloté par
  // "geolocation:changed"/"geolocation:visibility" ci-dessous, jamais par une logique propre ici.
  const geolocationButton =
    config.geolocationButton !== false
      ? new GeolocationButton(container, { ...config.geolocationButton }, () => mapAdapter.centerOnUserLocation())
      : null;

  // Seul widget de la pile présent en édition comme en visualisation : zoomer est un geste de carte,
  // pas un geste de visiteur.
  const zoomControl =
    config.zoomControl !== false
      ? new ZoomControl(container, { ...config.zoomControl }, (delta) => mapAdapter.zoomBy(delta))
      : null;

  const documentSwitcherConfig = config.documentSwitcher;
  const documentSwitcherPosition: DocumentSwitcherPosition | null =
    !editMode && documentSwitcherConfig ? documentSwitcherConfig.position ?? "top-right" : null;
  const documentSwitcher =
    !editMode && documentSwitcherConfig
      ? new DocumentSwitcher(
          container,
          {
            getState: () => ({
              documents: documentSwitcherConfig.getDocuments(),
              currentDocumentId: documentSwitcherConfig.getCurrentDocumentId(),
            }),
            position: documentSwitcherConfig.position,
            style: documentSwitcherConfig.style,
            label: documentSwitcherConfig.label,
          },
          documentSwitcherConfig.onSelect,
          () => restackDocumentSwitcher(),
        )
      : null;

  function refreshDocumentSwitcher(): void {
    documentSwitcher?.refresh();
    restackDocumentSwitcher();
  }

  /** Sous la recherche quand ils partagent un coin, ou quand la largeur manque pour les mettre côte à
   * côte sur le même bord. */
  function restackDocumentSwitcher(): void {
    if (!documentSwitcher || !documentSwitcherPosition) return;
    const [switcherEdge, switcherSide] = documentSwitcherPosition.split("-");
    const [searchEdge, searchSide] = searchPosition?.split("-") ?? [];
    const requiredWidthPx =
      2 * STACK_METRICS.baseEdgeOffsetPx + (searchBox?.widthPx() ?? 0) + STACK_METRICS.gapPx + documentSwitcher.widthPx();
    const belowSearch =
      !!searchBox &&
      searchBox.heightPx() > 0 &&
      switcherEdge === searchEdge &&
      (switcherSide === searchSide || container.clientWidth < requiredWidthPx);
    documentSwitcher.setStackOffset(
      belowSearch ? STACK_METRICS.baseEdgeOffsetPx + searchBoxHeightPx() + STACK_METRICS.gapPx : STACK_METRICS.baseEdgeOffsetPx,
    );
  }

  restackFloatingButtons();
  zoomControl?.setZoomState(mapAdapter.getZoom(), mapAdapter.getZoomBounds());

  // `FpsMeter` se positionne en CSS et non en style inline : il ne passe pas par `stackOffsetPx`,
  // et c'est le seul endroit qui sache si le zoom lui dispute ce coin.
  if (zoomControlPosition === "bottom-right") {
    const FPS_METER_GAP_PX = 12;
    container.style.setProperty(
      "--toka-fps-meter-right",
      `${STACK_METRICS.baseEdgeOffsetPx + ZOOM_CONTROL_WIDTH_PX + FPS_METER_GAP_PX}px`,
    );
  }

  /**
   * Regroupe les formes par `SearchItem.key` (défaut : `shape.id`, donc pas de fusion) à partir de
   * `TokaMapConfig.search.getSearchItem` — un `Map` conserve l'ordre d'insertion, donc l'ordre des
   * résultats reste celui du `ShapeStore` pour un `key` donné à sa première rencontre (voir
   * `SearchItem.key`). Jamais mise en cache (appelée à chaque frappe) pour rester cohérente avec
   * l'état courant du `ShapeStore`, y compris après une forme dessinée/éditée/undo-redo depuis —
   * `ui/SearchBox` lui-même reste agnostique de `Shape`/`ShapeStore` (frontière core/adapter, voir
   * CLAUDE.md), d'où cette conversion ici plutôt que dans le widget. Réutilisée par `TokaMapConfig
   * .routePanel` (voir plus bas) : les 2 fonctionnalités partagent la même notion de "forme liée à
   * une entité", pas la peine de la faire fournir 2 fois par le consommateur.
   */
  function buildSearchEntries(searchConfig: SearchBoxConfig): SearchBoxEntry[] {
    const entriesByKey = new Map<string, SearchBoxEntry>();
    for (const shape of shapeStore.getAll()) {
      const item = searchConfig.getSearchItem(shape);
      if (!item) continue;
      const key = item.key ?? shape.id;
      const existing = entriesByKey.get(key);
      if (existing) {
        existing.shapeIds.push(shape.id);
        continue;
      }
      entriesByKey.set(key, {
        shapeIds: [shape.id],
        title: item.title,
        subTitle: item.subTitle,
        image: item.image,
        searchText: item.searchText,
        categoryId: item.categoryId,
      });
    }
    return [...entriesByKey.values()];
  }

  /**
   * Pendant de `buildSearchEntries` pour les résultats hors document affiché (voir
   * `SearchBoxConfig.getExternalItems`) — aucune déduplication ici, contrairement aux formes : ces
   * entrées arrivent déjà telles que le consommateur veut les voir listées.
   */
  function buildExternalSearchEntries(getExternalItems: (() => SearchExternalItem[]) | undefined): SearchBoxEntry[] {
    return (getExternalItems?.() ?? []).map((item) => ({
      shapeIds: [],
      title: item.title,
      subTitle: item.subTitle,
      image: item.image,
      searchText: item.searchText,
      categoryId: item.categoryId,
      external: { payload: item.payload },
    }));
  }

  // Voir `"map-view:edit-started"`/`"map-view:edit-ended"` plus bas : masquée (pas détruite) pendant
  // l'édition de la vue par défaut, où elle n'a pas sa place — `AddressSearchBox`, un widget
  // entièrement différent, la remplace pour ce mode-là (voir `mapViewAddressSearch`).
  /** Le panneau s'ouvre à la place de la recherche, qui occupe le même coin. Masquer la recherche
   * entière, et pas seulement son bouton, évite qu'un champ inerte reste sous le panneau. */
  function setRouteEntryVisible(visible: boolean): void {
    if (visible) searchBox?.show();
    else searchBox?.hide();
    searchBox?.setActionVisible(ROUTE_ACTION_ID, visible);
  }

  if (config.search) {
    const searchConfig = config.search;
    // Posé DANS la recherche plutôt qu'en bouton flottant à part : chercher où aller et demander
    // l'itinéraire sont le même geste. Réservé à la visualisation.
    const routeEnabled = !!config.routePanel && !editMode;
    const searchBoxOptions: SearchBoxOptions = {
      getEntries: () => [...buildSearchEntries(searchConfig), ...buildExternalSearchEntries(searchConfig.getExternalItems)],
      position: searchConfig.position,
      style: searchConfig.style,
      placeholder: searchConfig.placeholder,
      getCategories: searchConfig.getCategories,
      onLayoutChange: () => {
        restackFloatingButtons();
        restackDocumentSwitcher();
      },
      actions: routeEnabled
        ? [
            {
              id: ROUTE_ACTION_ID,
              label: config.routePanel !== false ? config.routePanel?.buttonLabel ?? "Itinéraire" : "Itinéraire",
              icon: () => createIcon("route"),
              onClick: () => startRouteMode(),
              variant: "accent",
            },
          ]
        : [],
    };
    searchBox = new SearchBox(
      container,
      searchBoxOptions,
      (entry) => {
        // Rien à surligner ni à recentrer : la destination n'est pas dans ce document, seul le
        // consommateur sait comment l'atteindre.
        if (entry.external) {
          searchConfig.onSelectExternalItem?.(entry.external.payload);
          return;
        }

        mapAdapter.highlightShapes(entry.shapeIds, {
          zoomToFit: searchConfig.zoomToFit ?? true,
          // Revient à la rotation par défaut de la vue (même valeur que `resetView`/`ResetViewButton`)
          // plutôt que de laisser `fitBounds` la réinitialiser silencieusement à 0 — voir
          // `MapLibreAdapter.zoomToFitShapes`.
          rotationDeg: initialView.rotationDeg,
        });
        searchConfig.onSearch?.(entry.shapeIds, { title: entry.title, subTitle: entry.subTitle, image: entry.image });
      },
      () => mapAdapter.highlightShapes([]),
    );

    if (searchConfig.getCategories) {
      const categorizedSearchBox = searchBox;
      let refreshScheduled = false;
      shapeStore.onChange(() => {
        if (refreshScheduled) return;
        refreshScheduled = true;
        requestAnimationFrame(() => {
          refreshScheduled = false;
          categorizedSearchBox.refreshCategories();
        });
      });
      categorizedSearchBox.refreshCategories();
    }

    restackFloatingButtons();
    restackDocumentSwitcher();
  }

  // Voir `TokaMapConfig.routePanel`/CLAUDE.md "Itinéraire / pathfinding" — nécessite `config.search`
  // (réutilisé pour étiqueter les formes sélectionnables, voir `buildSearchEntries`), sans quoi le
  // panneau n'aurait aucune notion de "forme liée à une entité" à proposer.
  let routePanel: RoutePanel | null = null;
  if (config.routePanel) {
    if (!config.search) {
      console.warn(
        "[TokaMap] `TokaMapConfig.routePanel` nécessite `TokaMapConfig.search` (réutilisé pour étiqueter les formes) — mode itinéraire désactivé.",
      );
    } else {
      const searchConfig = config.search;
      const routeConfig = config.routePanel;

      if (routeConfig.getExternalItems && !routeConfig.onValidate) {
        console.warn(
          "[TokaMap] `RoutePanelConfig.getExternalItems` nécessite `onValidate` (ces destinations n'ont aucune forme chargée à router) — elles resteront sans effet.",
        );
      }

      const routePanelOptions: RoutePanelOptions = {
        getEntries: () => [
          ...buildSearchEntries(searchConfig),
          ...buildExternalSearchEntries(routeConfig.getExternalItems),
        ],
        // Conditionné à la géolocalisation ACTIVÉE, pas à un point déjà reçu : attendre le premier
        // relevé ferait apparaître la ligne en cours de frappe, sous les yeux du visiteur, et la
        // rendrait indisponible tant que l'autorisation du navigateur n'a pas été accordée — sans
        // que rien ne le dise. La position est résolue au moment de valider, où son absence donne un
        // message (voir `selectionToEndpoint`/`handleRouteValidate`).
        hasUserLocation: () => mapAdapter.getGeolocationEnabled(),
        userLocationLabel: routeConfig.userLocationLabel,
        position: routeConfig.position ?? searchPosition ?? "top-right",
        style: routeConfig.style,
        fromPlaceholder: routeConfig.fromPlaceholder,
        toPlaceholder: routeConfig.toPlaceholder,
        validateLabel: routeConfig.validateLabel,
      };
      routePanel = new RoutePanel(
        container,
        routePanelOptions,
        (from, to) => handleRouteValidate(from, to),
        () => cancelRouteMode(),
      );
    }
  }

  /** Convertit une sélection du panneau (voir `RoutePanelSelection`) en extrémité pour
   * `findRoute` (voir `RouteEndpoint`) — `null` si la position utilisateur a été choisie mais n'est
   * plus connue au moment de valider (ex. géolocalisation désactivée entre-temps). */
  async function selectionToEndpoint(selection: RoutePanelSelection): Promise<RouteEndpoint | null> {
    if (selection.type === "user-location") {
      // Attendue plutôt que lue : un premier relevé peut arriver longtemps après l'activation du
      // repérage (voir `MapAdapter.waitForUserLocation`), et l'utilisateur vient justement de
      // demander un itinéraire depuis là où il est.
      const position = await mapAdapter.waitForUserLocation();
      return position ? { type: "position", position } : null;
    }
    // Hors document affiché : aucune forme chargée, donc aucune extrémité que `findRoute` puisse
    // résoudre — seul `RoutePanelConfig.onValidate` sait quoi en faire (voir `handleRouteValidate`).
    if (selection.type === "external") return null;
    return { type: "shape", shapeId: selection.shapeId };
  }

  /** Bouton "Valider" du panneau : calcule l'itinéraire (`core/pathfinding.ts`) et l'affiche (voir
   * `MapAdapter.showRoute`) — le panneau reste ouvert dans tous les cas (succès ou échec), voir
   * `RoutePanel.setError`, pour permettre de changer un champ et revalider sans tout rouvrir. */
  async function handleRouteValidate(from: RoutePanelSelection, to: RoutePanelSelection): Promise<void> {
    if (!routePanel) return;

    // Calcul délégué : dès qu'une extrémité vit hors du document affiché, `findRoute` ne peut rien
    // en faire. Le consommateur reprend la main pour les DEUX cas (pas seulement celui-là) — sans
    // quoi le même itinéraire se calculerait ici ou là selon sa destination, avec deux rendus à
    // tenir cohérents.
    if (config.routePanel && config.routePanel.onValidate) {
      config.routePanel.onValidate(from, to);
      // Refermé, contrairement au calcul interne qui laisse le panneau ouvert sur son résultat ou
      // son erreur : ici c'est le consommateur qui affiche la suite, et le formulaire n'a plus rien
      // à montrer. Le laisser ouvert masquerait une partie de son rendu — et ne tiendrait de toute
      // façon pas, puisque changer de document le referme.
      routePanel.hide();
      setRouteEntryVisible(true);
      return;
    }

    const [fromEndpoint, toEndpoint] = await Promise.all([selectionToEndpoint(from), selectionToEndpoint(to)]);
    if (!fromEndpoint || !toEndpoint) {
      routePanel.setError("Position utilisateur inconnue.");
      return;
    }
    const result: RouteResult | null = findRoute({ shapes: shapeStore.getAll(), from: fromEndpoint, to: toEndpoint });
    if (!result) {
      mapAdapter.clearRoute();
      mapAdapter.highlightShapes([]);
      routePanel.setError("Aucun itinéraire trouvé.");
      return;
    }
    mapAdapter.showRoute(result.coordinates);
    mapAdapter.highlightShapes(result.shapeIds, { zoomToFit: true });
  }

  /**
   * Voir `TokaMapInstance.startRouteMode` — interrompt un dessin/une édition de vue en cours (même
   * exclusivité que `startEditingMapView` avec le dessin), efface un éventuel tracé/surlignage
   * précédent, puis affiche le panneau. Sans effet si `TokaMapConfig.routePanel` n'est pas configuré
   * (ou mal configuré, voir l'avertissement console ci-dessus).
   */
  function startRouteMode(options: StartRouteModeOptions = {}): void {
    if (!routePanel) return;
    cancelMapViewEdit();
    mapAdapter.cancelDrawing();
    mapAdapter.clearRoute();
    mapAdapter.highlightShapes([]);
    setRouteEntryVisible(false);
    config.routePanel && config.routePanel.onStart?.();
    routePanel.show({
      from: options.prefillFromShapeId ? { type: "shape", shapeId: options.prefillFromShapeId } : undefined,
      to: options.prefillToShapeId ? { shapeId: options.prefillToShapeId } : undefined,
    });
    if (options.autoValidate) {
      const { from, to } = routePanel.getSelections();
      if (from && to) handleRouteValidate(from, to);
    }
  }

  /** Voir `TokaMapInstance.cancelRouteMode` — masque le panneau et efface le tracé/surlignage
   * affichés depuis `startRouteMode`/`handleRouteValidate`. Sans effet si le mode n'est pas actif. */
  function cancelRouteMode(): void {
    // Le bouton revient même si le panneau n'était plus visible : c'est le seul moyen de rouvrir le
    // mode, il ne doit pas pouvoir rester masqué (ex. après un changement de document, qui masque
    // le panneau sans passer par ici).
    setRouteEntryVisible(true);
    if (!routePanel?.isVisible()) return;
    routePanel.hide();
    mapAdapter.clearRoute();
    mapAdapter.highlightShapes([]);
  }

  /**
   * Voir `TokaMapInstance.debugPathfindingGrid` — outil de diagnostic ponctuel (voir
   * `core/pathfinding.ts`'s `findRouteDebugGrid`), sans rapport avec `startRouteMode`/le calcul
   * d'itinéraire réel affiché à l'utilisateur. Affiche la grille effectivement explorée par
   * `gridRoute` pour cette paire de formes (cellules bloquées en rouge, à prix réduit — voir
   * `Shape.pathPriority` — en jaune) ET le tracé retenu par-dessus (`showRoute`, même rendu que
   * `startRouteMode`) : voir les deux ensemble permet de comprendre POURQUOI le tracé a — ou n'a
   * pas — profité d'une cellule à prix réduit visible, sans avoir à deviner. Ne fait rien (console
   * avertie) si aucune grille n'a été construite pour ce trajet (ligne droite directe déjà
   * suffisante, ou forme inconnue).
   */
  function debugPathfindingGrid(fromShapeId: string, toShapeId: string): void {
    const from: RouteEndpoint = { type: "shape", shapeId: fromShapeId };
    const to: RouteEndpoint = { type: "shape", shapeId: toShapeId };
    const shapes = shapeStore.getAll();
    const grid = findRouteDebugGrid({ shapes, from, to });
    if (!grid) {
      console.warn("[TokaMap] debugPathfindingGrid : aucune grille construite pour ce trajet (ligne droite directe, ou forme inconnue).");
      mapAdapter.clearPathfindingDebugGrid();
      return;
    }
    mapAdapter.showPathfindingDebugGrid(grid);
    const route = findRoute({ shapes, from, to });
    if (route) mapAdapter.showRoute(route.coordinates);
  }

  // Bascule "Grille de pathfinding" du popup réglages de `FpsMeter` (voir `FpsMeterOptions
  // .onToggleDebugGrid`) : contrairement à `debugPathfindingGrid` ci-dessus, sans notion de départ/
  // arrivée (`computeAreaDebugGrid`, pas `findRouteDebugGrid`) — pensée pour explorer toute la zone
  // visible plutôt qu'un trajet précis. Restauré depuis `DEBUG_GRID_STORAGE_KEY` INDÉPENDAMMENT
  // d'`editMode` (voir plus bas, où l'affichage lui-même n'est pas gardé par `editMode` non plus) :
  // la préférence est partagée par toute instance `createTokaMap`, y compris une instance en
  // lecture seule (l'Aperçu, `PreviewModal.tsx`) — seul le bouton/la modale de `FpsMeter` restent
  // réservés à `editMode`.
  let debugGridEnabled = localStorage.getItem(DEBUG_GRID_STORAGE_KEY) === "true";

  function refreshDebugGrid(): void {
    const grid = computeAreaDebugGrid(shapeStore.getAll(), mapAdapter.getViewportBounds());
    if (grid) mapAdapter.showPathfindingDebugGrid(grid);
    else mapAdapter.clearPathfindingDebugGrid();
  }

  mapAdapter.on(async (event) => {
    switch (event.type) {
      case "shape:click": {
        onShapeClick(event.shapeId);
        const clickedShape = shapeStore.getAll().find((s) => s.id === event.shapeId);

        // Redirection au clic (voir `Shape.urlAction`) : hors `editMode` uniquement — même garde que
        // `entityPanel` ci-dessous (mode édition = clic géré par le consommateur, voir son
        // commentaire), pour qu'un clic en édition sélectionne la forme au lieu de faire fuir
        // l'utilisateur de la carte en cours de modification.
        if (!editMode && clickedShape?.action === "open_url" && clickedShape.urlAction?.url) {
          const { url, openMode = "new_tab", confirmBeforeOpen } = clickedShape.urlAction;
          if (openMode === "overlay") {
            // Jamais de confirmation ici (voir `ShapeUrlAction.confirmBeforeOpen`) : une superposition
            // ne fait jamais quitter la carte, rien à confirmer.
            urlOverlay?.show(url);
          } else {
            const proceed = !confirmBeforeOpen || (await confirmDialog?.confirm({
              title: "Quitter la carte ?",
              message: "Vous allez quitter la carte pour ouvrir ce lien externe.",
              confirmText: "Continuer",
              cancelText: "Annuler",
            }) ?? true);
            if (proceed) window.open(url, openMode === "same_tab" ? "_self" : "_blank");
          }
        }

        if (entityPanel && entityPanelConfig) {
          const shape = clickedShape;
          const entry = shape ? entityPanelConfig.getEntityPanelEntry(shape) : null;

          if (entry && shape) {
            // "🧭 Y aller" (voir `EntityPanelActions.onGoTo`) : seulement si le mode itinéraire est
            // configuré ET que la géolocalisation est active — sinon un bouton qui ne mènerait nulle
            // part (pas de position utilisateur connue à utiliser comme départ, voir `startRouteMode`).
            const actions: EntityPanelActions | undefined =
              routePanel && mapAdapter.getGeolocationEnabled()
                ? {
                    onGoTo: () => {
                      // Ferme la fiche entité (le "drawer" contenant ce bouton) avant de démarrer
                      // le mode itinéraire — sinon elle reste ouverte par-dessus le panneau
                      // itinéraire qui vient de s'afficher.
                      entityPanel.close();
                      startRouteMode({ prefillToShapeId: shape.id, autoValidate: true });
                    },
                  }
                : undefined;
            entityPanel.show(entry, actions);
            // Même principe que `packages/example/src/App.tsx` avec sa propre sidebar (BESOINS.md
            // §4) : si la fiche qui vient de s'ouvrir recouvre la forme cliquée, on recadre pour la
            // garder visible plutôt que de la laisser coincée derrière/contre son bord.
            const insets = entityPanel.getOccupiedInsets();
            if (insets) mapAdapter.ensureShapeVisible(event.shapeId, insets);
          } else {
            entityPanel.close();
          }
        }
        break;
      }

      // Clic sur du vide (n'importe quel mode, pas seulement `editMode`) : efface un éventuel
      // surlignage de recherche (voir `TokaMapConfig.search`/`SearchBox.onClear`) — cliquer ailleurs
      // sur la carte est une façon naturelle de "désélectionner" le résultat trouvé, même sans
      // toucher au champ de recherche lui-même. Ferme aussi la fiche entité éventuellement ouverte,
      // même geste.
      case "map:click":
        mapAdapter.highlightShapes([]);
        entityPanel?.close();
        break;

      case "shape:dblclick":
        mapAdapter.enableEditMode(event.shapeId);
        break;

      case "shape:created":
        runShapeCommand(
          event.shape.id,
          new CreateShapeCommand(shapeStore, event.shape),
          "create",
          event.skipSave ?? false,
          event.skipHistory ?? false,
          event.skipConfirm ?? false,
          null,
          event.shape,
          event.shape,
        );
        break;

      case "shape:contextmenu": {
        if (!contextMenu) break;
        const shape = shapeStore.getAll().find((s) => s.id === event.shapeId);
        const layer = shape?.layer ?? DEFAULT_NEW_SHAPE_LAYER;
        contextMenu.show(event.screenPosition, [
          {
            label: "Modifier",
            icon: "edit",
            onSelect: () => mapAdapter.enableEditMode(event.shapeId),
          },
          {
            label: "Copier",
            icon: "copy",
            shortcut: "Ctrl+C",
            onSelect: () => copyShapes([event.shapeId]),
          },
          {
            label: "Coller",
            icon: "paste",
            shortcut: "Ctrl+V",
            onSelect: () => pasteClipboard(mapAdapter.screenPositionToLngLat(event.screenPosition)),
            disabled: clipboardIsEmpty(),
          },
          {
            label: "Copier le style",
            icon: "palette",
            onSelect: () => copyShapeStyle(event.shapeId),
          },
          {
            label: "Coller le style",
            icon: "roller",
            onSelect: () => pasteShapeStyle(event.shapeId),
            disabled: styleClipboard === null,
          },
          {
            label: "Verrouiller",
            icon: "lock",
            shortcut: "Ctrl+L",
            onSelect: () => setShapeLocked(event.shapeId, true),
          },
          {
            label: "Monter",
            icon: "layer-up",
            onSelect: () => raiseShapeLayer(event.shapeId),
            disabled: layer >= MAX_SHAPE_LAYER,
          },
          {
            label: "Descendre",
            icon: "layer-down",
            onSelect: () => lowerShapeLayer(event.shapeId),
            disabled: layer <= MIN_SHAPE_LAYER,
          },
          {
            label: "Monter tout en haut",
            icon: "layer-to-top",
            onSelect: () => bringShapeToFront(event.shapeId),
            disabled: layer >= MAX_SHAPE_LAYER,
          },
          {
            label: "Descendre tout en bas",
            icon: "layer-to-bottom",
            onSelect: () => sendShapeToBack(event.shapeId),
            disabled: layer <= MIN_SHAPE_LAYER,
          },
          {
            label: "Supprimer",
            icon: "trash",
            shortcut: "Suppr",
            onSelect: () => deleteShape(event.shapeId),
          },
        ]);
        break;
      }

      // Clic droit sur du vide, ou sur une forme verrouillée (voir `MapLibreAdapter`) : menu réduit,
      // juste "Coller" — ne dépend pas d'une forme actionnable sous le curseur.
      case "map:contextmenu": {
        if (!contextMenu) break;
        contextMenu.show(event.screenPosition, [
          {
            label: "Coller",
            icon: "paste",
            shortcut: "Ctrl+V",
            onSelect: () => pasteClipboard(mapAdapter.screenPositionToLngLat(event.screenPosition)),
            disabled: clipboardIsEmpty(),
          },
        ]);
        break;
      }

      // Clic droit À L'INTÉRIEUR du rectangle de sélection multiple (BESOINS.md §13, voir
      // `MapLibreAdapter.handleShapeContextMenu`) : agit sur TOUT le groupe, pas une forme unique —
      // menu réduit à Copier/Coller le style/Verrouiller/Supprimer (pas de "Coller" ni "Modifier",
      // qui n'ont pas de sens pour un groupe entier ici). "Coller le style" a lui du sens pour un
      // groupe (contrairement à "Copier le style", qui n'a qu'une seule source possible) : applique
      // le style copié à TOUTES les formes du groupe en un seul geste (`pasteShapeStyle` batche déjà
      // via `runShapeMutation`, voir plus haut).
      case "selection:contextmenu": {
        if (!contextMenu) break;
        contextMenu.show(event.screenPosition, [
          {
            label: "Copier",
            icon: "copy",
            shortcut: "Ctrl+C",
            onSelect: () => copyShapes(event.shapeIds),
          },
          {
            label: "Coller le style",
            icon: "roller",
            onSelect: () => pasteShapeStyle(event.shapeIds),
            disabled: styleClipboard === null,
          },
          {
            label: "Verrouiller",
            icon: "lock",
            shortcut: "Ctrl+L",
            // Un seul appel groupé (voir `setShapeLocked`/`runShapeMutation`) plutôt qu'une boucle
            // avec un appel par forme : tout le groupe verrouillé en une seule entrée `History`/un
            // seul `onSave`, cohérent avec le déplacement/la rotation/le redimensionnement du groupe
            // (BESOINS.md §13).
            onSelect: () => setShapeLocked(event.shapeIds, true),
          },
          {
            label: "Supprimer",
            icon: "trash",
            shortcut: "Suppr",
            // Même raisonnement que "Verrouiller" ci-dessus : `deleteShape` accepte déjà un tableau
            // (voir son propre commentaire) et batche en une seule entrée `History`/un seul `onSave`
            // — exactement ce que fait déjà le raccourci clavier Suppr/Retour arrière pour ce même
            // groupe (`useKeyboardShortcuts.ts` côté exemple). Sans cette entrée, la seule façon de
            // supprimer un groupe depuis ce menu était de rouvrir le menu contextuel forme par forme,
            // avec un appel `onSave` par forme au lieu d'un seul pour tout le groupe.
            onSelect: () => deleteShape(event.shapeIds),
          },
        ]);
        break;
      }

      case "shape:lock-toggled":
        setShapeLocked(event.shapeId, event.locked);
        break;

      case "shape:vertex-moved": {
        const shape = shapeStore.getAll().find((s) => s.id === event.shapeId);
        if (!shape) break;
        // Même logique que `ShapeStore.moveVertex` (sommet 0 dupliqué en fin d'anneau) : reproduite
        // ici pour connaître l'état résultant sans avoir à exécuter la commande avant `runShapeCommand`.
        const coordinates = [...shape.coordinates];
        coordinates[event.vertexIndex] = event.position;
        if (event.vertexIndex === 0) coordinates[coordinates.length - 1] = event.position;

        runShapeCommand(
          event.shapeId,
          new MoveVertexCommand(
            shapeStore,
            event.shapeId,
            event.vertexIndex,
            shape.coordinates[event.vertexIndex]!,
            event.position,
          ),
          "update",
          event.skipSave ?? false,
          event.skipHistory ?? false,
          event.skipConfirm ?? false,
          shape,
          { ...shape, coordinates },
          { ...shape, coordinates },
        );
        break;
      }

      case "shape:rotated": {
        const shape = shapeStore.getAll().find((s) => s.id === event.shapeId);
        if (!shape) break;
        runShapeCommand(
          event.shapeId,
          new RotateShapeCommand(
            shapeStore,
            event.shapeId,
            { rotation: shape.rotation, coordinates: shape.coordinates },
            { rotation: event.rotation, coordinates: event.coordinates },
            shape.shapeKind,
          ),
          "update",
          event.skipSave ?? false,
          event.skipHistory ?? false,
          event.skipConfirm ?? false,
          shape,
          { ...shape, rotation: event.rotation, coordinates: event.coordinates },
          { ...shape, rotation: event.rotation, coordinates: event.coordinates },
        );
        break;
      }

      case "shape:moved": {
        const shape = shapeStore.getAll().find((s) => s.id === event.shapeId);
        if (!shape) break;
        runShapeCommand(
          event.shapeId,
          new MoveShapeCommand(shapeStore, event.shapeId, shape.coordinates, event.coordinates),
          "update",
          event.skipSave ?? false,
          event.skipHistory ?? false,
          event.skipConfirm ?? false,
          shape,
          { ...shape, coordinates: event.coordinates },
          { ...shape, coordinates: event.coordinates },
        );
        break;
      }

      case "shape:scaled": {
        const shape = shapeStore.getAll().find((s) => s.id === event.shapeId);
        if (!shape) break;
        runShapeCommand(
          event.shapeId,
          new ScaleShapeCommand(shapeStore, event.shapeId, shape.coordinates, event.coordinates),
          "update",
          event.skipSave ?? false,
          event.skipHistory ?? false,
          event.skipConfirm ?? false,
          shape,
          { ...shape, coordinates: event.coordinates },
          { ...shape, coordinates: event.coordinates },
        );
        break;
      }

      // Clic droit sur un sommet en édition (voir `MapLibreAdapter.handleShapeContextMenu`) :
      // "Retirer le point" n'est proposé que si la forme reste valide sans lui (ligne : au moins 2
      // sommets restants ; polygone : au moins 3) ; "Diviser ici" jamais sur une extrémité de ligne
      // (rien à diviser d'un côté). Label volontairement "Diviser ici", pas "Couper" : trop proche du
      // couper/coller (Ctrl+X, voir le menu contextuel de la forme) pour une action qui n'a rien à
      // voir avec le presse-papiers. Les 2 actions elles-mêmes vivent sur `mapAdapter`
      // (`removeShapeVertex`/`cutShapeAtVertex`, voir leurs commentaires respectifs), qui émettent à
      // leur tour `"shape:scaled"`/`"shape:line-split"`/`"shape:kind-converted"`, gérés juste
      // en-dessous.
      case "vertex:contextmenu": {
        if (!contextMenu) break;
        const shape = shapeStore.getAll().find((s) => s.id === event.shapeId);
        if (!shape) break;
        const isLine = shape.shapeKind === "line";
        // Sommets UNIQUES (sans le point de clôture dupliqué d'un polygone) — même convention que
        // `EditingState.points`/`"shape:vertex-moved"`.
        const vertexCount = isLine ? shape.coordinates.length : shape.coordinates.length - 1;
        const isEndpoint = isLine && (event.vertexIndex === 0 || event.vertexIndex === vertexCount - 1);
        const items: ContextMenuItem[] = [
          {
            label: "Retirer le point",
            icon: "node-remove",
            onSelect: () => mapAdapter.removeShapeVertex(event.shapeId, event.vertexIndex),
            disabled: vertexCount <= (isLine ? 2 : 3),
          },
          {
            label: "Diviser ici",
            icon: "split",
            onSelect: () => mapAdapter.cutShapeAtVertex(event.shapeId, event.vertexIndex),
            disabled: isEndpoint,
          },
        ];
        // Uniquement pour une ligne (jamais un polygone, qui n'a pas d'extrémité à prolonger) — même
        // geste que le double-clic sur cette même extrémité (voir `handleShapeDblClick`), proposé
        // aussi ici : équivalent programmatique, `mapAdapter.startExtendLineAtVertex`. Désactivé
        // (pas omis) sur un sommet intérieur d'une ligne, même convention que "Diviser ici".
        if (isLine) {
          items.push({
            label: "Continuer la ligne",
            icon: "line-extend",
            onSelect: () => mapAdapter.startExtendLineAtVertex(event.shapeId, event.vertexIndex),
            disabled: !isEndpoint,
          });
        }
        contextMenu.show(event.screenPosition, items);
        break;
      }

      case "shape:line-split": {
        const shape = shapeStore.getAll().find((s) => s.id === event.shapeId);
        if (!shape) break;
        const resultShape = { ...shape, coordinates: event.firstCoordinates };
        runShapeBatchCommand(
          [
            { shapeId: event.shapeId, kind: "update", confirmedShapeIfNew: shape, resultShape },
            { shapeId: event.newShape.id, kind: "create", confirmedShapeIfNew: null, resultShape: event.newShape },
          ],
          new BatchCommand([
            new ScaleShapeCommand(shapeStore, event.shapeId, shape.coordinates, event.firstCoordinates),
            new CreateShapeCommand(shapeStore, event.newShape),
          ]),
          event.skipSave ?? false,
          event.skipHistory ?? false,
          event.skipConfirm ?? false,
          [resultShape, event.newShape],
        );
        break;
      }

      case "shape:kind-converted": {
        const shape = shapeStore.getAll().find((s) => s.id === event.shapeId);
        if (!shape) break;
        const to = { shapeKind: event.shapeKind, coordinates: event.coordinates, style: event.style };
        runShapeCommand(
          event.shapeId,
          new ConvertShapeKindCommand(
            shapeStore,
            event.shapeId,
            { shapeKind: shape.shapeKind, coordinates: shape.coordinates, style: shape.style },
            to,
          ),
          "update",
          event.skipSave ?? false,
          event.skipHistory ?? false,
          event.skipConfirm ?? false,
          shape,
          { ...shape, ...to },
          { ...shape, ...to },
        );
        break;
      }

      // Déplacement/rotation/redimensionnement de GROUPE (BESOINS.md §13, sélection multiple) : une
      // seule `BatchCommand`/entrée `History` pour tout le groupe (un `history.undo()` défait toutes
      // les formes d'un coup) et un seul appel `onSave` avec l'ensemble des formes — voir
      // `ShapeSaveTracker.runBatch`. `shapesForSave` porte l'état RÉSULTANT de chaque forme (identique
      // à `resultShape`, pas de suivi de "confirmation partielle" séparé ici, ces 2 valeurs coïncident
      // toujours pour une commande de groupe fraîchement exécutée).
      case "selection:moved": {
        const commands: Command[] = [];
        const entries: BatchSaveEntry[] = [];
        const shapesForSave: Shape[] = [];
        for (const { shapeId, coordinates } of event.shapes) {
          const shape = shapeStore.getAll().find((s) => s.id === shapeId);
          if (!shape) continue;
          commands.push(new MoveShapeCommand(shapeStore, shapeId, shape.coordinates, coordinates));
          const resultShape = { ...shape, coordinates };
          entries.push({ shapeId, kind: "update", confirmedShapeIfNew: shape, resultShape });
          shapesForSave.push(resultShape);
        }
        if (commands.length === 0) break;
        runShapeBatchCommand(entries, new BatchCommand(commands), false, false, false, shapesForSave);
        break;
      }

      case "selection:rotated": {
        const commands: Command[] = [];
        const entries: BatchSaveEntry[] = [];
        const shapesForSave: Shape[] = [];
        for (const { shapeId, rotation, coordinates } of event.shapes) {
          const shape = shapeStore.getAll().find((s) => s.id === shapeId);
          if (!shape) continue;
          commands.push(
            new RotateShapeCommand(
              shapeStore,
              shapeId,
              { rotation: shape.rotation, coordinates: shape.coordinates },
              { rotation, coordinates },
              shape.shapeKind,
            ),
          );
          const resultShape = { ...shape, rotation, coordinates };
          entries.push({ shapeId, kind: "update", confirmedShapeIfNew: shape, resultShape });
          shapesForSave.push(resultShape);
        }
        if (commands.length === 0) break;
        runShapeBatchCommand(entries, new BatchCommand(commands), false, false, false, shapesForSave);
        break;
      }

      case "selection:scaled": {
        const commands: Command[] = [];
        const entries: BatchSaveEntry[] = [];
        const shapesForSave: Shape[] = [];
        for (const { shapeId, coordinates } of event.shapes) {
          const shape = shapeStore.getAll().find((s) => s.id === shapeId);
          if (!shape) continue;
          commands.push(new ScaleShapeCommand(shapeStore, shapeId, shape.coordinates, coordinates));
          const resultShape = { ...shape, coordinates };
          entries.push({ shapeId, kind: "update", confirmedShapeIfNew: shape, resultShape });
          shapesForSave.push(resultShape);
        }
        if (commands.length === 0) break;
        runShapeBatchCommand(entries, new BatchCommand(commands), false, false, false, shapesForSave);
        break;
      }

      case "drawing:started":
        cancelDrawingButton.show();
        // Même exclusivité qu'avec `startEditingMapView` (voir plus bas) : un dessin qui démarre
        // (quel que soit le chemin — bouton "▭ Rectangle" de l'exemple, console, etc.) ferme un
        // mode itinéraire éventuellement ouvert, plutôt que de laisser les deux UI coexister.
        cancelRouteMode();
        break;

      case "drawing:ended":
        cancelDrawingButton.hide();
        break;

      case "drawing:tutorial-step":
        drawTutorial.showStep(event.shapeKind, event.step);
        break;

      case "drawing:tutorial-ended":
        drawTutorial.hide();
        break;

      case "map-view:edit-started":
        mapViewFrame.show();
        mapViewCrosshair.show((dx, dy) => mapAdapter.panByScreenPixels(dx, dy));
        mapViewEditTutorial.showMessage(
          "✛",
          "Vue par défaut",
          "Déplacez (glisser), zoomez (molette) et faites pivoter (clic droit + glisser) la carte, " +
            "puis cliquez sur Enregistrer pour valider la nouvelle vue par défaut.",
        );
        mapViewEditControls.show(confirmMapViewEdit, event.allowCancel ? cancelMapViewEdit : null);
        mapViewAddressSearch.show();
        searchBox?.hide();
        cancelRouteMode();
        break;

      case "map-view:edit-ended":
        mapViewFrame.hide();
        mapViewCrosshair.hide();
        mapViewEditTutorial.hide();
        mapViewEditControls.hide();
        mapViewAddressSearch.hide();
        searchBox?.show();
        mapViewEditBefore = null;
        mapViewEditOptions = {};
        break;

      case "map-view:changed": {
        // Voir `ResetViewButton` : masqué tant que la vue courante correspond encore à
        // `initialView`, affiché dès qu'elle en diverge (déplacement, zoom ou rotation), quelle
        // qu'en soit la cause (geste natif, `setMapView`, undo/redo...) — `resetView` lui-même
        // redéclenche ce même évènement et referme donc le bouton en retour.
        const resetViewButtonVisible = !isAtInitialView(event.view);
        resetViewButton?.setVisible(resetViewButtonVisible);
        zoomControl?.setZoomState(event.view.zoom, mapAdapter.getZoomBounds());
        // Suit la zone visible pendant qu'on déplace/zoome la carte, tant que la grille de
        // pathfinding est activée depuis le popup de `FpsMeter` (voir `debugGridEnabled` ci-dessus).
        if (debugGridEnabled) refreshDebugGrid();
        break;
      }

      case "geolocation:changed":
        // Désactivée : rien à recentrer, le bouton n'a plus de raison d'être affiché. Activée : pas
        // encore de position connue à cet instant précis (voir `"geolocation:visibility"` ci-dessous,
        // qui prendra le relais dès la première position reçue) — le bouton reste donc caché jusque-là.
        if (!event.enabled) setGeolocationButtonVisible(false);
        break;

      case "geolocation:visibility":
        setGeolocationButtonVisible(!event.visible);
        break;
    }
  });

  // Géolocalisation (`configurations.geolocation`/`TokaMapConfig.geolocation`, voir BESOINS.md §15) :
  // appliquée seulement maintenant, une fois `mapAdapter.on(...)` ci-dessus enregistré (voir son
  // commentaire) — même précédence que `tileLayerName`/`defaultShapeStyle` (donnée chargée > option de
  // construction > `false`), en un seul appel direct (PAS `configurationSaveTracker.run`, comme
  // `mapAdapter.setTileLayerName(view.tileLayerName)` plus haut) : ce n'est ici qu'un état de départ,
  // pas une modification à suivre/persister.
  mapAdapter.setGeolocationEnabled(view?.geolocation ?? config.geolocation ?? false);

  // `fpsMeter` (le bouton/la modale eux-mêmes) reste réservé à `editMode` (outil de diagnostic,
  // voir `FpsMeter`) — mais l'AFFICHAGE de la grille (ci-dessous) ne l'est pas : la préférence est
  // partagée par toute instance `createTokaMap` (voir `DEBUG_GRID_STORAGE_KEY`), y compris une
  // instance en lecture seule comme celle de l'Aperçu (`PreviewModal.tsx`, `editMode: false`) — sans
  // quoi activer la grille depuis l'éditeur ne se répercuterait jamais sur ce que l'Aperçu affiche.
  const fpsMeter = editMode
    ? new FpsMeter(container, {
        initialDebugGridEnabled: debugGridEnabled,
        onToggleDebugGrid: (enabled) => {
          debugGridEnabled = enabled;
          localStorage.setItem(DEBUG_GRID_STORAGE_KEY, String(enabled));
          if (enabled) refreshDebugGrid();
          else mapAdapter.clearPathfindingDebugGrid();
        },
      })
    : null;
  if (debugGridEnabled) refreshDebugGrid();

  // La grille affiche l'état COURANT des formes (`computeAreaDebugGrid(shapeStore.getAll(), ...)`)
  // — sans ça, activer/changer l'attractivité d'une forme depuis la sidebar ne se refléterait qu'au
  // prochain déplacement/zoom de la carte (voir `"map-view:changed"` plus haut), pas immédiatement.
  shapeStore.onChange(() => {
    if (debugGridEnabled) refreshDebugGrid();
  });

  // Synchronise entre plusieurs onglets/instances déjà ouverts (voir `DEBUG_GRID_STORAGE_KEY`) —
  // cet évènement ne se déclenche JAMAIS dans l'onglet qui vient d'écrire la valeur (seulement dans
  // les AUTRES), donc pas de boucle avec `onToggleDebugGrid` ci-dessus. `fpsMeter` peut être `null`
  // ici (instance en lecture seule, ex. l'Aperçu) : rien à synchroniser côté case à cocher, mais la
  // grille elle-même doit tout de même s'afficher/se masquer.
  window.addEventListener("storage", (event) => {
    if (event.key !== DEBUG_GRID_STORAGE_KEY) return;
    debugGridEnabled = event.newValue === "true";
    fpsMeter?.setDebugGridEnabled(debugGridEnabled);
    if (debugGridEnabled) refreshDebugGrid();
    else mapAdapter.clearPathfindingDebugGrid();
  });

  /** Voir `TokaMapInstance.captureImage` — délègue entièrement à `mapAdapter`, qui seul connaît les
   * canvas à recomposer. */
  function captureImage(options?: CaptureImageOptions): Promise<CaptureImageResult> {
    return mapAdapter.captureImage(options);
  }

  /** Voir `TokaMapInstance.getMapView` — simple lecture de `mapAdapter`, pas de logique propre. */
  function getMapView(): MapViewState {
    const { min: zoomMin, max: zoomMax } = mapAdapter.getZoomBounds();
    return {
      center: mapAdapter.getCenter(),
      zoom: mapAdapter.getZoom(),
      zoomMin,
      zoomMax,
      rotationDeg: mapAdapter.getRotation(),
    };
  }

  /**
   * Instantané complet de la "configuration" courante (vue ET style par défaut, voir
   * `TokaMapConfigurationState`) — lu en direct depuis `mapAdapter`, jamais mis en cache. Sert de
   * base à `setMapView`/`setDefaultShapeStyle` : chacune ne modifie QUE son propre champ, mais
   * construit quand même l'état COMPLET (voir `TokaMapConfigurationState`), pour que
   * `onSaveConfiguration`/le rollback portent toujours sur la configuration entière.
   *
   * `formatVersion` n'a, lui, rien d'équivalent à lire sur `mapAdapter` (contrairement à
   * `tileLayerName`/`defaultShapeStyle`) : il vient de la fermeture (`formatVersion` mis à jour par
   * `migrationGate` ci-dessous une fois une migration acceptée), pas de l'adaptateur.
   */
  function getConfigurationState(): TokaMapConfigurationState {
    return {
      view: getMapView(),
      defaultShapeStyle: mapAdapter.getDefaultShapeStyle(),
      tileLayerName: mapAdapter.getTileLayerName(),
      osmOptions: mapAdapter.getOsmOptions(),
      backgroundColor: mapAdapter.getBackgroundColor(),
      geolocation: mapAdapter.getGeolocationEnabled(),
      allowPitch: mapAdapter.getAllowPitch(),
      framing: mapAdapter.getFraming(),
      formatVersion,
    };
  }

  /**
   * Factorise `setMapView`/`setDefaultShapeStyle`/`setTileLayerName`/`setBackgroundColor`/
   * `setGeolocationEnabled` ci-dessous — toutes lisent la configuration courante, en dérivent une
   * nouvelle (`buildTo`), et passent le tout par `configurationSaveTracker.run(...)`.
   *
   * `buildTo` reçoit la configuration COURANTE (`current`, ci-dessous) — pas `historyBefore` — pour
   * ne jamais perdre un champ modifié entre-temps par un autre appel (ex. `tileLayerName` changé
   * pendant qu'un slider de zoom drague `view.zoom`, voir `historyBefore` ci-dessous) : seul le
   * champ ciblé par CET appel doit changer, le reste doit toujours refléter l'état réel courant.
   *
   * `historyBefore` (voir `ConfigurationSaveTracker.resolveHistoryBeforeConfiguration`) sert
   * uniquement de "avant" à la `Command` elle-même (ce que `.undo()` restaure) — DIFFÉRENT de
   * `current` dès qu'une rafale `skipHistory: true` est en cours (ex. les ticks continus d'un
   * slider de zoom, voir `SliderInputNumberFeatureStyle` côté consommateur) : sans lui, la commande
   * RÉELLE qui clôt le geste (`skipHistory: false`) se construirait un "avant" déjà mutée par tous
   * les ticks intermédiaires — quasiment identique à son "après" — et `Annuler` ne défaisait alors
   * quasiment rien, au lieu de tout le geste d'un coup (même bug que `ShapeSaveTracker
   * .resolveHistoryBeforeShape`, voir son commentaire).
   */
  function buildConfigurationCommand(
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean },
    buildTo: (current: TokaMapConfigurationState) => TokaMapConfigurationState,
  ): void {
    const current = getConfigurationState();
    const skipHistory = options.skipHistory ?? false;
    const historyBefore = configurationSaveTracker.resolveHistoryBeforeConfiguration(current, skipHistory);
    const to = buildTo(current);
    const command = new SetConfigurationCommand(mapAdapter, historyBefore, to);
    configurationSaveTracker.run(
      command,
      options.skipSave ?? false,
      skipHistory,
      options.skipConfirm ?? false,
      historyBefore,
      to,
    );
  }

  /**
   * Change la vue de caméra de la carte — pas un geste utilisateur sur la carte (pas de
   * `MapAdapterEvent` dédié), typiquement déclenché depuis la console (voir `commands` ci-dessous).
   * Passe par `configurationSaveTracker.run(...)` comme `runShapeCommand` passe par
   * `shapeSaveTracker.run(...)` pour les formes — mêmes options `skipHistory`/`skipSave`/
   * `skipConfirm` (voir `TokaMapInstance.setMapView`/`ConfigurationSaveTracker.run`).
   */
  function setMapView(
    view: Partial<MapViewState>,
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean } = {},
  ): void {
    buildConfigurationCommand(options, (current) => ({ ...current, view: { ...current.view, ...view } }));
  }

  /**
   * Voir `TokaMapInstance.setDefaultShapeStyle` — même mécanisme que `setMapView` (lit la
   * configuration courante, ne change QUE `defaultShapeStyle` en la fusionnant avec sa valeur
   * courante, passe le tout par `configurationSaveTracker.run(...)`).
   */
  function setDefaultShapeStyle(
    style: Partial<ShapeStyle>,
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean } = {},
  ): void {
    buildConfigurationCommand(options, (current) => ({
      ...current,
      defaultShapeStyle: { ...current.defaultShapeStyle, ...style },
    }));
  }

  /**
   * Voir `TokaMapInstance.setTileLayerName` — même mécanisme que `setDefaultShapeStyle` (lit la
   * configuration courante, mais REMPLACE `tileLayerName` au lieu de le fusionner : scalaire, pas
   * d'objet à fusionner comme `defaultShapeStyle`), passe le tout par `configurationSaveTracker.run(...)`.
   */
  function setTileLayerName(
    tileLayerName: TileLayerName,
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean } = {},
  ): void {
    buildConfigurationCommand(options, (current) => ({ ...current, tileLayerName }));
  }

  /**
   * La fusion est faite ici et pas dans `MapAdapter.setOsmOptions` : la commande porte alors l'objet
   * complet dans son "avant" comme dans son "après", et son `undo()` peut retirer un réglage au lieu
   * de seulement en superposer un autre.
   */
  function setOsmOptions(
    osmOptions: Partial<OsmBackgroundOptions>,
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean } = {},
  ): void {
    buildConfigurationCommand(options, (current) => ({
      ...current,
      osmOptions: { ...current.osmOptions, ...osmOptions },
    }));
  }

  /**
   * Voir `TokaMapInstance.setBackgroundColor` — même mécanisme que `setTileLayerName` (lit la
   * configuration courante, REMPLACE `backgroundColor` — scalaire, pas d'objet à fusionner —, passe
   * le tout par `configurationSaveTracker.run(...)`).
   */
  function setBackgroundColor(
    backgroundColor: string | undefined,
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean } = {},
  ): void {
    buildConfigurationCommand(options, (current) => ({ ...current, backgroundColor }));
  }

  /**
   * Voir `TokaMapInstance.setAllowPitch` — même mécanisme que `setTileLayerName`/`setBackgroundColor`
   * (lit la configuration courante, REMPLACE `allowPitch` — scalaire, pas d'objet à fusionner —,
   * passe le tout par `configurationSaveTracker.run(...)`).
   */
  function setAllowPitch(
    allowPitch: boolean,
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean } = {},
  ): void {
    buildConfigurationCommand(options, (current) => ({ ...current, allowPitch }));
  }

  function setFraming(
    framing: MapFraming | undefined,
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean } = {},
  ): void {
    buildConfigurationCommand(options, (current) => ({ ...current, framing }));
  }

  /**
   * Voir `TokaMapInstance.setGeolocationEnabled` — même mécanisme que `setTileLayerName` (lit la
   * configuration courante, REMPLACE `geolocation` — scalaire, pas d'objet à fusionner —, passe le
   * tout par `configurationSaveTracker.run(...)`) : undoable, déclenche `onSaveConfiguration` sauf
   * `options.skipSave`, même que le reste de `TokaMapConfigurationState` (BESOINS.md §15).
   */
  function setGeolocationEnabled(
    enabled: boolean,
    options: { skipHistory?: boolean; skipSave?: boolean; skipConfirm?: boolean } = {},
  ): void {
    buildConfigurationCommand(options, (current) => ({ ...current, geolocation: enabled }));
  }

  // Configuration capturée à `startEditingMapView()` — c'est le "from" du `SetConfigurationCommand`
  // exécuté par `confirmMapViewEdit` (pas la configuration courante au moment de "Enregistrer", qui
  // est le "to" : elle vaut alors exactement ce que l'utilisateur veut sauvegarder, donc un "from"
  // égal au "to" serait un no-op). Reconstruite indépendamment de `MapLibreAdapter.mapViewEditState`
  // (même moment de capture, mais un `Command`/`History` n'a rien à voir avec l'adapter lui-même) ;
  // remise à `null` par le handler `"map-view:edit-ended"` ci-dessus, qui couvre aussi bien
  // Enregistrer qu'Annuler qu'une interruption par un dessin. Le style par défaut ne change jamais
  // pendant ce mode (seule la vue est éditée interactivement) : il est juste reporté tel quel du
  // "from" au "to" dans `confirmMapViewEdit`.
  let mapViewEditBefore: TokaMapConfigurationState | null = null;
  // Options `skipSave`/`skipHistory`/`skipConfirm` (`DrawTriggerOptions`, voir
  // `StartEditingMapViewOptions`) capturées à `startEditingMapView()`, au même titre que
  // `mapViewEditBefore` — reportées sur le `SetConfigurationCommand` construit par
  // `confirmMapViewEdit()`, exactement comme `enableEditMode`'s options survivent jusqu'à
  // `disableEditMode`. Remises à `{}` par le handler `"map-view:edit-ended"` ci-dessus.
  let mapViewEditOptions: DrawTriggerOptions = {};

  /**
   * Voir `TokaMapInstance.startEditingMapView` — recentre d'abord sur `initialView` (même vue que
   * `ResetViewButton`, voir plus haut), pour partir d'un point de départ propre et prévisible
   * plutôt que d'où la caméra se trouvait par hasard avant d'entrer dans le mode. Capture ensuite
   * `mapViewEditBefore` (à partir de cette vue tout juste réinitialisée) avant de déléguer à
   * l'adapter (qui capture sa propre copie pour son besoin propre, voir
   * `MapLibreAdapter.startEditingMapView`) — "Annuler" revient donc à `initialView`, pas à la vue
   * transitoire d'avant l'entrée dans le mode. Capture aussi `skipSave`/`skipHistory`/`skipConfirm`
   * dans `mapViewEditOptions`, pour `confirmMapViewEdit()` plus tard — l'adapter lui-même n'en a pas
   * besoin (seul `allowCancel` lui est transmis, voir `MapLibreAdapter.startEditingMapView`).
   */
  function startEditingMapView(options?: StartEditingMapViewOptions): void {
    resetView();
    mapViewEditBefore = getConfigurationState();
    mapViewEditOptions = {
      skipSave: options?.skipSave,
      skipHistory: options?.skipHistory,
      skipConfirm: options?.skipConfirm,
    };
    mapAdapter.startEditingMapView(options);
  }

  /**
   * "Enregistrer" : construit directement un `SetConfigurationCommand` (pas `setMapView`, qui
   * relirait la configuration courante comme "from" — ici le "from" correct est `mapViewEditBefore`,
   * capturé à l'entrée dans le mode) et le passe par `configurationSaveTracker.run(...)` — toute
   * modification de la vue par défaut, qu'elle vienne de `setMapView` ou de ce mode interactif,
   * déclenche `onSaveConfiguration` de la même façon (BESOINS.md §7.1), sauf si `startEditingMapView`
   * a été appelé avec `skipSave`/`skipHistory`/`skipConfirm` (voir `mapViewEditOptions`) — puis
   * referme le mode via l'adapter (qui ne touche plus à la caméra, déjà exactement là où
   * l'utilisateur la veut).
   */
  function confirmMapViewEdit(): void {
    if (!mapViewEditBefore) return;
    const to: TokaMapConfigurationState = { ...mapViewEditBefore, view: getMapView() };
    const command = new SetConfigurationCommand(mapAdapter, mapViewEditBefore, to);
    configurationSaveTracker.run(
      command,
      mapViewEditOptions.skipSave ?? false,
      mapViewEditOptions.skipHistory ?? false,
      mapViewEditOptions.skipConfirm ?? false,
      mapViewEditBefore,
      to,
    );
    mapAdapter.endEditingMapView();
  }

  /** "Annuler" : simple relais vers l'adapter, qui revient à la vue capturée à l'entrée dans le
   * mode et referme celui-ci — rien à confirmer, donc pas de `Command`/`History` impliqué ici. */
  function cancelMapViewEdit(): void {
    mapAdapter.cancelEditingMapView();
  }

  /**
   * Point d'entrée commun à tous les setters `setShape*`/`raiseShapeLayer`/`lowerShapeLayer`/
   * `deleteShape` ci-dessous, pour qu'un `shapeId: string | string[]` se comporte exactement comme
   * la sélection multiple à la souris (BESOINS.md §13) : une SEULE forme suit le chemin
   * `runShapeCommand` existant (un `SaveEvent` `{ shapes: [une seule], kind }`), plusieurs suivent
   * `runShapeBatchCommand` — une seule `BatchCommand`/entrée `History` (un `Annuler` défait tout le
   * groupe d'un coup) et un seul appel `onSave` avec `{ shapes: [...toutes], kind }` (voir
   * `ShapeSaveTracker.runBatch`). `build` reçoit l'état ACTUEL de chaque forme (relu depuis
   * `shapeStore`, jamais un état capturé avant cet appel) et renvoie la `Command` à exécuter pour
   * elle plus son état résultant — c'est ce qui permet à `style`/`text`/... (fusionnés avec l'état
   * courant DE CHAQUE FORME, pas une valeur commune) de rester corrects même si les formes du
   * groupe n'avaient pas le même état de départ. `kind` (défaut `"update"`, tous les setters) est
   * UN SEUL pour tout l'appel — jamais un mélange au sein d'un même groupe — `resultShape` peut
   * valoir `null` (suppression, voir `deleteShape`), auquel cas la forme elle-même (son dernier état
   * connu) est envoyée à `onSave` à la place, même convention que `run()`/`shapeForSave`. Des ids
   * inconnus sont silencieusement ignorés (même convention que chaque setter avant cette
   * factorisation) ; sans effet si aucun id ne résout à une forme existante.
   *
   * `build` reçoit un 2e paramètre `historyBefore` — voir `ShapeSaveTracker
   * .resolveHistoryBeforeShape` : l'état à utiliser comme "avant" de la `Command` (celui que
   * `.undo()` doit restaurer), DIFFÉRENT de `shape` (l'état COURANT, ci-dessus, utilisé pour fusionner/
   * calculer `resultShape`) dès qu'une rafale `skipHistory: true` est en cours pour cette forme (ex.
   * les ticks continus d'un slider, voir `SliderInputNumberFeatureStyle` côté consommateur) — sans
   * rafale, `historyBefore === shape`, comportement inchangé. Chaque appelant doit construire sa
   * `Command` à partir de `historyBefore` (pas `shape`) pour son paramètre "ancienne valeur".
   */
  function runShapeMutation(
    shapeId: string | string[],
    options: DrawTriggerOptions,
    build: (shape: Shape, historyBefore: Shape) => { command: Command; resultShape: Shape | null },
    kind: SaveEvent["kind"] = "update",
  ): void {
    const shapeIds = Array.isArray(shapeId) ? shapeId : [shapeId];
    const shapes = shapeIds
      .map((id) => shapeStore.getAll().find((s) => s.id === id))
      .filter((s): s is Shape => s != null);
    if (shapes.length === 0) return;

    const skipHistory = options.skipHistory ?? false;

    if (shapes.length === 1) {
      const shape = shapes[0]!;
      const historyBefore = shapeSaveTracker.resolveHistoryBeforeShape(shape.id, shape, skipHistory);
      const { command, resultShape } = build(shape, historyBefore);
      runShapeCommand(
        shape.id,
        command,
        kind,
        options.skipSave ?? false,
        skipHistory,
        options.skipConfirm ?? false,
        shape,
        resultShape,
        resultShape ?? shape,
      );
      return;
    }

    const commands: Command[] = [];
    const entries: BatchSaveEntry[] = [];
    const shapesForSave: Shape[] = [];
    for (const shape of shapes) {
      const historyBefore = shapeSaveTracker.resolveHistoryBeforeShape(shape.id, shape, skipHistory);
      const { command, resultShape } = build(shape, historyBefore);
      commands.push(command);
      entries.push({ shapeId: shape.id, kind, confirmedShapeIfNew: shape, resultShape });
      shapesForSave.push(resultShape ?? shape);
    }
    runShapeBatchCommand(
      entries,
      new BatchCommand(commands),
      options.skipSave ?? false,
      skipHistory,
      options.skipConfirm ?? false,
      shapesForSave,
    );
  }

  // Pas un geste utilisateur sur la carte (pas de `MapAdapterEvent` dédié) : typiquement déclenché
  // depuis la console (voir `commands` ci-dessous). Passe par `runShapeMutation` (une seule forme
  // ou un groupe, voir plus haut), pour rester undoable et déclencher `onSave` (BESOINS.md §7.1) au
  // même titre qu'une édition depuis la carte — `style` est fusionné avec le style COURANT de
  // chaque forme visée, pas une valeur unique partagée.
  function setShapeStyle(
    shapeId: string | string[],
    style: Partial<ShapeStyle>,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => {
      const newStyle: ShapeStyle = { ...shape.style, ...style };
      return {
        command: new SetShapeStyleCommand(shapeStore, shape.id, historyBefore.style, newStyle),
        resultShape: { ...shape, style: newStyle },
      };
    });
  }

  /**
   * Change le style de survol d'une forme par son ID (`Shape.hoverStyle`) — même logique que
   * `setShapeStyle` ci-dessus (fusionné avec le style de survol courant, undoable sauf
   * `options.skipHistory`, déclenche `onSave` sauf `options.skipSave`, ne confirme jamais la forme
   * si `options.skipConfirm`). Typiquement déclenché depuis la console (voir `commands`
   * ci-dessous), pas depuis un geste utilisateur sur la carte.
   */
  function setShapeHoverStyle(
    shapeId: string | string[],
    hoverStyle: Partial<ShapeStyle>,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => {
      const newHoverStyle: ShapeStyle = { ...shape.hoverStyle, ...hoverStyle };
      return {
        command: new SetShapeHoverStyleCommand(shapeStore, shape.id, historyBefore.hoverStyle, newHoverStyle),
        resultShape: { ...shape, hoverStyle: newHoverStyle },
      };
    });
  }

  /**
   * Recalcule les 4 sommets d'une forme-image (`shapeKind: "image"`) pour qu'ils gardent le même
   * ratio largeur/hauteur que l'image nouvellement affectée — sans ça, changer l'image casserait
   * l'invariant "le rectangle épouse le ratio de son image" garanti au dessin (voir
   * `MapLibreAdapter.startDrawingImage`/`imageCornersFromScreen`) et au redimensionnement par coin
   * (voir `VertexResizingState.lockRatio`), laissant l'ancien rectangle inchangé avec la nouvelle
   * image simplement "contenue" dedans (voir `Shape.image`, toujours en mode "contain") — des marges
   * transparentes disgracieuses si le ratio a changé, puisqu'une forme-image n'a jamais de fond/
   * bordure pour les masquer.
   *
   * Centre et rotation propres inchangés ; l'AIRE du rectangle est préservée (seul son ratio
   * largeur/hauteur change) — un choix délibéré plutôt que de garder la largeur ou la hauteur fixe,
   * pour que le changement de forme reste "équilibré" quel que soit le sens dans lequel le ratio
   * évolue. Repère local approximé en mètres (même formule que
   * `MapLibreAdapter.shapeBoundingBoxPx` : `cos(latitude)` pour la longitude, précision suffisante à
   * l'échelle d'une forme individuelle) — dupliqué ici plutôt que partagé avec l'adapter : ce fichier
   * reste map-agnostique (voir CLAUDE.md, la frontière core/adapter), et `centroid`/ce type de petit
   * calcul géométrique pur est déjà dupliqué entre `core/commands.ts` et l'adapter pour la même
   * raison.
   *
   * Les largeur/hauteur ACTUELLES sont mesurées directement sur les arêtes `ring[0]->ring[1]`/
   * `ring[1]->ring[2]` de la forme (pas sur `Shape.rotation`, qui ne reflète pas forcément
   * l'inclinaison réelle de `coordinates` — voir le commentaire de `shapeBoundingBoxPx`) : cette
   * mesure reste donc correcte même si la forme a déjà été pivotée via la poignée de rotation, ou
   * dessinée pendant que la carte avait elle-même un bearing non nul.
   *
   * `null` si la forme n'a pas exactement 4 sommets (géométrie inattendue pour une image, ne pas
   * tenter d'ajustement) ou si le nouveau ratio ne peut pas être déterminé (`width`/`height` absents,
   * invalides, ou arêtes actuelles de longueur nulle).
   */
  function resizeImageShapeToNewRatio(coordinates: LngLat[], newImage: ShapeImage | undefined): LngLat[] | null {
    const ring = coordinates.slice(0, -1);
    if (ring.length !== 4) return null;
    const { width, height } = newImage ?? {};
    if (!width || !height || width <= 0 || height <= 0) return null;
    const newRatio = width / height;

    const [p0, p1, p2] = ring as [LngLat, LngLat, LngLat, LngLat];
    const centerLng = ring.reduce((sum, [lng]) => sum + lng, 0) / ring.length;
    const centerLat = ring.reduce((sum, [, lat]) => sum + lat, 0) / ring.length;
    const metersPerDegLat = 111_320;
    const metersPerDegLng = 111_320 * Math.cos((centerLat * Math.PI) / 180);

    const toLocal = ([lng, lat]: LngLat) => ({
      x: (lng - centerLng) * metersPerDegLng,
      y: (lat - centerLat) * metersPerDegLat,
    });
    const p0Local = toLocal(p0);
    const p1Local = toLocal(p1);
    const p2Local = toLocal(p2);

    const widthMeters = Math.hypot(p1Local.x - p0Local.x, p1Local.y - p0Local.y);
    const heightMeters = Math.hypot(p2Local.x - p1Local.x, p2Local.y - p1Local.y);
    if (!(widthMeters > 0) || !(heightMeters > 0)) return null;
    const area = widthMeters * heightMeters;

    const newWidthMeters = Math.sqrt(area * newRatio);
    const newHeightMeters = Math.sqrt(area / newRatio);

    const widthDir = { x: (p1Local.x - p0Local.x) / widthMeters, y: (p1Local.y - p0Local.y) / widthMeters };
    const heightDir = { x: (p2Local.x - p1Local.x) / heightMeters, y: (p2Local.y - p1Local.y) / heightMeters };

    const halfW = newWidthMeters / 2;
    const halfH = newHeightMeters / 2;
    const localCorners = [
      { x: -halfW * widthDir.x - halfH * heightDir.x, y: -halfW * widthDir.y - halfH * heightDir.y },
      { x: halfW * widthDir.x - halfH * heightDir.x, y: halfW * widthDir.y - halfH * heightDir.y },
      { x: halfW * widthDir.x + halfH * heightDir.x, y: halfW * widthDir.y + halfH * heightDir.y },
      { x: -halfW * widthDir.x + halfH * heightDir.x, y: -halfW * widthDir.y + halfH * heightDir.y },
    ];

    const newRing: LngLat[] = localCorners.map(({ x, y }) => [
      centerLng + x / metersPerDegLng,
      centerLat + y / metersPerDegLat,
    ]);
    return [...newRing, newRing[0]!];
  }

  /**
   * Complète `image.width`/`.height` quand ils sont absents ou incohérents (voir
   * `trustedImageDimensions`) en chargeant l'image (`loadImageDimensions`) pour les déduire de ses
   * dimensions réelles, ET complète `image.svg` quand ni le consommateur (`image.svg`) ni
   * l'heuristique d'URL (`isSvgImageSrc` — extension `.svg`/préfixe `data:image/svg+xml`) ne
   * peuvent trancher (typiquement une URL CDN opaque, sans extension) en sondant le
   * `Content-Type`/le contenu réel (`detectSvgImageType`) — sans cette info, une telle image SVG
   * est prise pour un raster par `isSvgShapeImage` côté rendu, et MapLibre refuse purement et
   * simplement de charger un SVG comme source raster (voir `MapLibreAdapter.upsertRasterShapeImage`).
   * Utilisé par `setShapeImage`/`startDrawingImage`, qui ont toutes les deux besoin de ces deux
   * informations (ratio + mode de rendu) sans obliger le consommateur à les précalculer lui-même.
   * Un échec de chargement des dimensions est journalisé et l'image d'origine renvoyée telle quelle
   * (pas d'exception propagée) — l'appelant retombe alors sur le repli existant (sondage asynchrone
   * côté rendu, voir `MapLibreAdapter.probeImageDimensions`). La détection du type, elle, ne peut
   * jamais faire échouer cette fonction : `detectSvgImageType` avale ses propres erreurs et renvoie
   * `undefined` (voir son commentaire), pour ne jamais figer une valeur fausse dans `image.svg`.
   */
  async function resolveImageDimensions(image: ShapeImage | undefined): Promise<ShapeImage | undefined> {
    if (!image) return image;
    const dimensions = trustedImageDimensions(image);
    const needsSvgDetection = image.svg === undefined && !isSvgImageSrc(image.src);
    if (dimensions && !needsSvgDetection) return image;
    try {
      const [resolvedDimensions, detectedSvg] = await Promise.all([
        dimensions ? Promise.resolve(dimensions) : loadImageDimensions(image.src),
        needsSvgDetection ? detectSvgImageType(image.src) : Promise.resolve(undefined),
      ]);
      return {
        ...image,
        ...resolvedDimensions,
        ...(detectedSvg !== undefined ? { svg: detectedSvg } : {}),
      };
    } catch (error) {
      console.warn(`[setShapeImage] impossible de déterminer les dimensions de "${image.src}" :`, error);
      return image;
    }
  }

  /**
   * Change l'image d'une forme par son ID (`Shape.image`) — même logique que `setShapeStyle`/
   * `setShapeText` (fusionné avec l'image courante de la forme, voir `TokaMapInstance
   * .setShapeImage`) : `image` et `options` (`DrawTriggerOptions`) sont deux paramètres distincts,
   * pas mélangés dans un seul objet. Un `src` absent ou vide une fois fusionné (`merged.src`) vaut
   * "pas d'image" — c'est ce qui permet de retirer l'image entièrement via `{ src: undefined }` : la
   * fusion (`{ ...shape.image, src: undefined }`) écrase alors explicitement l'ancien `src`,
   * contrairement à omettre la clé (qui la laisserait inchangée). Typiquement déclenché depuis la
   * console (voir `commands` ci-dessous), pas depuis un geste utilisateur sur la carte. `async` :
   * voir `resolveImageDimensions` — `shape` est relu APRÈS cette résolution (pas avant), pour ne pas
   * construire la commande sur un état devenu périmé si la forme a changé pendant le chargement
   * réseau.
   */
  async function setShapeImage(
    shapeId: string | string[],
    image: Partial<ShapeImage> = {},
    options: DrawTriggerOptions = {},
  ): Promise<void> {
    const shapeIds = Array.isArray(shapeId) ? shapeId : [shapeId];
    const shapes = shapeIds
      .map((id) => shapeStore.getAll().find((s) => s.id === id))
      .filter((s): s is Shape => s != null);
    if (shapes.length === 0) return;

    // Résolution des dimensions/SVG EN PARALLÈLE, une fois par forme (chacune fusionne `image` avec
    // sa propre image courante — voir `runShapeMutation`) — avant `runShapeMutation`, pour la même
    // raison que la version à une seule forme : ne pas construire les commandes sur un état devenu
    // périmé pendant le chargement réseau.
    const resolvedImages = new Map(
      await Promise.all(
        shapes.map(async (shape) => {
          const merged = { ...shape.image, ...image };
          const srcChanged = image.src !== undefined && image.src !== shape.image?.src;
          if (srcChanged && image.width === undefined && image.height === undefined) {
            // `width`/`height` hérités du spread ci-dessus décrivent l'ANCIENNE image : sans ça,
            // `trustedImageDimensions` les jugerait valides et court-circuiterait le rechargement
            // dans `resolveImageDimensions` alors que `src` a changé.
            delete merged.width;
            delete merged.height;
          }
          if (srcChanged && image.svg === undefined) {
            // Même raisonnement que pour `width`/`height` ci-dessus : `merged.svg` hérité du spread
            // décrit l'ANCIENNE image. Sans ça, `needsSvgDetection` dans `resolveImageDimensions` le
            // jugerait déjà tranché et ne re-sonderait jamais le nouveau `src` (heuristique d'URL
            // puis `detectSvgImageType`) quand l'ancienne image avait un `svg` explicite.
            delete merged.svg;
          }
          const removing = "src" in image && image.src === undefined;
          if (!merged.src && merged.source && !removing) return [shape.id, { ...merged, src: "" } as ShapeImage] as const;
          const candidateImage: ShapeImage | undefined = merged.src ? (merged as ShapeImage) : undefined;
          // Même image : rien à relire (sinon chaque cran d'un curseur rechargerait l'image).
          if (candidateImage && !srcChanged && shape.image?.src === candidateImage.src) {
            return [shape.id, candidateImage] as const;
          }
          return [shape.id, await resolveImageDimensions(candidateImage)] as const;
        }),
      ),
    );

    runShapeMutation(shapeId, options, (currentShape, historyBefore) => {
      const resolvedImage = resolvedImages.get(currentShape.id);
      const imageCommand = new SetShapeImageCommand(shapeStore, currentShape.id, historyBefore.image, resolvedImage);

      // Une forme-image (`shapeKind: "image"`) doit garder le ratio de son image — voir
      // `resizeImageShapeToNewRatio`. `null` (géométrie inattendue, ou nouvelles dimensions
      // inconnues) : la forme garde ses `coordinates` actuelles, comportement inchangé.
      const newCoordinates =
        currentShape.shapeKind === "image" ? resizeImageShapeToNewRatio(currentShape.coordinates, resolvedImage) : null;

      if (!newCoordinates) {
        return {
          command: imageCommand,
          resultShape: { ...currentShape, image: resolvedImage },
        };
      }

      return {
        command: new BatchCommand([
          imageCommand,
          new ScaleShapeCommand(shapeStore, currentShape.id, currentShape.coordinates, newCoordinates),
        ]),
        resultShape: { ...currentShape, image: resolvedImage, coordinates: newCoordinates },
      };
    });
  }

  /**
   * Change le texte affiché au centre d'une forme par son ID (`Shape.text`) — même logique que
   * `setShapeStyle`/`setShapeHoverStyle` ci-dessus (fusionné avec le texte courant de la forme,
   * `undefined` le retire entièrement plutôt que de fusionner, voir `TokaMapInstance.setShapeText`).
   * Typiquement déclenché depuis la console (voir `commands` ci-dessous), pas depuis un geste
   * utilisateur sur la carte.
   */
  function setShapeText(
    shapeId: string | string[],
    text: Partial<ShapeText> | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => {
      const newText = text === undefined ? undefined : ({ ...shape.text, ...text } as ShapeText);
      return {
        command: new SetShapeTextCommand(shapeStore, shape.id, historyBefore.text, newText),
        resultShape: { ...shape, text: newText },
      };
    });
  }

  /**
   * Change l'action d'une forme par son ID (`Shape.action`) — REMPLACE entièrement la valeur
   * courante (scalaire, pas d'objet à fusionner comme `setShapeStyle`/`setShapeText`, voir
   * `TokaMapInstance.setShapeAction`). Typiquement déclenché depuis la console (voir `commands`
   * ci-dessous), pas depuis un geste utilisateur sur la carte.
   */
  function setShapeAction(
    shapeId: string | string[],
    action: string | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeActionCommand(shapeStore, shape.id, historyBefore.action, action),
      resultShape: { ...shape, action },
    }));
  }

  /**
   * Change `Shape.useActionIcon` d'une forme par son ID — REMPLACE entièrement la valeur courante
   * (même logique que `setShapeAction`, voir `TokaMapInstance.setShapeUseActionIcon`). Typiquement
   * déclenché depuis la console (voir `commands` ci-dessous), pas depuis un geste utilisateur sur
   * la carte.
   */
  function setShapeUseActionIcon(
    shapeId: string | string[],
    useActionIcon: boolean | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeUseActionIconCommand(shapeStore, shape.id, historyBefore.useActionIcon, useActionIcon),
      resultShape: { ...shape, useActionIcon },
    }));
  }

  /**
   * Change `Shape.urlAction` d'une forme par son ID — FUSIONNE avec la valeur courante (même
   * logique que `setShapeText`, voir `TokaMapInstance.setShapeUrlAction`). Typiquement déclenché
   * depuis la sidebar de configuration de l'action "rediriger vers une page web", pas depuis un
   * geste utilisateur sur la carte.
   */
  function setShapeUrlAction(
    shapeId: string | string[],
    urlAction: Partial<ShapeUrlAction> | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => {
      const newUrlAction = urlAction === undefined ? undefined : ({ ...shape.urlAction, ...urlAction } as ShapeUrlAction);
      return {
        command: new SetShapeUrlActionCommand(shapeStore, shape.id, historyBefore.urlAction, newUrlAction),
        resultShape: { ...shape, urlAction: newUrlAction },
      };
    });
  }

  /** Voir `TokaMapInstance.setShapeTargetDocumentId`. */
  function setShapeTargetDocumentId(
    shapeId: string | string[],
    targetDocumentId: string | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeTargetDocumentIdCommand(
        shapeStore,
        shape.id,
        historyBefore.targetDocumentId,
        targetDocumentId,
      ),
      resultShape: { ...shape, targetDocumentId },
    }));
  }

  /**
   * Change la charge métier opaque d'une forme par son ID (`Shape.entity`) — REMPLACE entièrement la
   * valeur courante (même logique que `setShapeAction`, voir `TokaMapInstance.setShapeEntity`).
   * Typiquement déclenché depuis la console (voir `commands` ci-dessous), pas depuis un geste
   * utilisateur sur la carte.
   */
  /** Voir `TokaMapInstance.setShapeLocked` — même logique que `setShapeUseActionIcon` (booléen,
   * remplacement absolu), déclenché soit depuis le cadenas de la barre d'outils flottante
   * (`"shape:lock-toggled"` ci-dessus), soit directement depuis la console. */
  function setShapeLocked(
    shapeId: string | string[],
    locked: boolean | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeLockedCommand(shapeStore, shape.id, historyBefore.locked, locked),
      resultShape: { ...shape, locked },
    }));
  }

  /** Voir `TokaMapInstance.setShapeWalkable` — même principe que `setShapeLocked` (scalaire, REMPLACE
   * entièrement la valeur courante). */
  function setShapeWalkable(
    shapeId: string | string[],
    walkable: boolean | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeWalkableCommand(shapeStore, shape.id, historyBefore.walkable, walkable),
      resultShape: { ...shape, walkable },
    }));
  }

  /** Voir `TokaMapInstance.setShapeThemeId` — même principe que `setShapeWalkable` (scalaire,
   * REMPLACE entièrement la valeur courante). */
  function setShapeThemeId(
    shapeId: string | string[],
    themeId: string | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeThemeIdCommand(shapeStore, shape.id, historyBefore.themeId, themeId),
      resultShape: { ...shape, themeId },
    }));
  }

  /** Voir `TokaMapInstance.setShapePathPriority` — même principe que `setShapeWalkable` (scalaire,
   * REMPLACE entièrement la valeur courante). */
  function setShapePathPriority(
    shapeId: string | string[],
    pathPriority: Shape["pathPriority"],
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapePathPriorityCommand(shapeStore, shape.id, historyBefore.pathPriority, pathPriority),
      resultShape: { ...shape, pathPriority },
    }));
  }

  function setShapeHeight(
    shapeId: string | string[],
    height: Shape["height"],
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeHeightCommand(shapeStore, shape.id, historyBefore.height, height),
      resultShape: { ...shape, height },
    }));
  }

  function setShapeElevation(
    shapeId: string | string[],
    elevation: Shape["elevation"],
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeElevationCommand(shapeStore, shape.id, historyBefore.elevation, elevation),
      resultShape: { ...shape, elevation },
    }));
  }

  function setShapeHoverHeight(
    shapeId: string | string[],
    hoverHeight: Shape["hoverHeight"],
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeHoverHeightCommand(shapeStore, shape.id, historyBefore.hoverHeight, hoverHeight),
      resultShape: { ...shape, hoverHeight },
    }));
  }

  function setShapeHoverElevation(
    shapeId: string | string[],
    hoverElevation: Shape["hoverElevation"],
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeHoverElevationCommand(shapeStore, shape.id, historyBefore.hoverElevation, hoverElevation),
      resultShape: { ...shape, hoverElevation },
    }));
  }

  function setShapeMinZoomOffset(
    shapeId: string | string[],
    minZoomOffset: Shape["minZoomOffset"],
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeMinZoomOffsetCommand(shapeStore, shape.id, historyBefore.minZoomOffset, minZoomOffset),
      resultShape: { ...shape, minZoomOffset },
    }));
  }

  /** Voir `TokaMapInstance.raiseShapeLayer`/`lowerShapeLayer` — `direction` factorise le seul calcul
   * qui diffère entre "Monter" et "Descendre" du menu contextuel. */
  function shiftShapeLayer(
    shapeId: string | string[],
    direction: 1 | -1,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => {
      const from = shape.layer;
      const to = Math.min(
        MAX_SHAPE_LAYER,
        Math.max(MIN_SHAPE_LAYER, (from ?? DEFAULT_NEW_SHAPE_LAYER) + direction * SHAPE_LAYER_STEP),
      );
      return {
        command: new SetShapeLayerCommand(shapeStore, shape.id, historyBefore.layer, to),
        resultShape: { ...shape, layer: to },
      };
    });
  }

  function raiseShapeLayer(shapeId: string | string[], options: DrawTriggerOptions = {}): void {
    shiftShapeLayer(shapeId, 1, options);
  }

  function lowerShapeLayer(shapeId: string | string[], options: DrawTriggerOptions = {}): void {
    shiftShapeLayer(shapeId, -1, options);
  }

  /** Voir `TokaMapInstance.bringShapeToFront`/`sendShapeToBack` — pose `Shape.layer` directement à
   * la borne visée plutôt que de la décaler par pas, seule différence avec `shiftShapeLayer`. */
  function setShapeLayerAbsolute(shapeId: string | string[], to: number, options: DrawTriggerOptions = {}): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeLayerCommand(shapeStore, shape.id, historyBefore.layer, to),
      resultShape: { ...shape, layer: to },
    }));
  }

  function bringShapeToFront(shapeId: string | string[], options: DrawTriggerOptions = {}): void {
    setShapeLayerAbsolute(shapeId, MAX_SHAPE_LAYER, options);
  }

  function sendShapeToBack(shapeId: string | string[], options: DrawTriggerOptions = {}): void {
    setShapeLayerAbsolute(shapeId, MIN_SHAPE_LAYER, options);
  }

  function setShapeEntity(
    shapeId: string | string[],
    entity: unknown,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => ({
      command: new SetShapeEntityCommand(shapeStore, shape.id, historyBefore.entity, entity),
      resultShape: { ...shape, entity },
    }));
  }

  /**
   * Change le texte de survol d'une forme par son ID (`Shape.hoverText`) — même logique que
   * `setShapeText` ci-dessus (fusionné avec le texte de survol courant, `undefined` le retire
   * entièrement plutôt que de fusionner). Typiquement déclenché depuis la console (voir `commands`
   * ci-dessous), pas depuis un geste utilisateur sur la carte.
   */
  function setShapeHoverText(
    shapeId: string | string[],
    hoverText: Partial<ShapeText> | undefined,
    options: DrawTriggerOptions = {},
  ): void {
    runShapeMutation(shapeId, options, (shape, historyBefore) => {
      const newHoverText = hoverText === undefined ? undefined : ({ ...shape.hoverText, ...hoverText } as ShapeText);
      return {
        command: new SetShapeHoverTextCommand(shapeStore, shape.id, historyBefore.hoverText, newHoverText),
        resultShape: { ...shape, hoverText: newHoverText },
      };
    });
  }

  /**
   * Supprime une forme par son ID — même logique que le menu contextuel "Supprimer" ci-dessus (qui
   * l'appelle désormais directement), mais utilisable sans clic droit sur la carte (ex. depuis la
   * console). Passe par `runShapeMutation` (une seule forme ou un groupe, voir plus haut) comme
   * toute autre modification (undoable sauf `options.skipHistory`, déclenche `onSave` sauf
   * `options.skipSave`, ne confirme jamais la forme si `options.skipConfirm`). Sans effet si
   * `shapeId` est inconnu. `shapeId` accepte aussi un tableau d'IDs (BESOINS.md §13, suppression
   * groupée) : tout le groupe est supprimé en une seule entrée `History` (un `Annuler` restaure
   * toutes les formes d'un coup) et un seul appel `onSave({ shapes: [...], kind: "delete" })` —
   * c'est ce que Suppr/Retour arrière sur une sélection multiple utilise désormais (voir
   * `packages/example/src/useKeyboardShortcuts.ts`), plutôt qu'un appel par forme.
   */
  function deleteShape(shapeId: string | string[], options: DrawTriggerOptions = {}): void {
    runShapeMutation(
      shapeId,
      options,
      (_shape, historyBefore) => ({ command: new DeleteShapeCommand(shapeStore, historyBefore), resultShape: null }),
      "delete",
    );
  }

  /** Voir `TokaMapInstance.copyShapes`. */
  function copyShapes(shapeIds: string[]): void {
    const shapes = shapeStore.getAll().filter((shape) => shapeIds.includes(shape.id));
    if (shapes.length === 0) return;
    clipboard.write({ shapes: shapes.map(cloneShapeForClipboard), origin: clipboard.origin });
  }

  /** Centre de la boîte englobante d'un ensemble de points — voir `pasteClipboard` (`at`). */
  function boundingBoxCenter(coordinates: LngLat[]): LngLat {
    const xs = coordinates.map(([x]) => x);
    const ys = coordinates.map(([, y]) => y);
    return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
  }

  /** Voir `TokaMapInstance.pasteClipboard`. */
  function pasteClipboard(at?: LngLat, options: DrawTriggerOptions = {}): void {
    const content = clipboard.read();
    if (!content?.shapes.length) return;

    // Venu d'un autre document (voir `ShapeClipboard`) : ses coordonnées n'ont pas de sens ici, le
    // groupe est déplacé au centre de la vue — ou sous le clic droit — à sa taille réelle.
    if (content.origin !== clipboard.origin) {
      for (const copied of relocateShapes(content.shapes, at ?? mapAdapter.getCenter())) {
        createPastedShape(copied, options);
      }
      return;
    }

    const copiedShapes = content.shapes;
    // `at` donné (clic droit "Coller", voir `mapAdapter.screenPositionToLngLat`) : translate le
    // groupe ENTIER d'un seul et même vecteur, pour que son centre (boîte englobante de toutes les
    // formes copiées) atterrisse exactement sous le clic — préserve les positions RELATIVES des
    // formes entre elles (une sélection multiple copiée-collée garde sa forme). Sans `at` (Ctrl+V au
    // clavier, ou appel console) : chaque forme est décalée indépendamment par rapport à sa propre
    // position d'origine (voir `computePasteOffset`), comportement historique inchangé.
    const groupTranslation = at ? boundingBoxCenter(copiedShapes.flatMap((shape) => shape.coordinates)) : null;

    for (const copied of copiedShapes) {
      const offset =
        at && groupTranslation
          ? ([at[0] - groupTranslation[0], at[1] - groupTranslation[1]] as LngLat)
          : computePasteOffset(copied.coordinates);

      createPastedShape(
        { ...copied, coordinates: copied.coordinates.map(([x, y]) => [x + offset[0], y + offset[1]] as LngLat) },
        options,
      );
    }
  }

  /** Crée, avec un nouvel id, une copie de `copied` déjà placée — voir `pasteClipboard`. */
  function createPastedShape(copied: Shape, options: DrawTriggerOptions): void {
    const newShape: Shape = { ...cloneShapeForClipboard(copied), id: generateId() };

    runShapeCommand(
      newShape.id,
      new CreateShapeCommand(shapeStore, newShape),
      "create",
      options.skipSave ?? false,
      options.skipHistory ?? false,
      options.skipConfirm ?? false,
      null,
      newShape,
      newShape,
    );
  }

  /** Voir `TokaMapInstance.copyShapeStyle`. */
  function copyShapeStyle(shapeId: string): void {
    const shape = shapeStore.getAll().find((s) => s.id === shapeId);
    if (!shape) return;
    styleClipboard = {
      style: cloneShapeStyle(shape.style),
      hoverStyle: cloneShapeStyle(shape.hoverStyle),
      image: cloneImageStyle(shape.image),
      text: cloneTextStyle(shape.text),
      hoverText: cloneTextStyle(shape.hoverText),
    };
  }

  /** Voir `TokaMapInstance.pasteShapeStyle`. `style`/`hoverStyle`/(le cas échéant) `image`/`text`/
   * `hoverText` changent en une seule entrée `History`/un seul appel `onSave` (`BatchCommand`, même
   * pour une forme unique) — des appels séparés à `setShapeStyle`/`setShapeHoverStyle`/
   * `setShapeImage`/`setShapeText`/`setShapeHoverText` produiraient autant d'`Annuler` distincts
   * pour ce qui doit rester un seul geste utilisateur. `image`/`text`/`hoverText` ne sont mutés QUE
   * si la forme cible EN A DÉJÀ (voir `cloneImageStyle`/`cloneTextStyle`) : sans ça, fusionner juste
   * la présentation créerait un `ShapeImage`/`ShapeText` sans `src`/`text` (champs requis) sur une
   * forme qui n'en avait pas — pas une simple fusion partielle inoffensive. */
  async function pasteShapeStyle(shapeId: string | string[], options: DrawTriggerOptions = {}): Promise<void> {
    if (!styleClipboard) return;
    const clipboardStyle = styleClipboard;
    const shapeIds = Array.isArray(shapeId) ? shapeId : [shapeId];
    const shapes = shapeIds.map((id) => shapeStore.getAll().find((s) => s.id === id)).filter((s): s is Shape => s != null);
    if (shapes.length === 0) return;

    // Résolution des dimensions de l'image AVANT `runShapeMutation` — même raison que
    // `setShapeImage` : ne pas construire les commandes sur un état devenu périmé pendant la
    // résolution. Seulement pour les formes qui ont déjà leur propre image (voir ci-dessus) ;
    // `src`/`width`/`height`/`svg` de la forme cible restent inchangés (absents de
    // `clipboardStyle.image`, voir `cloneImageStyle`), donc `resolveImageDimensions` n'a en
    // pratique jamais besoin de recharger quoi que ce soit ici — appelé quand même pour rester
    // rigoureusement cohérent avec le même contrat que `setShapeImage`.
    const resolvedImages = new Map(
      await Promise.all(
        shapes
          .filter((shape) => clipboardStyle.image && shape.image)
          .map(async (shape) => {
            const merged = { ...shape.image, ...clipboardStyle.image } as ShapeImage;
            return [shape.id, await resolveImageDimensions(merged)] as const;
          }),
      ),
    );

    runShapeMutation(shapeId, options, (shape, historyBefore) => {
      const newStyle: ShapeStyle = { ...shape.style, ...clipboardStyle.style };
      const newHoverStyle: ShapeStyle = { ...shape.hoverStyle, ...clipboardStyle.hoverStyle };
      const commands: Command[] = [
        new SetShapeStyleCommand(shapeStore, shape.id, historyBefore.style, newStyle),
        new SetShapeHoverStyleCommand(shapeStore, shape.id, historyBefore.hoverStyle, newHoverStyle),
      ];

      let newImage = shape.image;
      let newCoordinates = shape.coordinates;
      if (clipboardStyle.image && shape.image) {
        newImage = resolvedImages.get(shape.id);
        commands.push(new SetShapeImageCommand(shapeStore, shape.id, historyBefore.image, newImage));

        // Une forme-image (`shapeKind: "image"`) doit garder le ratio de son image — même logique
        // que `setShapeImage` (voir `resizeImageShapeToNewRatio`) : `width`/`height` copiés depuis
        // la source peuvent changer le ratio effectif de l'image de la forme cible.
        const resized = shape.shapeKind === "image" ? resizeImageShapeToNewRatio(shape.coordinates, newImage) : null;
        if (resized) {
          newCoordinates = resized;
          commands.push(new ScaleShapeCommand(shapeStore, shape.id, shape.coordinates, resized));
        }
      }

      let newText = shape.text;
      if (clipboardStyle.text && shape.text) {
        newText = { ...shape.text, ...clipboardStyle.text } as ShapeText;
        commands.push(new SetShapeTextCommand(shapeStore, shape.id, historyBefore.text, newText));
      }

      let newHoverText = shape.hoverText;
      if (clipboardStyle.hoverText && shape.hoverText) {
        newHoverText = { ...shape.hoverText, ...clipboardStyle.hoverText } as ShapeText;
        commands.push(new SetShapeHoverTextCommand(shapeStore, shape.id, historyBefore.hoverText, newHoverText));
      }

      return {
        command: new BatchCommand(commands),
        resultShape: {
          ...shape,
          style: newStyle,
          hoverStyle: newHoverStyle,
          image: newImage,
          coordinates: newCoordinates,
          text: newText,
          hoverText: newHoverText,
        },
      };
    });
  }

  /**
   * Ajoute une forme à partir des mêmes données qu'un "feature" du fichier exemple.json —
   * coordonnées Web Mercator, `properties` imbriqué dans `geometry` (voir `LatestRawFeature` et
   * `parseLatestJsonFeature`, qui fait la conversion, la même que celle utilisée au
   * chargement initial, toujours au format le plus récent — `properties.extra.type` pour
   * rectangle/polygone en v2). Pratique pour injecter directement une forme copiée depuis les
   * données réelles, sans repasser par le format interne `Shape` (WGS84, `style` à la racine, etc.).
   * Sans effet si `feature.geometry.type` n'est pas "Polygon" (ou anneau à moins de 3 points).
   */
  function addShapeFromFeature(feature: LatestRawFeature, options: AddShapeFromFeatureOptions = {}): void {
    const shape = parseLatestJsonFeature(feature, {
      layer: options.layer,
    });
    if (!shape) return;

    runShapeCommand(
      shape.id,
      new CreateShapeCommand(shapeStore, shape),
      "create",
      options.skipSave ?? false,
      options.skipHistory ?? false,
      options.skipConfirm ?? false,
      null,
      shape,
      shape,
    );
  }

  /** Voir `TokaMapInstance.startDrawingPolygon` — simple relais, pas de logique propre. */
  function startDrawingPolygon(options?: StartDrawingOptions): void {
    mapAdapter.startDrawingPolygon(options);
  }

  /** Voir `TokaMapInstance.startDrawingRectangle` — simple relais, pas de logique propre. */
  function startDrawingRectangle(options?: StartDrawingOptions): void {
    mapAdapter.startDrawingRectangle(options);
  }

  /** Voir `TokaMapInstance.startDrawingCircle` — simple relais, pas de logique propre. */
  function startDrawingCircle(options?: StartDrawingOptions): void {
    mapAdapter.startDrawingCircle(options);
  }

  /** Voir `TokaMapInstance.startDrawingLine` — simple relais, pas de logique propre. */
  function startDrawingLine(options?: StartDrawingOptions): void {
    mapAdapter.startDrawingLine(options);
  }

  /**
   * Voir `TokaMapInstance.startDrawingImage` — résout `width`/`height` ET `svg` (chargement/sondage
   * de l'image si besoin, même logique que `resolveImageDimensions`, voir son commentaire pour le
   * pourquoi de la détection de `svg`) avant de déléguer à `mapAdapter.startDrawingImage`, qui reste
   * lui entièrement synchrone (voir `DrawAdapter.startDrawingImage`).
   */
  async function startDrawingImage(src: string, options: StartDrawingImageOptions = {}): Promise<void> {
    const { width, height, svg } = options;
    const resolved = trustedImageDimensions({ src, width, height });
    const needsSvgDetection = svg === undefined && !isSvgImageSrc(src);
    if (resolved && !needsSvgDetection) {
      mapAdapter.startDrawingImage(src, { ...options, width: resolved.width, height: resolved.height });
      return;
    }
    try {
      const [dimensions, detectedSvg] = await Promise.all([
        resolved ? Promise.resolve(resolved) : loadImageDimensions(src),
        needsSvgDetection ? detectSvgImageType(src) : Promise.resolve(undefined),
      ]);
      mapAdapter.startDrawingImage(src, {
        ...options,
        width: dimensions.width,
        height: dimensions.height,
        svg: svg ?? detectedSvg,
      });
    } catch (error) {
      console.warn(`[startDrawingImage] impossible de déterminer les dimensions de "${src}" :`, error);
    }
  }

  /** Voir `TokaMapInstance.getAllShapes` — simple relais, pas de logique propre. */
  function getAllShapes(): Shape[] {
    return shapeStore.getAll();
  }

  /** Voir `TokaMapInstance.highlightShapes` — simple relais, pas de logique propre. */
  function highlightShapes(shapeIds: string[], options?: HighlightShapesOptions): void {
    mapAdapter.highlightShapes(shapeIds, options);
  }

  /** Voir `TokaMapInstance.setRemoteCursor` — simple relais, pas de logique propre. */
  function setRemoteCursor(cursor: RemoteCursor): void {
    mapAdapter.setRemoteCursor(cursor);
  }

  /** Voir `TokaMapInstance.removeRemoteCursor` — simple relais, pas de logique propre. */
  function removeRemoteCursor(id: string): void {
    mapAdapter.removeRemoteCursor(id);
  }

  /** Voir `TokaMapInstance.getDefaultShapeStyle` — délègue à `MapLibreAdapter`, qui tient la valeur
   * courante à jour (construction, JSON chargé, ou `setDefaultShapeStyle` — voir plus haut). */
  function getDefaultShapeStyle(): ShapeStyle | undefined {
    return mapAdapter.getDefaultShapeStyle();
  }

  /** Voir `TokaMapInstance.getTileLayerName` — délègue à `MapLibreAdapter`, qui tient la valeur
   * courante à jour (construction, JSON chargé, ou `setTileLayerName` — voir plus haut). */
  function getTileLayerName(): TileLayerName {
    return mapAdapter.getTileLayerName();
  }

  /** Voir `TokaMapInstance.getBackgroundColor` — délègue à `MapLibreAdapter`, qui tient la valeur
   * courante à jour (construction, JSON chargé, ou `setBackgroundColor` — voir plus haut). */
  function getBackgroundColor(): string | undefined {
    return mapAdapter.getBackgroundColor();
  }

  /** Voir `TokaMapInstance.getGeolocationEnabled` — délègue à `MapLibreAdapter`, qui tient la valeur
   * courante à jour (construction, JSON chargé, ou `setGeolocationEnabled` — voir plus haut). */
  function getGeolocationEnabled(): boolean {
    return mapAdapter.getGeolocationEnabled();
  }

  /** Voir `TokaMapInstance.getAllowPitch` — délègue à `MapLibreAdapter`, qui tient la valeur
   * courante à jour (construction, JSON chargé, ou `setAllowPitch` — voir plus haut). */
  function getAllowPitch(): boolean {
    return mapAdapter.getAllowPitch();
  }

  // Tableau de commandes pilotables depuis la console (voir `ConsoleCommand`). À compléter au fur
  // et à mesure que de nouvelles commandes console apparaissent, plutôt que d'obliger à connaître
  // par cœur chaque méthode de l'instance.
  const commands: ConsoleCommand[] = [
    {
      name: "resetView",
      signature: "resetView()",
      description: "Recentre la carte sur sa vue de départ (centre/zoom/rotation) et remet le pitch (3D) à 0.",
      run: resetView,
    },
    {
      name: "getMapView",
      signature: "getMapView()",
      description: "Retourne la vue de caméra courante (centre, zoom, bornes de zoom, rotation).",
      run: getMapView,
    },
    {
      name: "setMapView",
      signature:
        'setMapView({ zoom: 17, rotationDeg: 0 }, { skipHistory: false, skipSave: false, skipConfirm: false })',
      description:
        "Change la vue de caméra (centre, zoom, zoomMin, zoomMax, rotationDeg) ; champs omis inchangés. Déclenche onSaveConfiguration sauf skipSave.",
      run: setMapView,
    },
    {
      name: "setDefaultShapeStyle",
      signature: 'setDefaultShapeStyle({ bgColor: "#ff0000", borderColor: "#000000" })',
      description:
        "Change le style par défaut des PROCHAINES formes dessinées (fusionné avec le style par défaut courant). Déclenche onSaveConfiguration sauf skipSave.",
      run: setDefaultShapeStyle,
    },
    {
      name: "setTileLayerName",
      signature: 'setTileLayerName("osm")',
      description:
        'Change le fond de carte affiché ("unset" : aucun, "osm" : plan OpenStreetMap, "satellite" : imagerie aérienne). Déclenche onSaveConfiguration sauf skipSave.',
      run: setTileLayerName,
    },
    {
      name: "getTileLayerName",
      signature: "getTileLayerName()",
      description: "Retourne le fond de carte actuellement affiché.",
      run: getTileLayerName,
    },
    {
      name: "setOsmOptions",
      signature: 'setOsmOptions({ style: "dark", poiLabels: false })',
      description:
        'Change les réglages du fond "osm" (style "light"/"dark"/"sober"/"detailed", language, placeLabels, roadLabels, poiLabels, buildings3d), fusionnés avec les réglages courants. Déclenche onSaveConfiguration sauf skipSave.',
      run: setOsmOptions,
    },
    {
      name: "getOsmOptions",
      signature: "getOsmOptions()",
      description: "Retourne les réglages courants du fond \"osm\" (champs jamais renseignés absents).",
      run: () => mapAdapter.getOsmOptions(),
    },
    {
      name: "setBackgroundColor",
      signature: 'setBackgroundColor("#101a22")',
      description:
        "Change la couleur de fond du conteneur de la carte (undefined pour revenir au comportement par défaut). Déclenche onSaveConfiguration sauf skipSave.",
      run: setBackgroundColor,
    },
    {
      name: "getBackgroundColor",
      signature: "getBackgroundColor()",
      description: "Retourne la couleur de fond actuellement configurée sur le conteneur de la carte.",
      run: getBackgroundColor,
    },
    {
      name: "setAllowPitch",
      signature: "setAllowPitch(true, { skipHistory: false, skipSave: false, skipConfirm: false })",
      description: "Autorise/bloque l'inclinaison 3D de la caméra. Déclenche onSaveConfiguration sauf skipSave.",
      run: setAllowPitch,
    },
    {
      name: "getAllowPitch",
      signature: "getAllowPitch()",
      description: "Retourne si l'inclinaison 3D de la caméra est actuellement autorisée.",
      run: getAllowPitch,
    },
    {
      name: "startEditingMapView",
      signature: "startEditingMapView({ allowCancel: false, skipHistory: false, skipSave: false, skipConfirm: false })",
      description:
        "Démarre l'édition interactive de la vue par défaut (croix au centre + Enregistrer/Annuler) ; allowCancel: false masque Annuler. skipHistory/skipSave/skipConfirm s'appliquent au SetConfigurationCommand construit par confirmMapViewEdit().",
      run: startEditingMapView,
    },
    {
      name: "confirmMapViewEdit",
      signature: "confirmMapViewEdit()",
      description: "Valide (\"Enregistrer\") la vue courante comme nouvelle vue par défaut.",
      run: confirmMapViewEdit,
    },
    {
      name: "cancelMapViewEdit",
      signature: "cancelMapViewEdit()",
      description: "Annule l'édition de la vue par défaut, revient à la vue d'avant ce mode.",
      run: cancelMapViewEdit,
    },
    {
      name: "startRouteMode",
      signature: 'startRouteMode({ prefillToShapeId: "1", autoValidate: true })',
      description: "Ouvre le panneau itinéraire (Départ/Arrivée/Valider) — sans effet si `routePanel` n'est pas configuré. `autoValidate` calcule l'itinéraire tout de suite si les 2 champs sont déjà renseignés.",
      run: startRouteMode,
    },
    {
      name: "cancelRouteMode",
      signature: "cancelRouteMode()",
      description: "Ferme le panneau itinéraire et efface le tracé affiché.",
      run: cancelRouteMode,
    },
    {
      name: "debugPathfindingGrid",
      signature: 'debugPathfindingGrid("1", "2")',
      description: "Diagnostic : affiche la grille explorée par le calcul d'itinéraire entre ces 2 formes (cellules bloquées en rouge, à prix réduit — `Shape.pathPriority` — en vert). `mapAdapter.clearPathfindingDebugGrid()` pour l'effacer.",
      run: debugPathfindingGrid,
    },
    {
      name: "panByScreenPixels",
      signature: "panByScreenPixels(80, 0)",
      description: "Décale la caméra de dx/dy pixels écran (ex. ce que font les flèches du mode d'édition de vue).",
      run: (dx: number, dy: number) => mapAdapter.panByScreenPixels(dx, dy),
    },
    {
      name: "setShapeStyle",
      signature: 'setShapeStyle("1", { bgColor: "#ff0000", borderColor: "#000000" })',
      description: "Change la couleur de fond/bordure d'une forme par son ID.",
      run: setShapeStyle,
    },
    {
      name: "setShapeHoverStyle",
      signature: 'setShapeHoverStyle("1", { bgColor: "#ff0000", borderColor: "#000000" })',
      description: "Change la couleur de fond/bordure au survol (editMode) d'une forme par son ID.",
      run: setShapeHoverStyle,
    },
    {
      name: "setShapeImage",
      signature: 'setShapeImage("1", { src: "https://...png", scale: 1, rotation: 0 }, { skipSave: true })',
      description:
        "Change l'image (plaquée sur la forme, mode contain) d'une forme par son ID (fusionné avec l'image courante) ; { src: undefined } la retire entièrement.",
      run: setShapeImage,
    },
    {
      name: "setShapeText",
      signature: 'setShapeText("1", { text: "Stand A1", size: 14, color: "#000000" })',
      description:
        "Change le texte affiché au centre d'une forme par son ID (fusionné avec le texte courant) ; undefined le retire entièrement.",
      run: setShapeText,
    },
    {
      name: "setShapeAction",
      signature: 'setShapeAction("1", "open_place")',
      description:
        "Change l'identifiant d'action déclenchée par un clic sur une forme par son ID ; undefined la retire entièrement.",
      run: setShapeAction,
    },
    {
      name: "setShapeUseActionIcon",
      signature: 'setShapeUseActionIcon("1", true)',
      description: "Affiche (ou non) une icône signalant que la forme déclenche une action au clic.",
      run: setShapeUseActionIcon,
    },
    {
      name: "setShapeUrlAction",
      signature: 'setShapeUrlAction("1", { url: "https://exemple.com", openMode: "new_tab" })',
      description: "Change la configuration de redirection (`Shape.urlAction`) d'une forme par son ID.",
      run: setShapeUrlAction,
    },
    {
      name: "setShapeTargetDocumentId",
      signature: 'setShapeTargetDocumentId("1", "2")',
      description: "Change le document ouvert au clic (`Shape.targetDocumentId`) d'une forme par son ID.",
      run: setShapeTargetDocumentId,
    },
    {
      name: "setShapeLocked",
      signature: 'setShapeLocked("1", true)',
      description: "Verrouille (ou déverrouille) une forme par son ID — masque le \"…\" de sa barre d'outils flottante et désactive son clic droit.",
      run: setShapeLocked,
    },
    {
      name: "setShapeWalkable",
      signature: 'setShapeWalkable("1", true)',
      description: "Fait (ou défait) l'appartenance d'une forme au réseau traversable par le mode itinéraire.",
      run: setShapeWalkable,
    },
    {
      name: "setShapePathPriority",
      signature: 'setShapePathPriority("1", 6)',
      description: "Marque (ou démarque) une forme ligne comme \"chemin\"/\"mur attractif\" pour le mode itinéraire.",
      run: setShapePathPriority,
    },
    {
      name: "setShapeHeight",
      signature: 'setShapeHeight("1", 12)',
      description: "Donne une hauteur (en mètres) au volume d'une forme, visible quand la 3D est autorisée.",
      run: setShapeHeight,
    },
    {
      name: "setShapeElevation",
      signature: 'setShapeElevation("1", 5)',
      description: "Décolle du sol (en mètres) le volume d'une forme qui a une hauteur, visible quand la 3D est autorisée.",
      run: setShapeElevation,
    },
    {
      name: "setShapeHoverHeight",
      signature: 'setShapeHoverHeight("1", 20)',
      description: "Donne au volume d'une forme une autre hauteur (en mètres) au survol.",
      run: setShapeHoverHeight,
    },
    {
      name: "setShapeHoverElevation",
      signature: 'setShapeHoverElevation("1", 5)',
      description: "Décolle du sol (en mètres) le volume d'une forme au survol.",
      run: setShapeHoverElevation,
    },
    {
      name: "setShapeMinZoomOffset",
      signature: 'setShapeMinZoomOffset("1", 1)',
      description: "N'affiche une forme qu'à partir d'un zoom, en niveaux au-delà de la vue de départ (1 = deux fois plus zoomé).",
      run: setShapeMinZoomOffset,
    },
    {
      name: "raiseShapeLayer",
      signature: 'raiseShapeLayer("1")',
      description: "Monte une forme d'un calque (Shape.layer, +10, plafonné à 100).",
      run: raiseShapeLayer,
    },
    {
      name: "lowerShapeLayer",
      signature: 'lowerShapeLayer("1")',
      description: "Descend une forme d'un calque (Shape.layer, -10, plancher à 0).",
      run: lowerShapeLayer,
    },
    {
      name: "bringShapeToFront",
      signature: 'bringShapeToFront("1")',
      description: "Monte une forme directement au calque maximal (Shape.layer = 100).",
      run: bringShapeToFront,
    },
    {
      name: "sendShapeToBack",
      signature: 'sendShapeToBack("1")',
      description: "Descend une forme directement au calque minimal (Shape.layer = 0).",
      run: sendShapeToBack,
    },
    {
      name: "setShapeEntity",
      signature: 'setShapeEntity("1", { placeId: "A1" })',
      description:
        "Change la charge métier opaque d'une forme par son ID (jamais interprétée par @tokamap/core) ; undefined la retire entièrement.",
      run: setShapeEntity,
    },
    {
      name: "setShapeHoverText",
      signature: 'setShapeHoverText("1", { text: "Stand A1", size: 14, color: "#000000" })',
      description:
        "Change le texte affiché au survol (editMode) d'une forme par son ID (fusionné avec le texte de survol courant) ; undefined le retire entièrement.",
      run: setShapeHoverText,
    },
    {
      name: "deleteShape",
      signature: 'deleteShape("1") ou deleteShape(["1", "2"])',
      description: "Supprime une ou plusieurs formes par leur ID (tableau : une seule entrée History, un seul appel onSave).",
      run: deleteShape,
    },
    {
      name: "copyShapes",
      signature: 'copyShapes(["1", "2"])',
      description: "Copie les formes données dans le presse-papier interne (remplace son contenu précédent).",
      run: copyShapes,
    },
    {
      name: "pasteClipboard",
      signature: "pasteClipboard([2.35, 48.85], { skipSave: true })",
      description:
        "Colle le contenu du presse-papier interne (voir copyShapes) : crée une copie de chaque forme. " +
        "`at` (lng/lat, optionnel) centre le groupe collé sur ce point ; omis, chaque forme est décalée par rapport à sa propre originale.",
      run: pasteClipboard,
    },
    {
      name: "copyShapeStyle",
      signature: 'copyShapeStyle("1")',
      description:
        "Copie l'apparence d'une forme (style/hoverStyle/image/text/hoverText, sauf l'URL de l'image/le texte/l'action) dans un presse-papier de style séparé.",
      run: copyShapeStyle,
    },
    {
      name: "pasteShapeStyle",
      signature: 'pasteShapeStyle("2") ou pasteShapeStyle(["2", "3"])',
      description:
        "Colle le style copié (voir copyShapeStyle) sur une ou plusieurs formes — image/text/hoverText seulement si la cible en a déjà.",
      run: pasteShapeStyle,
    },
    {
      name: "startDrawingPolygon",
      signature: "startDrawingPolygon({ skipTutorial: true })",
      description:
        "Démarre le dessin d'un polygone (clic par sommet, clic sur le 1er sommet pour fermer) ; affiche un tutoriel pas-à-pas sauf skipTutorial.",
      run: startDrawingPolygon,
    },
    {
      name: "startDrawingRectangle",
      signature: "startDrawingRectangle({ skipTutorial: true })",
      description:
        "Démarre le dessin d'un rectangle (2 clics : premier coin, puis coin opposé) ; affiche un tutoriel pas-à-pas sauf skipTutorial.",
      run: startDrawingRectangle,
    },
    {
      name: "startDrawingCircle",
      signature: "startDrawingCircle({ skipTutorial: true })",
      description:
        "Démarre le dessin d'un cercle (2 clics : centre, puis point du bord) ; affiche un tutoriel pas-à-pas sauf skipTutorial.",
      run: startDrawingCircle,
    },
    {
      name: "startDrawingLine",
      signature: "startDrawingLine({ skipTutorial: true })",
      description:
        "Démarre le dessin d'une ligne (un clic par sommet, clic sur le dernier sommet posé pour terminer) ; affiche un tutoriel pas-à-pas sauf skipTutorial.",
      run: startDrawingLine,
    },
    {
      name: "startDrawingImage",
      signature: 'startDrawingImage("https://.../image.png", { scale: 1 })',
      description:
        "Démarre le dessin d'une image (2 clics, ratio de l'image conservé) ; charge l'image au " +
        "préalable si width/height ne sont pas précisées.",
      run: startDrawingImage,
    },
    {
      name: "addShapeFromFeature",
      signature:
        'addShapeFromFeature({ type: "Feature", geometry: { type: "Polygon", ' +
        "coordinates: [[[280675.24, 6270498.31], [280694.34, 6270447.86], [280648.96, 6270430.69], " +
        '[280675.24, 6270498.31]]], properties: { id: 1, extra: { style: { bgColor: "#ff0000" } } } } }, ' +
        "{ layer: 10 })",
      description: "Ajoute une forme depuis un feature au format exemple.json, sur le calque donné.",
      run: addShapeFromFeature,
    },
    {
      name: "getDefaultShapeStyle",
      signature: "getDefaultShapeStyle()",
      description: "Retourne le style par défaut appliqué aux nouvelles formes dessinées.",
      run: getDefaultShapeStyle,
    },
    {
      name: "getAllShapes",
      signature: "getAllShapes()",
      description: "Retourne toutes les formes actuellement dans le store (triées par calque puis ordre).",
      run: getAllShapes,
    },
    {
      name: "highlightShapes",
      signature: 'highlightShapes(["1", "2"], { zoomToFit: true })',
      description:
        "Surligne (contour + fond pulsants) les formes données ; [] efface le surlignage. zoomToFit centre/zoome la caméra pour les faire tenir à l'écran.",
      run: highlightShapes,
    },
    {
      name: "setRemoteCursor",
      signature: 'setRemoteCursor({ id: "user-1", userName: "Alice", color: "#e91e63", position: [2.35, 48.85] })',
      description:
        "Affiche (ou met à jour, si l'id est déjà connu) le curseur d'un autre utilisateur sur la carte.",
      run: setRemoteCursor,
    },
    {
      name: "removeRemoteCursor",
      signature: 'removeRemoteCursor("user-1")',
      description: "Efface le curseur affiché par setRemoteCursor pour cet id.",
      run: removeRemoteCursor,
    },
    {
      name: "setGeolocationEnabled",
      signature: "setGeolocationEnabled(true, { skipHistory: false, skipSave: false, skipConfirm: false })",
      description:
        "Active/désactive le repérage de la position de l'utilisateur (point + cap sur la carte, voir BESOINS.md §15). Déclenche onSaveConfiguration sauf skipSave.",
      run: setGeolocationEnabled,
    },
    {
      name: "getGeolocationEnabled",
      signature: "getGeolocationEnabled()",
      description: "Retourne si le repérage de la position de l'utilisateur est actuellement activé.",
      run: getGeolocationEnabled,
    },
    {
      name: "captureImage",
      signature: 'captureImage({ maxWidth: 640, type: "image/png" })',
      description:
        "Capture la carte affichée (fond + SVG + formes, sans l'interface ni la sélection) et rend { blob, width, height }.",
      run: captureImage,
    },
  ];

  /** Voir `TokaMapInstance.loadDocument`. */
  async function loadDocument(json: unknown, options: LoadDocumentOptions = {}): Promise<void> {
    const data = parseJsonDocument(json);

    const view = resolveIncomingView(documentlessView, data.view);
    const animate = options.animateView ?? true;

    // Avant tout le reste : ce qui pointe vers des formes du document sortant, qui n'existeront
    // plus une fois le store remplacé — un panneau ouvert sur l'une d'elles, un itinéraire tracé
    // entre deux d'entre elles, une sélection, un surlignage.
    cancelRouteMode();
    entityPanel?.close();
    urlOverlay?.close();
    mapAdapter.clearSelection();
    mapAdapter.setFocusedShape(null);
    mapAdapter.highlightShapes([]);
    // Effacé ici et pas seulement via `cancelRouteMode` : celui-ci ne fait rien tant que le panneau
    // n'est pas visible (voir sa garde), alors qu'un tracé peut lui survivre — un consommateur qui
    // enchaîne les documents en affichant lui-même chaque tronçon (voir `RoutePanelConfig
    // .onValidate`) masque le panneau au premier changement mais continue de dessiner. Sans ça, le
    // tracé du document sortant resterait par-dessus l'entrant, dans des coordonnées qui n'y
    // correspondent plus.
    mapAdapter.clearRoute();
    // Les commandes déjà exécutées restent appliquées au document sortant (déjà enregistrées via
    // `onSave`) : seule la possibilité de les annuler disparaît, faute de formes à qui les appliquer.
    history.clear();
    refreshDocumentSwitcher();

    // Mêmes champs, dans le même ordre, qu'au chargement initial — `rawLoadedDocument`/
    // `formatVersion` compris, que `migrationGate`/`getConfigurationState` lisent : sans eux, une
    // sauvegarde faite après un changement de document rapporterait la version du premier chargé.
    rawLoadedDocument = json;
    formatVersion = data.formatVersion;
    // `applyLoadedView` ignore un champ `undefined` — or c'est justement la valeur de construction de
    // ces trois réglages tant que rien n'a été configuré (un fond OSM jamais touché est en clair sans
    // `osmOptions`) : les y ramener lui-même, sinon ceux du document sortant resteraient en place.
    if (data.view?.osmOptions === undefined) mapAdapter.setOsmOptions(documentlessView.osmOptions);
    if (data.view?.backgroundColor === undefined) mapAdapter.setBackgroundColor(documentlessView.backgroundColor);
    if (data.view?.defaultShapeStyle === undefined) mapAdapter.setDefaultShapeStyle(documentlessView.defaultShapeStyle);
    // Le cadrage de l'entrant ne s'impose qu'à l'arrivée : la caméra part de la vue du sortant, hors
    // de ses limites, et y appliquer celles-ci couperait le vol (voir `holdFramingConstraints`).
    if (animate) mapAdapter.holdFramingConstraints();
    applyLoadedView(view, { animate });

    // La géolocalisation appartient au document au même titre que son fond ou ses thèmes : passer
    // sur un document qui l'active doit l'activer, et inversement. Posée ici plutôt que dans
    // `applyLoadedView` pour la même raison d'ordre qu'au chargement initial (voir son commentaire),
    // et via l'adapter directement : charger un document n'est pas une modification de
    // l'utilisateur, rien ne doit partir dans `History`/`onSaveConfiguration`.
    mapAdapter.setGeolocationEnabled(data.view?.geolocation ?? config.geolocation ?? false);

    shapeStore.replaceAll(data.shapes);
    showFramedStartView({ animate });
    if (animate) mapAdapter.releaseFramingConstraintsAfterMove();

    // Même raison qu'à la création (voir plus bas) : ne rendre la main qu'une fois la carte peinte,
    // pour qu'un consommateur qui masque son écran de chargement là-dessus ne voie pas la carte
    // sortante pendant que l'entrante se charge. Pendant un vol, la carte n'est justement jamais
    // "idle" avant la fin du déplacement — attendre reviendrait à retarder l'appelant de toute la
    // durée de l'animation, alors que les formes, elles, sont déjà en place.
    if (!animate) await mapAdapter.waitUntilIdle();
    refreshDocumentSwitcher();
  }

  // Ne résoudre `createTokaMap` qu'une fois la carte réellement peinte (fond de carte + formes déjà
  // chargées inclus) — voir `MapLibreAdapter.waitUntilIdle`. Sans ça, un consommateur qui masque son
  // propre écran de chargement dès que cette Promise résout (ex. `onReady`) verrait un bref flash de
  // fond vide/tuiles manquantes, `tileLayerName`/`shapeStore.addMany` ci-dessus n'ayant fait que
  // DÉMARRER le chargement des tuiles/images, pas l'attendre.
  await mapAdapter.waitUntilIdle();

  return {
    mapAdapter,
    shapeStore,
    history,
    loadDocument,
    refreshDocumentSwitcher,
    onSaveStatusChange: shapeSaveTracker.onStatusChange.bind(shapeSaveTracker),
    onSaveConfigurationStatusChange: configurationSaveTracker.onStatusChange.bind(configurationSaveTracker),
    resetView,
    getMapView,
    setMapView,
    captureImage,
    setDefaultShapeStyle,
    setTileLayerName,
    setOsmOptions,
    setBackgroundColor,
    setAllowPitch,
    getFraming: () => mapAdapter.getFraming(),
    setFraming,
    startEditingMapView,
    confirmMapViewEdit,
    cancelMapViewEdit,
    startRouteMode,
    cancelRouteMode,
    debugPathfindingGrid,
    setShapeStyle,
    setShapeHoverStyle,
    setShapeImage,
    setShapeText,
    setShapeAction,
    setShapeUseActionIcon,
    setShapeUrlAction,
    setShapeTargetDocumentId,
    setShapeLocked,
    setShapeThemeId,
    setThemes: (themes) => mapAdapter.setThemes(themes),
    getThemes: () => mapAdapter.getThemes(),
    setDefaultThemeId: (themeId) => mapAdapter.setDefaultThemeId(themeId),
    getDefaultThemeId: () => mapAdapter.getDefaultThemeId(),
    setShapeWalkable,
    setShapePathPriority,
    setShapeHeight,
    setShapeElevation,
    setShapeHoverHeight,
    setShapeHoverElevation,
    setShapeMinZoomOffset,
    raiseShapeLayer,
    lowerShapeLayer,
    bringShapeToFront,
    sendShapeToBack,
    setShapeEntity,
    setShapeHoverText,
    deleteShape,
    copyShapes,
    pasteClipboard,
    copyShapeStyle,
    pasteShapeStyle,
    addShapeFromFeature,
    startDrawingPolygon,
    startDrawingRectangle,
    startDrawingCircle,
    startDrawingLine,
    startDrawingImage,
    getDefaultShapeStyle,
    getTileLayerName,
    getOsmOptions: () => mapAdapter.getOsmOptions(),
    getBackgroundColor,
    getGeolocationEnabled,
    setGeolocationEnabled,
    getAllowPitch,
    getAllShapes,
    highlightShapes,
    setRemoteCursor,
    removeRemoteCursor,
    commands,
  };
}
